/*!
// <copyright file="Grid.js" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

/// <reference path="../html-binding-controls.js" />
/// <reference path="../html-binding-handlers.ts" />
(function () {
    'use strict';

    Globalize.addCultureInfo('en', {
        messages: {
            List_DefaultCheckBoxText: 'Select Rows ',
            List_DefaultHeaderCheckBoxText: 'Mark all rows',
            List_NoResultsMessage: "We didn't find anything to show here.",
            List_LoadingMoreData: "Loading more data...",
            List_IncSortNumber: "Sort smallest to largest",
            List_DecSortNumber: "Sort largest to smallest",
            List_IncSortTime: "Sort oldest to newest",
            List_DecSortTime: "Sort newest to oldest",
            List_IncSortString: "Sort A to Z",
            List_DecSortString: "Sort Z to A",
            List_ColumnNotFilterable: "Column is not filterable.",
            Grid_Next: "Next",
            Grid_Previous: "Previous",
        }
    });

    $dyn.ui.ListMode = {
        tabular: "Tabular",
        list: "List"
    };

    var PendingRowTypes = {
        newRow: $dyn.debug.general ? 'newRow' : 0,
        existingRow: $dyn.debug.general ? 'existingRow' : 1,
    };

    var PendingRowStates = {
        pending: $dyn.debug.general ? 'pending' : 0,
        processing: $dyn.debug.general ? 'processing' : 1,
        processed: $dyn.debug.general ? 'processed' : 2,
        notQueued: $dyn.debug.general ? 'notQueued' : 3,
    };

    // Imports
    // MS Internal use only api
    var selector = $dyn.ui.selector;

    $dyn.controls.HierarchicalGridBase = function (data, element, parent) {
        var self = this;
        var root = self._$listElement = $(element);

        // Virtualization support:
        // List does not creates a control instance per cell.
        // It uses an active row with a full control instance.
        // It has an inactive row with a partial control instance (for example, allowedit turned off).
        // It changes the data for inactive row and create an inert clone for each row.
        // It moves the active row to wherever the selected index is.
        // List and tabular modes share the same dom structure
        // Event handler are put on the list body instead of the list rows/cells

        self.ForEachRenderCount = $dyn.observable(0);
        self.isContainer = true;
        self._isCardList = undefined;
        self.PageSize = 25;

        //Count of pending row change interactions. Increments by 1 every time an interaction is sent to the queue
        //Decrements every time interaction is throttled or on callback
        self._PendingRowChangeCount = $dyn.observable(0);

        //Default to false while working on the prototype
        self._fastEdit = $dyn.observable($dyn.debug.gridFastEdit);
        if (self._fastEdit() && element) {
            element.classList.add('isPendingCommit');
        }
        self._pendingRowsQueue = $dyn.observableArray([]);
        self._processServerChanges = $dyn.observable(true);

        self.usingDisposable($dyn.observe(self._pendingRowsQueue, self._pendingRowsQueueUpdated, self));
        //Indicates user is busy performing some keyboard action (entering/navigating within the grid)
        //Flag is set to true on a RowKeyDown, and reset to false on a timer
        //When grid is in ahead mode, this flag will ensure that New/Change row callbacks don't re-render the grid while the user is busy
        //either entering/navigating within the grid
        this._userActionBusyTimer = undefined;
        this._userActionBusy = $dyn.observable(false);
        this._activeRowValid = $dyn.observable(true);
        this._pendingExistingRows = {};
        this._pendingNewRowsToExistingMap = {};
        this._createdPendingRows = {};
        this._pendingRowIdToPendingRow = {};

        this.usingDisposable($dyn.observe(this._userActionBusy, this._userActionBusyChanged, this));

        //Flag that indicates whether all resources for controls within the grid have been loaded or not
        self._allResourcesLoaded = $dyn.observable(true);


        // An array that stores rendered rows data, including reference to row element, data id, version, selected, marked states
        // It can have more rows than current server page has
        self.renderedRows = [];

        $dyn.ui.Control.apply(this, arguments);

        if (self._fastEdit()) {
            $dyn.setValue(this, 'InfiniteScroll', false);
        }

        self.LayoutMinWidth = { minSizeFunction: $dyn.layout.gridLayoutMinWidth, cssClassArgument: 'listRoot' };
        self.LayoutMinHeight = { minSizeFunction: $dyn.layout.gridLayoutMinHeight, cssClassArgument: 'listRoot' };

        if (!$dyn.isObservable(self.SelectedIndex)) {
            // The selected index must be an observable value.
            self.SelectedIndex = $dyn.observable(self.SelectedIndex);
        }

        if (!$dyn.isObservable(self.GridLines)) {
            // GridLines must be an observable value.
            self.GridLines = $dyn.observable(self.GridLines);
        }

        this._activeCell.rowIndex = self.ConvertPageIndexToServerCacheIndex($dyn.peek(self.SelectedIndex));

        self.LastSelectedIndex = self.ConvertPageIndexToServerCacheIndex($dyn.peek(self.SelectedIndex));

        if (!$dyn.isObservable(self.MarkedIndexes)) {
            self.MarkedIndexes = $dyn.observable(self.MarkedIndexes || []);
        }

        self.markedIndexesChanged = function () {
            $dyn.log.list && $dyn.log.list("List: _PendingRowChangeCount " + $dyn.value(self._PendingRowChangeCount));
            // Do not react to any changes to the marked indexes if there are pending data load or key down events or pending row change interaction
            // Pending events like the ones above mean that either an interaction is pending or will be processing in the near future
            // which will re-apply this logic again. Reacting to these changes while such events are pending would result in
            // premature marking/unmarking of rows which is undesirable
            if (self._supportsMarking() && $dyn.value(self._PendingRowChangeCount) == 0) {
                $dyn.log.list && $dyn.log.list("List: Marking Row(s)");
                var marked = self.MarkedIndexes() || [];
                var items = $('.listBody .listItem', self.element);
                //
                var offset = self.ConvertServerCacheIndexToClientCacheIndex($dyn.value(self.FetchedDataStartPosition));
                for (var i = 0, len = marked.length; i < len; i++) {
                    items[i + offset] && self._setRowMarking(items[i + offset], marked[i]);
                }
            }
        };

        // Represents the child hierarchy as a single observable unit.
        self.ChildHierarchy = this._rowElementGenerator(element);

        self.ChildHierarchy.valueChanged.subscribe(function (inactiveRowElement) {
            $dyn.log.list && $dyn.log.list('List: Inactive row schema changed');
            self._resetCachedInactiveItemElement($dyn.value(inactiveRowElement)[0]);
            self._rerenderOptions($dyn.peek(self._rerenderOptions) + 1);

        });

        self._listBodyElement = $('.listBody', this.element)[0];
        self._headerRowElement = this.element && this.element.querySelector(".listAsTable-HeaderRow");

        self._inactiveItem = $dyn.observable();

        self.dataSourceName = $dyn.observable('');

        var lastHeaderMetadata;
        var lastControlContexts = {};
        self.ColumnHeaderMetadata = $dyn.computed(function () {
            var rowTemplates = $dyn.value(element.dynChildren);
            var headerMetadata = [];
            var listHeaderMetadata = {};
            var controlContexts = {};

            var firstRowColumns = null;
            if (rowTemplates.length) {
                firstRowColumns = $dyn.value(rowTemplates[0].dynChildren);
            }

            if (firstRowColumns && firstRowColumns.length) {
                $.each(firstRowColumns, function (index, element) {
                    var getObservableProperty = $dyn.ui.getObservableProperty;

                    var parentRole = $dyn.role(rowTemplates[0]);
                    var role = parentRole.getChildRole(element);
                    var context = $dyn.context(element);
                    var name, headerContext, lastControlContext, lastHeaderContext, lastVersion;
                    // Elements can be added to dynChilren before they are data bound so take a dependency on databound.
                    if (!role.isDataBound() && context) {
                        $dyn.log("Role %o at index %i for control %s not databound yet", role, index, element.getAttribute("data-dyn-controlname"));
                    }
                    if (context) {
                        name = context.Name;
                        lastHeaderContext = lastHeaderMetadata && lastHeaderMetadata[name];
                        lastControlContext = lastControlContexts[name];
                        lastVersion = lastHeaderContext && lastHeaderContext.Version;

                        if (name) {
                            controlContexts[name] = context;
                        }

                        if (context === lastControlContext) {
                            headerContext = lastHeaderContext;
                        } else {
                            headerContext = {
                                FormDataSourceName: getObservableProperty(context, 'DataSourceName'),
                                IsNumber: context.IsNumber,
                                Name: name,
                                Visible: getObservableProperty(context, 'Visible'),
                                Alignment: getObservableProperty(context, 'Alignment'),
                                IsFiltered: getObservableProperty(context, 'IsFiltered'),
                                IsSorted: getObservableProperty(context, 'SortDirection'),
                                Label: self.getColumnLabel(
                                    getObservableProperty(context, 'Label'),
                                    context.Name,
                                    getObservableProperty(context, 'ShowLabel')),
                                Width: getObservableProperty(context, 'Width'),
                                IsFilterable: getObservableProperty(context, 'IsFilterable'),
                                FieldName: getObservableProperty(context, 'FieldName'),
                                IsSortable: getObservableProperty(context, 'IsFilterable'),
                                ControlType: getObservableProperty(context, 'TypeName'),
                                IsLookupField: getObservableProperty(context, 'LookupButton'),
                                IsBound: true,
                                Version: lastVersion !== undefined ? lastVersion + 1 : 1
                            };
                        }

                        headerContext.FilteringFields = [];

                        if (context.TypeName == "ReferenceGroup") {
                            headerContext.IsLookupField = true;

                            // Filtering is performed in the context of a Reference Group's replacement fields. Add additional context to be consumed by
                            // the filtering system.
                            var childControls = $dyn.value(context.Children);
                            childControls.forEach(function (child) {
                                var childFilterContext = {
                                    IsFilterable: getObservableProperty(child, 'IsFilterable'),
                                    FormDataSourceName: getObservableProperty(child, 'DataSourceName'),
                                    FieldName: getObservableProperty(child, 'FieldName'),
                                    ControlType: child.TypeName,
                                    Visible: getObservableProperty(child, 'Visible'),
                                    Label: self.getColumnLabel(
                                        getObservableProperty(child, 'Label'),
                                        child.Name,
                                        getObservableProperty(child, 'ShowLabel')),
                                    Name: child.Name
                                };

                                headerContext.FilteringFields.push(childFilterContext);
                            });
                        } else if (context.TypeName == "SegmentedEntry") {
                            // Segmented entry display value is stored in a reference table linked through the field's EDT
                            // eg. Table.LedgerDimension (int64 RecId) -> DimensionAttributeValueCombination.DisplayValue (str)
                            var childFilterContext = {
                                IsFilterable: getObservableProperty(context, 'IsFilterable'),
                                FormDataSourceName: context.ReferenceValueDataSourceName,
                                FieldName: context.ReferenceValueDataSourceFieldName,
                                ControlType: context.TypeName,
                                Visible: getObservableProperty(context, 'Visible'),
                                Label: self.getColumnLabel(
                                    getObservableProperty(context, 'Label'),
                                    context.Name,
                                    getObservableProperty(context, 'ShowLabel')),
                                Name: context.Name
                            };

                            headerContext.FilteringFields.push(childFilterContext);
                        } else {
                            var childFilterContext = {
                                IsFilterable: getObservableProperty(context, 'IsFilterable'),
                                FormDataSourceName: getObservableProperty(context, 'DataSourceName'),
                                FieldName: getObservableProperty(context, 'FieldName'),
                                ControlType: context.TypeName,
                                Visible: getObservableProperty(context, 'Visible'),
                                Label: self.getColumnLabel(
                                    getObservableProperty(context, 'Label'),
                                    context.Name,
                                    getObservableProperty(context, 'ShowLabel')),
                                Name: context.Name
                            };

                            headerContext.FilteringFields.push(childFilterContext);
                        }

                    } else {
                        name = element.getAttribute("data-dyn-controlname");
                        lastHeaderContext = lastHeaderMetadata && lastHeaderMetadata[name];
                        lastVersion = lastHeaderContext && lastHeaderContext.Version;

                        if (lastHeaderContext && lastHeaderContext.IsBound === false) {
                            headerContext = lastHeaderContext;
                        } else {
                            headerContext = {
                                FormDataSourceName: self.DataSourceName,
                                IsNumber: undefined,
                                Name: name,
                                IsFiltered: $dyn.observable(false),
                                IsSorted: $dyn.observable(-1),
                                Visible: undefined,
                                Alignment: undefined,
                                Label: undefined,
                                Width: undefined,
                                IsFilterable: $dyn.observable(false),
                                FieldName: "",
                                ControlType: "",
                                IsLookupField: false,
                                IsBound: false,
                                Version: lastVersion !== undefined ? lastVersion + 1 : 1,
                            };
                        }

                        if (name) {
                            controlContexts[name] = undefined;
                        }
                    }

                    headerMetadata.push(headerContext);
                    if (name) {
                        ///#DEBUG
                        if (listHeaderMetadata[name]) {
                            $dyn.logError('List: Duplicate control, name=%s', name);
                        }
                        ///#ENDDEBUG
                        listHeaderMetadata[name] = headerContext;
                    }
                });
            }

            lastHeaderMetadata = listHeaderMetadata;
            lastControlContexts = controlContexts;

            return headerMetadata;
        });

        self.RenderedHeaderMetadata = $dyn.observable();

        if (root[0]) {
            var listBox = root.find(".listView").first()[0];
            if (listBox) {
                self.ListBoxIdentifier = root.find(".listView").first()[0].id;
            }
        }

        self._rerenderOptions = $dyn.observable(0);
        self.rerender = function () {
            self._rerenderOptions($dyn.peek(self._rerenderOptions) + 1);
        };

        self._dataChangedVersion = $dyn.observable(0);
        self.dataChanged = function () {
            self._dataChangedVersion($dyn.peek(self._dataChangedVersion) + 1);
        };

        //observe column visiblity changes and rerender grid
        $dyn.observe(function () {
            var rowTemplates = element ? $dyn.value(element.dynChildren) : null;
            var columnVisibility = [];
            var firstRowColumns = null;
            if (rowTemplates && rowTemplates.length && rowTemplates.length > 0) {
                var rowtemplate = rowTemplates[0];
                var role = $dyn.role(rowtemplate);
                var context = $dyn.context(rowtemplate);
                // Elements can be added to dynChildren before they are data bound so take a dependency on databound.
                if (role && !role.isDataBound() && context) {
                    $dyn.log.list && $dyn.log.list("Role %o not databound yet", role);
                }
                firstRowColumns = $dyn.value(rowTemplates[0].dynChildren);
            }
            if (firstRowColumns && firstRowColumns.length) {
                for (var index = 0; index < firstRowColumns.length ; index++) {
                    var parentRole = $dyn.role(rowTemplates[0]);
                    var column = firstRowColumns[index];
                    var role = parentRole.getChildRole(column);
                    var context = $dyn.context(column);
                    // Elements can be added to dynChildren before they are data bound so take a dependency on databound.
                    if (!role.isDataBound() && context) {
                        $dyn.log.list && $dyn.log.list("Role %o at index %i for control %s not databound yet", role, index, column.getAttribute("data-dyn-controlname"));
                    }
                    if (context) {
                        columnVisibility.push($dyn.value(context.Visible));
                    }
                }
            }
            return columnVisibility;
        }, function (columnVisibility) {
            if (columnVisibility && columnVisibility.length > 0) {
                self.rerender();
            }

        });

        self._densityChangeHandler = function (evt, newDensity) {
            // Force the post render to redo the header layout when the list becomes visible again
            root.addClass('layoutPending');
            self.RowHeightInPixels = newDensity;
            self.ScrollbarSizeInPixels = newDensity * 2 / 3;
        };

        $(document).on("densitychanged", self._densityChangeHandler);

        self.IsNoResultsMessageDisplayed = $dyn.computed(function () {
            return $dyn.length(self.DataSource) === 0;
        });
        self.NoResultImage = $dyn.shell.rootPath.concat("resources/images/emptyGrid.svg");

        self.PerformNonEditModeNavigationAction = function (event) {
            /// <summary>
            /// Conditionally performs the list's navigation action when it is non-editable.
            /// </summary>
            if (!$dyn.value(self.AllowEdit) && $dyn.value(self.Enabled) && $dyn.value(self.NavigationAction) && $dyn.value(self.ForcePrimaryNavigation)) {
                $dyn.applyFunction(self.NavigationAction, self);
                if (event && event.preventDefault) {
                    event.preventDefault();
                }

                // Prevent the keyDown from bubbling so the form level enter shortcut is prevented.
                // See documentation on this behavior in $dyn.ui.Control constructor.
                event.stopPropagation();
                $dyn.log.stopPropagation && $dyn.log.stopPropagation('Prevent form level Enter shortcut.');
            }
            else if (self.IsNavigationAction(event) && $dyn.value(self.NavigationEnabled)) {
                var rowElement = $(event.target).closest('.listItem')[0];
                if (!rowElement) {
                    return;
                }

                var index = self.rowIndex(rowElement);

                event.preventDefault();
                event.stopPropagation();
                //clear marked rows before navigating since navigation is not supported with multiselection
                self.ClearMarkedState(index);

                // Update server on last selected control so it can retain focus if current grid is still active after navigation action is executed.
                if (self.Root && self.Root.updateLastClientSelectedControl) {
                    self.Root.updateLastClientSelectedControl();
                }
                $dyn.applyFunction(self.NavigationAction, self);
            }
        };

        self.ShowHeader = $dyn.computed(function () {
            return $dyn.value(self.Mode) === $dyn.ui.ListMode.tabular && $dyn.value(self.IsHeaderVisible);
        });

        self.isRowItemVisible = function (item, $scrollContainer) {
            /// <summary>
            /// Determines if a given row is currently visible within its current scroll region dimensions.
            /// </summary>
            return ((item.offsetTop >= $scrollContainer[0].scrollTop) && (item.offsetTop < $scrollContainer[0].scrollTop + $scrollContainer[0].clientHeight));
        };

        self.scrollItemIntoView = function (item, $scrollContainer) {
            /// <summary>
            /// Scroll the item into focus if a scroll container has been registered and the row item is not currently visible
            /// </summary>
            if ($scrollContainer && $scrollContainer[0] && item && !self.isRowItemVisible(item, $scrollContainer)) {
                $dyn.scrollIntoView(item, $scrollContainer[0]);
            }
        };

        var scrollRegion;
        var tableHeader;
        var lasthorizontalOffset;
        self.SyncHorizontalScrollOffset = function (log) {
            /// <summary>
            /// Synchronizes the header's horizontal scroll offset with the data table's horizontal scroll offset.
            /// </summary>
            /// <param name="log" optional="true>An optional logging function</param>

            if (!scrollRegion) {
                // Cache the header and scroll region
                scrollRegion = root.find(".scrollRegion").first();
                tableHeader = root.find(".table_header").first();
            }

            var horizontalOffset = -scrollRegion.scrollLeft();

            // for RTL we need special math just for webkit offset calculations
            if ($dyn.util.isRTLMode() && $dyn.util.isWebKit()) {
                var horizontalOffset = Math.abs(horizontalOffset);
                horizontalOffset = -(scrollRegion[0].scrollWidth - scrollRegion[0].clientWidth - horizontalOffset);
            }

            if (lasthorizontalOffset !== horizontalOffset) {
                // Only sync if changed set left for non RTL , right for RTL
                if (!$dyn.util.isRTLMode()) {
                    tableHeader.css('margin-left', horizontalOffset.toString() + 'px');
                }
                else {
                    tableHeader.css('margin-right', horizontalOffset.toString() + 'px');
                }

                log && log('List: Sync offset from %s to %s', lasthorizontalOffset, horizontalOffset);

                // if there is a open  grid header filter close it
                var filteredColumn = root.find(".columnHeaderWrapper[aria-expanded='true']");
                if (filteredColumn[0]) {
                    var context = $dyn.context(filteredColumn[0]);
                    if (context) {
                        filteredColumn[0].click();
                    }
                }
            }

            lasthorizontalOffset = horizontalOffset;
        };

        // Applys cache coordinate system shift that happens when cache expands upwards (0 index points to another record)
        // Updates server cache related indexes
        self.applyServerCacheCoordinateShift = function (shift) {
            self._rangePivot += shift;
            self.clientCacheStart += shift;
            self.LastSelectedIndex += shift;

            if (self._serverCacheCoordinateShiftHandler) {
                self._serverCacheCoordinateShiftHandler(shift);
            }
            self._InternalFocusedRowIndex += shift;
        };

        self.OnScroll = function (event) {
            self.SyncHorizontalScrollOffset();
            if (self._scrollHandler) {
                self._scrollHandler(event);
            }
            if (self._onScrollHandled) {
                self._onScrollHandled();
            }
        };

        self._updateRowMarking = function (recordMarkUpdate) {
            /// <summary>
            /// Updates the visualization for marking a record
            /// </summary>
            /// <param name="recordMarkUpdate">Object of marked and unmarked arrays indicating which records need to be marked and unmarked</param>
            if (self._supportsMarking()) {
                // Immediately visualize the change locally to avoid a feeling of lag.
                // Note: The following doesn't (and shouldn't) trigger an interaction.
                if (recordMarkUpdate.unmarked) {
                    for (var i = 0, len = recordMarkUpdate.unmarked.length; i < len; i++) {
                        var index = recordMarkUpdate.unmarked[i];
                        var rowElement = this.FindRowAtIndex(index);
                        if (rowElement) {
                            self._setRowMarking(rowElement, false);
                        }
                    }
                }

                if (recordMarkUpdate.marked) {
                    for (var i = 0, len = recordMarkUpdate.marked.length; i < len; i++) {
                        var index = recordMarkUpdate.marked[i];
                        var rowElement = this.FindRowAtIndex(index);
                        if (rowElement) {
                            $dyn.log.list && $dyn.log.list("List: Marking row index " + index);
                            self._setRowMarking(rowElement, true);
                        }
                    }
                }
            }
        };

        self.ExecuteRowMarkUpdate = function () {
            /// <summary>
            /// Executes a record mark update command.
            /// </summary>
            if (self._supportsMarking()) {
                $dyn.callFunction(self.ToggleMark, self);
            }
        };

        self.ClearMarkedState = function (selectedIndex) {
            /// <summary>
            /// Clears marked state of records.
            /// </summary>
            var startIndex = 0, endIndex = -1;
            var marksChanged = false;
            if (self._supportsMarking()) {
                var markedIndexes = self.MarkedIndexes();
                var numberOfRows = $dyn.length(self.DataSource);
                endIndex = numberOfRows - 1;
                var rowsToBeUnMarked = [];
                var updates = { unmarked: rowsToBeUnMarked };
                //TODO We should abstract away the isMarked property from the datasource
                // and project it on the grid itself
                for (var i = startIndex; i <= endIndex; i++) {
                    if (markedIndexes[i]) {
                        if (selectedIndex !== i) {
                            rowsToBeUnMarked.push(i);
                            marksChanged = true;
                        }
                    } else if (i == selectedIndex) {
                        updates.marked = [i];
                        marksChanged = true;
                    }
                }
                if (marksChanged) {
                    self._updateRowMarking(updates);
                }
            }
        };

        self.MarkRange = function (startIndex, endIndex) {
            /// <summary>
            /// Marks a range of items.
            /// </summary>
            /// <param name="start">The start index.</param>
            /// <param name="end">The end index.</param>
            if (self._supportsMarking()) {
                var markStartIndex, markEndIndex, unMarkStartIndex, unMarkEndIndex;
                var rowsToBeMarked = { marked: [], unmarked: [] };
                var numberOfRows = $dyn.length(self.DataSource);
                var markedIndexes = self.MarkedIndexes();

                // Unmark the marked rows
                for (var i = 0; i <= numberOfRows - 1; i++) {
                    if (markedIndexes[i]) {
                        rowsToBeMarked.unmarked.push(i);
                    }
                }

                // Mark the rows in the range
                if (startIndex > endIndex) {
                    var temp = startIndex;
                    startIndex = endIndex;
                    endIndex = temp;
                }

                if (endIndex >= numberOfRows) {
                    endIndex = numberOfRows - 1;
                }

                if (startIndex < 0) {
                    startIndex = 0;
                }

                for (var i = startIndex; i <= endIndex; i++) {
                    rowsToBeMarked.marked.push(i);
                }

                self._updateRowMarking(rowsToBeMarked);
            }
        };

        self.CalculateRowHeight = function () {
            // Use outerHeight to include row borders
            return root.find(".listView .listItem").first().outerHeight();
        };

        self.CalculateNumberOfVisibleRows = function () {
            /// <summary>
            /// Calculates the number of items currently visible in the viewport.
            /// </summary>
            var numVisibleItems = 0;
            var calculatedRowHeight = self.CalculateRowHeight();
            if (calculatedRowHeight > 0) {
                numVisibleItems = Math.floor(self.GetScrollRegionHeight() / calculatedRowHeight);
            }

            return numVisibleItems;
        };

        self.FindActiveElement = function () {
            /// <summary>
            /// Find the active element within the currently selected row.
            /// </summary>
            if (document.activeElement) {
                return self.GetSelectedRow().find(document.activeElement);
            }

            return null;
        };

        self.GetSelectedRow = function () {
            /// <summary>
            /// Gets the currently selected row in the grid.
            /// </summary>
            return root.find('.listItem[data-dyn-row-active=true]');
        };

        self.TransferInputFocus = function (targetRow, $previouslyActiveElement) {
            /// <summary>
            /// Transfers input focus across rows in keyboard navigation scenarios.
            /// </summary>
            /// <param name="targetRow">
            /// jQuery object representing the target row.
            /// </param>
            /// <param name="activeControl">
            /// jQuery object representing the element that was active.
            /// </param>

            var transferFocus = true;
            if (self.Root && !self.Root.TakeFocus) {
                transferFocus = false;
            }

            if (transferFocus) {
                var foundCellInTargetListItem = null;
                var isFocusTransferred = false;
                var blurEvent = null;
                var selectedCellName;
                var selectText = false;

                if ($previouslyActiveElement && $previouslyActiveElement.length) {
                    var foundCell = $previouslyActiveElement.closest('.listCell');
                    if (foundCell.length) {
                        // Look up the equivalent position in the target row.
                        foundCellInTargetListItem = targetRow.children().eq(foundCell.index());
                    }

                    var oldValue = $dyn.shell.clearBodyFocusCorrection();

                    blurEvent = jQuery.Event("blur");
                    $previouslyActiveElement.trigger(blurEvent);

                    $dyn.shell.bodyFocusCorrection = oldValue;
                    if (!$previouslyActiveElement.prop("readonly")) {
                        selectText = true;
                    }
                } else if (this._fastEdit()) {
                    selectedCellName = this._activeCell.controlName;
                    var foundCellInTargetListItem = targetRow.find('[data-dyn-controlname="' + escape(selectedCellName) + '"]');
                } else if (self.Root && self.Root.ActiveControl()) {
                    selectedCellName = self.Root.ActiveControl();
                    var foundCellInTargetListItem = targetRow.find('[data-dyn-controlname="' + escape(selectedCellName) + '"]');
                }

                if (foundCellInTargetListItem) {
                    var $focusTarget = null;
                    // First attempt: Find the first instance of data-dyn-focus and use that to find the focusable element. This may fail
                    // if the content contained isn't a control.
                    var $containerOfFocusableTarget = foundCellInTargetListItem.find('[data-dyn-focus]').first().addBack();
                    if ($containerOfFocusableTarget.length) {
                        var focusTargetSelector = $containerOfFocusableTarget.attr('data-dyn-focus');
                        if (focusTargetSelector) {
                            $focusTarget = $containerOfFocusableTarget.find(focusTargetSelector);
                        }
                    }

                    // Second attempt: Simply find the first occurence of an input element and use that as the focus target.
                    if (!$focusTarget || !$focusTarget.length) {
                        $focusTarget = foundCellInTargetListItem.find("input").first();
                    }

                    $dyn.log.list && $dyn.log.list.trace('List: [fast] Transfering focus to: %s', selectedCellName);
                    // Find the first input element give it focus.
                    if ((!blurEvent || !blurEvent.isDefaultPrevented()) && $focusTarget && $focusTarget.focus().length) {
                        isFocusTransferred = true;
                        if (selectText) {
                            $focusTarget.select();
                        }
                    }
                }
            }

            return transferFocus;
        };

        self.CurrentPageIndex = function () {
            // TODO: Move this on DataSource itself or create a generic helper somewhere else.s
            var currentIndex = -1;
            var ds = $dyn.value(self.DataSource);
            if (ds.length > 0 && ds[0] && ds[0].parent) {
                currentIndex = $dyn.value(ds[0].parent.pageIndex);
            }
            return currentIndex;
        };

        self.HasSinglePage = function () {
            var currentIndex = -1;
            var clientPageSize = -1;
            var ds = $dyn.value(self.DataSource);
            var numRows = $dyn.length(self.DataSource);
            if (ds && ds.length > 0 && ds[0] && ds[0].parent) {
                currentIndex = $dyn.value(ds[0].parent.pageIndex);
                clientPageSize = $dyn.value(ds[0].parent.clientPageSize);
            }
            /// TODO: Notice that the function has strange logic that prevents grid from having paging buttons (as it is invoked from ArePagingButtonsVisible)
            /// when data source doesn't support page index and page size properties (currentIndex === -1 or clientPageSize === -1)
            var pageSize = (clientPageSize && clientPageSize !== -1) ? clientPageSize : self.PageSize;
            if (numRows === 0 || (numRows < pageSize && currentIndex === 0) || clientPageSize <= 0) {
                return true;
            }
            return false;
        };

        self.ArePagingButtonsVisible = $dyn.computed(function () {
            var isVisible = true;
            if ($dyn.value(self.InfiniteScroll) || !$dyn.value(self.IsPagingSupported) || self.HasSinglePage()) {
                isVisible = false;
            }
            return isVisible;
        });

        /// <summary>
        /// Used for the computation of the new row index to send the row change interaction
        /// For editable grids this will always be the SelectedIndex property.
        /// For read-only grids the currently "selected" row could be different from the SelectedIndex (due to keyboard buffering
        /// upon a previous row change interaction). The _InternalFocusedRowIndex is an internal grid variable that maintains
        /// the currently selected row during key up/down events happening when a row change interaction is pending.
        /// </summary>
        self.GetPreviouslyFocusedRowIndex = function () {
            var previouslyFocusedRowIndex;
            if ($dyn.value(self.AllowEdit) && !$dyn.value(self._fastEdit)) {
                previouslyFocusedRowIndex = $dyn.value(self.SelectedIndex);
            }
            else {
                if (self._InternalFocusedRowIndex === undefined) {
                    self._InternalFocusedRowIndex = $dyn.value(self.SelectedIndex) + $dyn.value(self.FetchedDataStartPosition);
                }
                previouslyFocusedRowIndex = self.ConvertServerCacheIndexToPageIndex(self._InternalFocusedRowIndex);
            }

            return previouslyFocusedRowIndex;
        };

        self.FocusRowRelativeToActiveRow = function (relativePosition, markMode, event, restrictToRenderedRows) {
            /// <summary>
            /// Moves focus relative to the currently active row for select and toggle action using keyboard navigation scenarios.
            /// For range selection, the focused is moved relative to the rangePivot set on the grid.
            /// </summary>
            /// <param name="relativePosition">
            /// Integer indicating the amount to move relative to the current position.
            /// </param>
            /// <param name="markMode">
            /// The current mark mode indicating whether or not rows should be marked as part of the row focus change.
            /// </param>
            /// <param name="event">
            /// The event that triggerd this row change.
            var totalNumberOfRows = $dyn.length(self.DataSource);

            var previouslyFocusedRowIndex = self.GetPreviouslyFocusedRowIndex();

            var updatedFocusedRowIndex = previouslyFocusedRowIndex + relativePosition;

            if (self._fastEdit() && self.AllowEdit()) {
                self.InputMode = $dyn.ui.InputType.keyboard;
                self._fastEditUpdateRow(relativePosition, totalNumberOfRows, markMode, $activeElement, self.InputMode, event);
                return;
            }

            var withinRenderedRows = function (pageIndex) {
                var clientCacheIndex = self.ConvertPageIndexToClientCacheIndex(pageIndex);
                return clientCacheIndex >= 0 && clientCacheIndex < self.RenderedRowsCount();
            };

            if ($dyn.value(self.InfiniteScroll) && restrictToRenderedRows === true) {
                if (withinRenderedRows(previouslyFocusedRowIndex) && !withinRenderedRows(updatedFocusedRowIndex) && !self._isLastRow(updatedFocusedRowIndex - 1)) {
                    return false;
                }
            }

            if (updatedFocusedRowIndex != previouslyFocusedRowIndex) {
                // Call updaveValue from the active control so that any value changes are processed before the row change. Note that
                // if the validation fails we need to prevent the row change from happening.
                var $activeElement = self.FindActiveElement();
                if ($activeElement) {
                    var activeControl = $dyn.context($activeElement[0]);
                    if (activeControl) {
                        if (activeControl.updateValue) {
                            activeControl.updateValue(true);
                        }

                        if (activeControl.focusOutCleanup) {
                            activeControl.focusOutCleanup(true);
                        }
                    }
                }

                var hasInvalidControls = self.Root ? self.Root.HasInvalidControls() : false;
                if (!hasInvalidControls || $dyn.util.waitForClientBusy()) {
                    if (!$dyn.value(self.InfiniteScroll)) {
                        if (updatedFocusedRowIndex < 0) {
                            // Bring in previous page of records
                            var pageIndex = self.CurrentPageIndex();
                            var previousPageCallback = function previousPageCallback(success) {
                                if (!success) {
                                    $dyn.async(function () {
                                        // If LoadNextPage was not successful, go to the top row in the current page.
                                        var updatedRelativePosition = previouslyFocusedRowIndex;
                                        self.FocusRowRelativeToActiveRow(updatedRelativePosition, markMode, event);
                                    }, undefined, "Go to top row in current page");
                                }
                            };
                            $dyn.callFunction(self.LoadPreviousPage, self, [Math.abs(relativePosition).toString()], previousPageCallback);
                        } else if (updatedFocusedRowIndex >= totalNumberOfRows) {
                            // Bring in next page of records and set the next active row to the corresponding index in the next page.
                            var nextPageCallback = function nextPageCallback(success) {
                                if (!success) {
                                    $dyn.async(function () {
                                        // If LoadNextPage was not successful, go to the bottom row in the current page.
                                        var updatedRelativePosition = totalNumberOfRows - previouslyFocusedRowIndex - 1;
                                        self.FocusRowRelativeToActiveRow(updatedRelativePosition, markMode, event);
                                    }, undefined, "Go to bottom row in the current page.");
                                }
                            };

                            $dyn.callFunction(self.LoadNextPage, self, [relativePosition.toString()], nextPageCallback);
                        }
                    }

                    if ($dyn.value(self.InfiniteScroll) || (updatedFocusedRowIndex >= 0 && updatedFocusedRowIndex < totalNumberOfRows)) {
                        self.InputMode = $dyn.ui.InputType.keyboard;

                        self.UpdateRow(previouslyFocusedRowIndex, updatedFocusedRowIndex, totalNumberOfRows, markMode, $activeElement, self.InputMode, event);
                    }
                } else {
                    event.stopImmediatePropagation();

                    // Revert the row change visualization as there are invalid controls on the form.
                    self._invalidateRowSelection(updatedFocusedRowIndex, self.ConvertServerCacheIndexToPageIndex(self.LastSelectedIndex), markMode);

                    // Set focus to the invalid control
                    self.Root.SetFocusToInvalidControl();
                }
            }
        };

        self.RowKeyDown = function (event) {
            /// <summary>
            /// Handles key down events within a row.
            /// FLOW FOR SELECTION AND MARKING
            /// 1) User navigates through the grid using keyboard
            /// 2) Based on the key being used, the FocusRowRelativeToActiveRow function is called with the correct set of parameters.
            /// 3a) FocusRowRelativeToActiveRow calculates the index of the row that needs to be focused on based on the parameters passed to it.
            /// 3b) Any control changes are processed before processing row change
            /// 3c) If the client is processing interactions during this time we block and wait
            /// 3d) Once the client is free, we check if there are any invalid controls on the form.
            /// 3e) If there are no invalid controls, the selection visualization is updated. - New row is seen as selected and old row is seen as unselected.
            /// 3f) Calculate the markMode. i.e. whether it is a single row selection, range selection, toggling or marked records
            /// 3g) Update the marking visualization based on the markMode.
            /// 3h) If there are no modifier keys associated with the command and there is defaultAction/jumpRef action on the control then send the secondary navigation command to the server
            /// 3i) Call FocusRow which sends the ChangeSelectedIndex to the server with a callback
            /// 4ai) If changeRow on the server succeeds then ChangeSelectedIndex command has a return value of 1.
            /// 4aii) SelectedIndexSubscription is called as the SelectedIndex is updated by the server. This function switches the current active row to be inactive and makes the row at the new selected index as active.
            /// 4aiii) Ensures that the visualization controlled by data-dyn-row-active and aria-checked is correct on the active row.
            /// 4aiv) MarkedIndexes array that stores which records on the grid are marked is filled only after the server returns.
            /// 4bi) If changeRow on the server fails then ChangeSelectedIndex command has a return value of 0.
            /// 4bii) Callback associated with the ChangeSelectedIndex is invoked which reverses the aria-checked and data-dyn-row-active attributes on the records to ensure that the previous record has focus.
            /// </summary>
            /// <param name="event">
            /// The jQuery event for the key down.
            /// </param>
            /// <param name="index">
            /// The index of the row in which the key down occured.
            /// </param>
            /// <param name="rowDataContext">
            /// The data context of the row in which the key down occured.
            /// </param>

            var wasUpdatingSet;

            $dyn.log.list && $dyn.log.list("List: Row Key Down");

            // Grid does not have any shortcut using Ctrl+Shift
            // This is a temp fix until refactoring the shortcut handling to follow same pattern that other control are using
            if (event.altKey && event.shiftKey) {
                return;
            }

            if (self._fastEdit() && self.AllowEdit()) {
                // TODO: Needed?
                if (!$dyn.peek($dyn.ui.updating)) {
                    $dyn.ui.updating(true);
                    wasUpdatingSet = true;
                }

                self._setUserActionBusy();
            }


            var rowElement = $(event.target).closest('.rowTemplate')[0];
            var index = this.SelectedIndex();
            if (rowElement) {
                index = this.rowIndex(rowElement);
            }
            var rowMarkedUpdates;
            // calculateMarkMode() handles the modifier keys in the up/down arrow cases

            if (!event.altKey && event.which == $dyn.ui.KeyCodes.downArrow) {
                self.FocusRowRelativeToActiveRow(1, this.calculateMarkMode(event), event, true);
                event.stopPropagation();
            }
            else if (!event.altKey && event.which == $dyn.ui.KeyCodes.upArrow) {
                self.FocusRowRelativeToActiveRow(-1, this.calculateMarkMode(event), event, true);
                event.stopPropagation();
            }
            else if ($dyn.util.isShortcutKeyPressed(event, { keyCode: $dyn.ui.KeyCodes.pageDown })) {
                self.FocusRowRelativeToActiveRow(self.CalculateNumberOfVisibleRows(), this.calculateMarkMode(event), event);
            }
            else if ($dyn.util.isShortcutKeyPressed(event, { keyCode: $dyn.ui.KeyCodes.pageUp })) {
                self.FocusRowRelativeToActiveRow(-self.CalculateNumberOfVisibleRows(), this.calculateMarkMode(event), event);
            }
            else if ($dyn.util.isShortcutKeyPressed(event, { ctrl: true, keyCode: $dyn.ui.KeyCodes.spacebar })) {
                var isRowCurrentlyMarked = this.IsMarked(index);
                self._updateRowMarking({ marked: !isRowCurrentlyMarked ? [index] : [], unmarked: isRowCurrentlyMarked ? [index] : [] });
                self.ExecuteRowMarkUpdate();
                event.preventDefault();
            }
            else if ($dyn.util.isShortcutKeyPressed(event, { ctrl: true, shift: true, keyCode: $dyn.ui.KeyCodes.letterM })) {
                if (!$dyn.value(self.AllowEdit)) {
                    self.ToggleMarkAllRecordsMode();
                    event.preventDefault();
                    event.stopPropagation();
                } else {
                    event.preventDefault();
                    event.stopPropagation();
                }

            }
            else if ($dyn.util.isShortcutKeyPressed(event, { keyCode: $dyn.ui.KeyCodes.enter })) {
                self.PerformNonEditModeNavigationAction(event);
            }

            else if ($dyn.util.isShortcutKeyPressed(event, { ctrl: true, keyCode: $dyn.ui.KeyCodes.letterG })) {
                var $activeElement = self.FindActiveElement();
                if ($activeElement) {
                    var dynControl = $dyn.context($activeElement[0]);
                    if (dynControl) {
                        var columnHeaderName = dynControl.Name;
                        if (dynControl.ReferenceGroup) {
                            columnHeaderName = dynControl.ReferenceGroup.Name;
                        }
                        var $activeColumnHeader = root.find($dyn.format('[data-dyn-columnname={0}]', columnHeaderName)).find('.columnHeaderWrapper');
                        if ($activeColumnHeader) {
                            $activeColumnHeader.click();
                            event.preventDefault();
                            event.stopPropagation();
                            $dyn.log('Event propagation stopped for ctrl+g inside grid');
                        }
                    }
                }
            }
                //If gridFastEdit debug flag is set then allow client to get ahead of the server when creating new rows
            else if ($dyn.util.isShortcutKeyPressed(event, { alt: true, keyCode: $dyn.ui.KeyCodes.letterN }) && self._fastEdit()) {
                // alt-n
                self._createNewRow(self._getRowElementFromEvent(event));
                event.preventDefault();
                event.stopPropagation();
            }
            else if ($dyn.util.isShortcutKeyPressed(event, { keyCode: $dyn.ui.KeyCodes.tab }) && self._fastEdit()) {
                this._onTabKey(event);
            } else if ($dyn.util.isShortcutKeyPressed(event, { shift: true, keyCode: $dyn.ui.KeyCodes.tab }) && self._fastEdit()) {
                this._onTabKey(event);
            }

            if (wasUpdatingSet) {
                $dyn.ui.updating(false);
            }
        };

        self.OnKeyboardSecondaryNavigation = function (event) {
            if (!$dyn.value(self.Enabled)) {
                event.preventDefault();
            }
            else if (self.IsNavigationAction(event) && $dyn.value(self.NavigationEnabled)) {
                var rowElement = $(event.target).closest('.rowTemplate')[0];
                if (!rowElement) {
                    return;
                }

                var index = self.rowIndex(rowElement);

                event.preventDefault();
                //clear marked rows before navigating since navigation is not supported with multiselection
                self.ClearMarkedState(index);
                $dyn.function(self.NavigationAction)();
            }
        };

        self.OnMouseSecondaryNavigation = function (event) {
            function preventDefaultAndStopPropagation() {
                event.preventDefault();
                event.stopImmediatePropagation();
                $dyn.log.stopPropagation && $dyn.log.stopPropagation('Mouse Secondary Navigation (Navigation Action)');
            }

            if (!$dyn.value(self.Enabled)) {
                preventDefaultAndStopPropagation();
            }
            else {
                var rowElement = $(event.target).closest('.rowTemplate')[0];
                if (!rowElement) {
                    return;
                }
                var index = self.rowIndex(rowElement);

                // ForcePrimaryNavigation = is the grid in a lookup
                if (!$dyn.value(self.AllowEdit) && $dyn.value(self.Enabled) && $dyn.value(self.NavigationAction) && $dyn.value(self.ForcePrimaryNavigation)) {
                    // If the target control is editable inside the readonly grid
                    // then we should not stop the propagation and the default action
                    var controlContext = $dyn.context(event.target);
                    if (!controlContext || !$dyn.value(controlContext.AllowEdit)) {
                        preventDefaultAndStopPropagation();
                    }

                    // ForcePrimaryNavigation execute the navigation regardless of where it was clicked
                    $dyn.function(self.NavigationAction)();
                }
                else if (self.IsNavigationAction(event) && $dyn.value(self.NavigationEnabled)) {
                    self.ClearMarkedState(index);

                    if ($dyn.util.clickedText(event.target) || self.IsCardList()) {
                        // Only preventDefault and navigate if the text is clicked or its a card list. In other cases (such as if the lookup button is clicked) do not navigate.
                        preventDefaultAndStopPropagation();
                        $dyn.function(self.NavigationAction)();
                    }
                }
                else if (!self.IsValidSecondaryNavigation(event)) {
                    preventDefaultAndStopPropagation();
                }
            }
        };

        self.IsNavigationAction = function (event) {
            /// <summary>
            /// Indicates whether or not the event should trigger a navigation action.
            /// </summary>
            /// <param name="navigationEvent">The jQueryEvent which may cause a navigation action.</param>
            /// <returns><c>true</c> if a naviation action should be performed; otherwise, <c>false</c>.</returns>
            return $dyn.value(self.NavigationEnabled) && (self.IsCardList() || self.TargetSupportsNavigation(event));
        };

        self.IsCardList = function () {
            if (self._isCardList === undefined) {
                self._isCardList = $(self.element).hasClass('cardList');
            }
            return self._isCardList;
        };

        self.TargetSupportsNavigation = function (event) {
            var listCell = $(event.target).closest('.listCell');
            return listCell && listCell.hasClass('supportsNavigation');
        };

        self.IsValidSecondaryNavigation = function (event) {
            /// <summary>
            /// Determines whether or not the secondary navigation action is valid.
            /// </summary>
            /// <param name="event">The event representing the secondary navigation action.</param>
            /// <returns><c>true</c> if the secondary navigation action is allowed; otherwise, <c>false</c>.</returns>
            var closestListItem = $(event.target).closest('.listItem');
            return closestListItem.length === 1 || closestListItem.hasClass('marked');
        };

        self.FocusRowAndSelectCell = function (index, totalNumberOfRows, markMode, inputMode, event) {
            $dyn.log.list && $dyn.log.list("List: Entered FocusRowAndSelectCell");
            self.InputMode = inputMode;

            self.FocusRow(index, markMode, inputMode, event);
            // Call the base SelectCell implementation to update the active control in the case of keyboard navigation
            if (event) {
                if (!event.ctrlKey && !event.shiftKey && !event.altKey) {
                    if (event.target) {
                        var controlToSelect = self._getParentControlName(event.target);
                        if (controlToSelect && index >= 0 && index < totalNumberOfRows) {
                            $dyn.callFunction(self.SelectCell, self, [index, controlToSelect, inputMode, false]);
                        }
                    }
                }
            }
            $dyn.log.list && $dyn.log.list("List: Leaving FocusRowAndSelectCell");
        };

        /// <summary>
        /// Handles all key down events while there's a pending row change interaction.
        /// This method will update various internal grid variables that will be used after
        /// the row change interaction has been processed
        /// </summary>
        self.BlockingDivAllowedKeyDown = function (event) {
            $dyn.log.list && $dyn.log.list("List: Entered BlockingDivAllowedKeyDown handler");
            self.RowKeyDown(event);
            $dyn.log.list && $dyn.log.list("List: Leaving BlockingDivAllowedKeyDown handler");
        };

        /// <summary>
        /// This method gets called either normally (via the RowKeyDown handler) OR via the BlockingDivKeyDown handler OR via the Command callback
        /// Update marking and selection and send row change interaction in all cases
        /// </summary>
        self.UpdateRow = function (previouslyFocusedRowIndex, updatedFocusedRowIndex, totalNumberOfRows, markMode, activeElement, inputMode, event) {
            $dyn.log.list && $dyn.log.list("List: PreviouslyFocusedRowIndex: " + previouslyFocusedRowIndex);
            $dyn.log.list && $dyn.log.list("List: NewFocusedRowIndex: " + updatedFocusedRowIndex);
            $dyn.log.list && $dyn.log.list("List: FetchedDataStartPosition: " + $dyn.value(self.FetchedDataStartPosition));
            var cacheIndex = updatedFocusedRowIndex + $dyn.value(self.FetchedDataStartPosition);
            var cacheSize = $dyn.value(self.CacheSize);
            $dyn.log.list && $dyn.log.list("List: Cache Index: " + cacheIndex);
            $dyn.log.list && $dyn.log.list("List: Cache Size: " + cacheSize);

            if (cacheIndex < 0) {
                cacheIndex = updatedFocusedRowIndex = 0;
            }
            else if (cacheIndex >= cacheSize + 1 && cacheSize > 0) {
                updatedFocusedRowIndex = cacheSize - $dyn.value(self.FetchedDataStartPosition) - 1;
                cacheIndex = cacheSize - 1;
            }

            self._InternalFocusedRowIndex = cacheIndex;

            $dyn.log.list && $dyn.log.list("List: UpdatedFocusedRowIndex: " + updatedFocusedRowIndex);
            self.SelectAndMarkRow(previouslyFocusedRowIndex, updatedFocusedRowIndex, markMode, activeElement);
            self.FocusRowAndSelectCell(updatedFocusedRowIndex, totalNumberOfRows, markMode, inputMode, event);

            if (event && event.preventDefault) {
                event.preventDefault();
            }
        };

        /// <summary>
        /// Selects the new row for updatedFocusedRowIndex. Also clears the marking for previouslyFocusedRowIndex
        /// If grid is in edit mode then the new row will be marked as well
        /// </summary>

        self.SelectAndMarkRow = function (previouslyFocusedRowIndex, updatedFocusedRowIndex, markMode, activeElement) {
            $dyn.log.list && $dyn.log.list("List: SelectAndMarkRow: Previous row index: %s; New row index: %s", previouslyFocusedRowIndex, updatedFocusedRowIndex);
            var targetRow = $(self.FindRowAtIndex(updatedFocusedRowIndex));
            //Change row selection to the new index
            self.UpdateRowSelection(previouslyFocusedRowIndex, updatedFocusedRowIndex);

            if (previouslyFocusedRowIndex != updatedFocusedRowIndex) {
                if (markMode == $dyn.controls.HierarchicalGridBase.MarkMode.rangeSelect) {
                    // Mark all of the records in the set bounded by rangePivot and updatedFocusedRowIndex.
                    self.MarkRange(self.rangePivotWithinPage(), updatedFocusedRowIndex);
                }

                if (markMode === $dyn.controls.HierarchicalGridBase.MarkMode.select) {
                    //Clear previous marking but don't mark the new index
                    //If there are no pending keydown events when the response to the row change interaction arrives then ApplyChanges will do the marking
                    //Note: This could result in a slight delay from the time the row is selected till the time it is marked
                    //If we try to mark the new index then quick keystrokes will result in a trail of marked rows that eventually get cleared
                    //as the rendering is slower than the rate of keydowns
                    var markRow = $dyn.value(self.AllowEdit) ? updatedFocusedRowIndex : -1;
                    self.ClearMarkedState(markRow);
                }

                // Only transfer focus if the hosting form can take focus
                var transferFocus = self.TransferInputFocus(targetRow, activeElement);

                //If we were not able to transfer focus, scroll the item into view
                if (!transferFocus) {
                    self.scrollItemIntoView(targetRow[0], self._$scrollContainer);
                }
            }

            return transferFocus;
        };

        self.FocusRow = function (rowIndex, markMode, inputMode, event) {
            /// <summary>
            /// Focuses the row at the specified index.
            /// </summary>
            $dyn.log.list && $dyn.log.list("List: FocusRow: SelectedIndex: " + $dyn.value(self.SelectedIndex));
            $dyn.log.list && $dyn.log.list("List: FocusRow: Change to row index " + rowIndex);
            var startTime, endTime, duration;
            if ($dyn.value(self.SelectedIndex) != rowIndex || self._PendingRowChangeCount() > 0 || $dyn.value(self.MarkAllRecords)) {
                var activeContext = $dyn.context($dyn.value(self.Root && self.Root.ActiveElement));
                if (activeContext) {
                    if (activeContext.updateValue) {
                        activeContext.updateValue(true);
                    }

                    if (activeContext.focusOutCleanup) {
                        activeContext.focusOutCleanup(true);
                    }
                }

                // if we are crossing a ServiceBoundary in this case a row change
                // we need to make sure there are no pending data changes
                var hasInvalidControls = self.Root ? self.Root.HasInvalidControls() : false;
                if (!hasInvalidControls || $dyn.util.waitForClientBusy()) {
                    var prevRowIndex = self.LastSelectedIndex;
                    var prevRowPageIndex = self.ConvertServerCacheIndexToPageIndex(prevRowIndex);
                    var changeSelectedIndexCallback = function changeSelectedIndexCallback(success) {
                        endTime = performance.now();
                        duration = endTime - startTime;
                        $dyn.log.list && $dyn.log.list("List: Duration of ChangeSelectedIndex: %s ms", duration);
                        self._PendingRowChangeCount(self._PendingRowChangeCount() - 1);
                        $dyn.log.list && $dyn.log.list("List: ChangeRowCallback: Decrementing _PendingRowChangeCount to " + $dyn.value(self._PendingRowChangeCount));
                        if (!success) {
                            // Change selected index failed on the server.
                            $dyn.log.list && $dyn.log.list("Change selected row failed on the server");

                            self._invalidateRowSelection(rowIndex, prevRowPageIndex, markMode);

                            // Revert the LastSelectedIndex to the prevRowElements index
                            self.LastSelectedIndex = prevRowIndex;
                        }
                    };

                    self._initialDataStartPosition = $dyn.value(self.FetchedDataStartPosition);

                    //If grid in fast edit mode then create pending row and enqueue into _pendingRowsQueue, else send the interaction
                    // TOOD: allow edit check is for lookups navigation but allow edit can have navigation as well
                    if (self._fastEdit() && $dyn.value(self.AllowEdit)) {
                        self._changePendingSelectedIndex(rowIndex, markMode, inputMode, event);
                    } else {
                        var shouldThrottle = (inputMode === $dyn.ui.InputType.keyboard);
                        $dyn.log.list && $dyn.log.list("List: FocusRow: Sending ChangeSelectedIndex for row " + rowIndex);

                        self._PendingRowChangeCount(self._PendingRowChangeCount() + 1);
                        $dyn.log.list && $dyn.log.list("List: FocusRow: Incrementing _PendingRowChangeCount to " + $dyn.value(self._PendingRowChangeCount));
                        startTime = performance.now();
                        $dyn.callFunction(self.ChangeSelectedIndex, self, [rowIndex, markMode, self.rangePivotWithinPage(), shouldThrottle, changeSelectedIndexCallback]);
                    }

                    self.LastSelectedIndex = self.ConvertPageIndexToServerCacheIndex(rowIndex);
                    return rowIndex;
                } else {
                    // Revert the row change visualization as there are invalid controls on the form.
                    self._invalidateRowSelection(rowIndex, self.ConvertServerCacheIndexToPageIndex(self.LastSelectedIndex), markMode);

                    // Set focus to the invalid control. And log the boundary error
                    if (self.Root) {
                        self.Root.SetFocusToInvalidControl();
                    } else {
                        $dyn.errorMessageBox({ Text: $dyn.label('BoundaryError'), Label: 'BoundaryError' });
                    }
                }
            } else {
                if (self._fastEdit() && !self._activeRowValid()) {
                    self._setCurrentPendingRow(null);
                    //User is trying to work (fix) on the active row
                    //If there's an activeRowValid flag set to false, reset that to true so as to re-process a pending row
                    self._activeRowValid(true);
                }
            }

            return $dyn.value(self.SelectedIndex);
        };

        // The row index used by ChangeSelectedIndex is in a volatile coordinate system, check the
        // data start position as late as possible to recalculate the row index if necessary
        self.recalculateRowIndex = function (initialRowIndex) {
            var currentDataStartPosition = $dyn.value(self.FetchedDataStartPosition);
            if (self._initialDataStartPosition == currentDataStartPosition) {
                return initialRowIndex;
            }

            var newRowIndex = initialRowIndex + self._initialDataStartPosition - currentDataStartPosition;
            $dyn.log.list && $dyn.log.list("List: recalculateRowIndex: Updating ChangeSelectedIndex row from %s to %s", initialRowIndex, newRowIndex);
            return newRowIndex;
        };

        self.headerLayoutTimer = null;
        self.incrementRenderCountOnHeaderLayout = false;
        self.performHeaderLayout = function (incrementRenderCount) {
            if ($dyn.value(self.ShowHeader)) {
                root.find('.table_header').layoutFloatingTableHeader(root.find('.TabularView'));

                self.incrementRenderCountOnHeaderLayout = false;
            }
        };

        // Represents the internal post render
        self.postRenderInternal = function (onlyExecuteIfLayoutPending) {
            if (root.closest("[data-dyn-lastvisible='false']").length > 0) {
                // Performance note: Don't use the jQuery :visible pseudo-selector here as it adds a large amount of overhead. Keep it light-weight
                // and handle the cases that we care about.

                // Post render layout can only run if the List is visible. The layout system will notify the List when
                // the next layout event occurs--at which point list will attempt layout again.

                if (!onlyExecuteIfLayoutPending) {
                    // Flag the control as having pending layout that cannot be completed.
                    root.addClass('layoutPending');
                }
            } else if (!onlyExecuteIfLayoutPending || root.hasClass('layoutPending')) {
                // The control is visible and is processing a pending layout or can have layout run immediately during the foreach render pass.
                root.removeClass('layoutPending');
                self.ApplyAutoSizing();
                if ($dyn.value(self.Mode) !== $dyn.ui.ListMode.tabular) {
                    self.ForEachRenderCount($dyn.value(self.ForEachRenderCount) + 1);
                } else {
                    // Note that for tabular ists render count is incremented synchronously with header layout so that the
                    // header is not prematurely displayed to the user before rendering is completed.
                    self.performHeaderLayout(true);
                }

                // Call the public post render callback, if any
                $dyn.applyFunction(self.PostRender, self);

                var form = self.Root;
                // We don't allow to restore focus in infinite scroll mode as it can break good user experience (focus restoration also performs scroll if an element is out of view).
                if (!$dyn.value(self.InfiniteScroll) && form && form.restoreFocus) {
                    form.restoreFocus(self.element);
                }
            }
        };

        self.forceRenderCardListIE = function () {
            /// This is a workaround for an IE rendering issue.
            /// IE renders cardlist grid in one column due to some IE only issues with nested flexbox container with flex-wrap on them
            /// With nested flexbox container sometimes IE calculates the height and width of top container incorrectly which causes the inner containers to all render in one column
            /// However, forcing the browser to re-render the parent container will fix the problem
            /// Here we check if the layout provider is css3grid (the provider we use for IE) and also if 'cardlist' class exists on grid
            /// and then hide and show the list body to force the browser to re-render (recalculate) flex wrap behavior
            if ($dyn.layout.provider.name === $dyn.layout.Providers.css3grid && $dyn.value(self.VisibleColumnCount) != 1 && self.element.classList.contains('cardList')) {
                var display = self._listBodyElement.style.display;
                self._listBodyElement.style.display = 'none';
                self._listBodyElement.style.display = display;
            }
        };

        self.restoreSelectionFocus = function () {
            $dyn.log.list && $dyn.log.list("List: [fast] Restoring focus");
            if (self._fastEdit() && self._currentPendingRow) {
                self._restoreFocusToPendingRow();
            } else {
                self.Root && self.Root.restoreFocus && self.Root.restoreFocus(self.element);
            }
        };

        self.getDataColumnContextsByIndex = function (index) {
            var foundColumns = [];
            var rowTemplates = $dyn.value(element.dynChildren);
            if (rowTemplates.length) {
                jQuery.each(rowTemplates, function (rowTemplateIndex, rowTemplate) {
                    var rowColumns = $dyn.value(rowTemplate.dynChildren);
                    if (rowColumns) {
                        var foundColumn = rowColumns[index];
                    }

                    if (foundColumn) {
                        foundColumns.push($dyn.context(foundColumn));
                    }
                });
            }

            return foundColumns;
        };

        //Since Grid and Table have different column counts, this overriddable method has been introduced to allow for Grid and Table to
        //convert from their header's column index to their content's column index.
        self.interiorColumnIndex = function (headerIndex) {
            // The header column index is reduced by 2 to compensate for the "mark", and "repeater-template" columns.
            return headerIndex - 1;
        };

        self.findControlElementsInAllRows = function (controlName) {
            return $('[data-dyn-controlname="' + controlName + '"]', root);
        };

        self.IsStaticRowHeightValid = function () {
            /// <summary>
            /// A static row height is used as a performance optimization in most cases (avoids an expensive height calculation; ~100ms). However, in some
            /// cases the static row height is not acceptable--such as in multi-line string edits and images.
            /// </summary>
            return root.find(".listItem").last().find(".customCellHeight").length === 0;
        };

        self.UpdateRowSelection = function (oldIndex, newIndex) {
            /// <summary>
            /// Updates the visualization of the row at the passed in rowIndex to indicate that it is the selected record before the server returns.
            /// The selection visualization on the old record is updated as well.
            /// </summary>
            if (oldIndex != newIndex) {
                if (self._selectingElement) {
                    self._selectingElement.removeAttribute('data-dyn-row-selecting');
                    self._selectingElement = null;
                }

                // Remove the data-dyn-row-active attribute off the previous row
                var rowElement = this.FindRowAtIndex(oldIndex);
                if (rowElement) {
                    rowElement.setAttribute('data-dyn-row-active',false);
                }

                // Set the data-dyn-row-active attribute on the new row.
                rowElement = this.FindRowAtIndex(newIndex);
                if (rowElement) {
                    $dyn.log.list && $dyn.log.list("List: Selecting index " + newIndex);
                    if (self.InputMode === $dyn.ui.InputType.mouse) {
                        rowElement.setAttribute('data-dyn-row-active', true);
                    }
                    else {
                        self._selectingElement = rowElement;
                        rowElement.setAttribute('data-dyn-row-selecting', true);
                    }
                }
            }
        };

        self.RowClicked = function (event) {
            /// <summary>
            /// Handles mouse click events within a row.
            /// FLOW FOR SELECTION AND MARKING:
            /// 1) User clicks a record
            /// 2) Update the selection visualization - New row is seen as selected and old row is seen as unselected.
            /// 3) Calculate the markMode. i.e. whether it is a single row selection, range selection, toggling or marked records
            /// 4) Update the marking visualization based on the markMode.
            /// 5) If there are no modifier keys associated with the command and there is defaultAction/jumpRef action on the control then send the secondary navigation command to the server
            /// 6) Call FocusRow which sends the ChangeSelectedIndex to the server with a callback
            /// 6ai) If changeRow on the server succeeds then ChangeSelectedIndex command has a return value of 1.
            /// 6aii) SelectedIndexSubscription is called as the SelectedIndex is updated by the server. This function switches the current active row to be inactive and makes the row at the new selected index as active.
            /// 6aiii) Ensures that the visualization controlled by data-dyn-row-active and aria-checked is correct on the active row.
            /// 6aiv) MarkedIndexes array that stores which records on the grid are marked is filled only after the server returns.
            /// 6bi) If changeRow on the server fails then ChangeSelectedIndex command has a return value of 0.
            /// 6bii) Callback associated with the ChangeSelectedIndex is invoked which reverses the aria-checked and data-dyn-row-active attributes on the records to ensure that the previous record has focus.
            /// </summary>
            /// <param name="event">
            /// The jQuery event for the click.
            /// </param>

            if (self._fastEdit() && self.AllowEdit()) {
                self._setUserActionBusy();
            }

            $dyn.log.list && $dyn.log.list("List: RowClicked");

            var rowElement = $(event.target).closest('.rowTemplate')[0];
            var targetElement = event.target;
            if (!rowElement || self._hasTextSelection(event)) {
                return;
            }

            self.InputMode = $dyn.ui.InputType.mouse;
            var index = this.rowIndex(rowElement);
            var prevSelectedIndex = self.GetPreviouslyFocusedRowIndex();
            var cacheIndex = index + $dyn.value(self.FetchedDataStartPosition);

            self._InternalFocusedRowIndex = cacheIndex;
            $dyn.log.list && $dyn.log.list("List: RowClicked:_InternalFocusedRowIndex is " + cacheIndex);

            var markMode;
            var isRowCurrentlyMarked = this.MarkedIndexes()[index];
            this.UpdateRowSelection(prevSelectedIndex, index);

            switch (event.which) {
                case $dyn.ui.MouseButtonCodes.leftButton:
                    if (!self._isInvalidMarkTarget(event)) {
                        markMode = this.calculateMarkMode(event);
                        $dyn.log.list && $dyn.log.list("List: Mark Mode is " + markMode);
                        if (markMode === $dyn.controls.HierarchicalGridBase.MarkMode.toggle) {
                            self._updateRowMarking({ marked: !isRowCurrentlyMarked ? [index] : [], unmarked: isRowCurrentlyMarked ? [index] : [] });
                            if (index == prevSelectedIndex) {
                                self.ExecuteRowMarkUpdate();
                            }
                        } else if (markMode === $dyn.controls.HierarchicalGridBase.MarkMode.rangeSelect) {
                            self.MarkRange(self.rangePivotWithinPage(), index);
                        }
                    }
                    if (markMode === $dyn.controls.HierarchicalGridBase.MarkMode.select) {
                        self.ClearMarkedState(index);

                        // When you click on the same rowIndex, mark the record only if it is not marked.
                        if (index == prevSelectedIndex && !self.MarkedIndexes()[index]) {
                            self.ExecuteRowMarkUpdate();
                        }
                    }

                    var totalNumberOfRows = $dyn.length(self.DataSource);

                    if (self._fastEdit()) {
                        self._fastEditFocusRowAndSelectCell(null, totalNumberOfRows, markMode, self.InputMode, event);
                    }
                    else {
                        self.FocusRowAndSelectCell(index, totalNumberOfRows, markMode, self.InputMode, event);
                    }

                    self.OnMouseSecondaryNavigation(event);
                    break;

                case $dyn.ui.MouseButtonCodes.rightButton:
                    markMode = this.calculateMarkMode(event);
                    if (!self._isInvalidMarkTarget(event)) {

                        // Right mouse click.
                        self._updateRowMarking({ marked: !isRowCurrentlyMarked ? [index] : [], unmarked: isRowCurrentlyMarked ? [index] : [] });
                    }
                    if (index == prevSelectedIndex) {
                        self.ExecuteRowMarkUpdate();
                    }

                    // Prevent the default browser context menu from being presented.
                    event.preventDefault();
                    self.FocusRow(index, markMode);
                    break;
            }
        };

        // Observable that will be toggled when the grid's internal contents height has been overridden
        self.HeightOverride = $dyn.observable(false);
        self.CachedScrollRegionHeight;
        self.CachedScrollRegionTop;
        self.scrollRegion;

        self.WindowResizeHandler = function () {
            self.UpdateScrollRegionWidth();

            if (self._resizeHandler) {
                self._resizeHandler();
            }
        };

        self.ApplyAutoSizing = function () {
            var scrollRegion = self.GetScrollRegion();
            var tableHeader = root.find(".table_header").first();
            if (scrollRegion.length > 0) {
                $dyn.observe($dyn.options(function () { 
                    return { 
                        layoutHeight: self.LayoutHeight, 
                        visibleRows: self.VisibleRowCount, 
                        rowHeight: self.RowHeightInPixels, 
                        scrollbarSize: self.ScrollbarSizeInPixels,
                        override: self.HeightOverride }; 
                }, $dyn.ui.updating), function (options) {
                    var layoutHeightToLower = options.layoutHeight.toLowerCase();
                    var heightOverride = options.override;
                    if ((scrollRegion.__layoutHeight && scrollRegion.__layoutHeight !== layoutHeightToLower)
                        || (scrollRegion.__layoutHeightOverride && scrollRegion.__layoutHeightOverride !== heightOverride)) {
                        scrollRegion.height("");
                    }
                    else {
                        scrollRegion.__layoutHeight = layoutHeightToLower;
                        scrollRegion.__layoutHeightOverride = heightOverride;
                    }
                    // If heightOverride is true, the grid should behave as SizeToContent height, and use the visibleRows option.
                    var visRows = (layoutHeightToLower != "auto" && layoutHeightToLower != "sizetocontent" && !heightOverride) ? 0 : options.visibleRows;
                    var userDensity = $dyn.densityThemeManager._defaultDensity;
                    if ($dyn.user && $dyn.user().Density) {
                        userDensity = $dyn.value($dyn.user().Density);
                    }

                    var rowHeight = (options.rowHeight || userDensity) + 3 /* 3px for margins */;
                    var scrollbarSize = options.scrollbarSize || userDensity * 2 / 3;
                    if (!self.IsStaticRowHeightValid() || ($dyn.value(self.Mode) === $dyn.ui.ListMode.list)) {
                        // The statically defined row height cannot be used because there are controls that span
                        // more than a single "normal" row in height.
                        var lastListItem = root.find(".listItem").last();
                        if (lastListItem.length > 0) {
                            rowHeight = lastListItem[0].offsetHeight;
                        }
                    }

                    if (visRows > 0 && rowHeight != 0) {
                        self.SetScrollRegionHeight(rowHeight * visRows + scrollbarSize);
                    }
                });

                $dyn.observe($dyn.options(function () { return { visibleColumns: self.VisibleColumnCount }; }, $dyn.ui.updating), function (options) {
                    if (options.visibleColumns > 0) {
                        //Visiblecolumns property does not align with current form patterns having SizeToAvaialble as the sizing
                        //Disable this property untill the property specs are finalized or property is deprecated officially
                        //var width = 0;
                        //var columnCount = 0;
                        //if ($dyn.value(self.Mode) === $dyn.ui.ListMode.tabular)
                        //{
                        //    var headers = tableHeader.find(".columnHeader:not(.repeater-template)");
                        //    for (var i = 0 ; i < headers.length ; i++) {
                        //        if (columnCount >= options.visibleColumns) {
                        //            break;
                        //        }
                        //        var colWidth = $(headers[i]).outerWidth();
                        //        if (colWidth > 0) {
                        //            width += colWidth;
                        //            columnCount = columnCount + 1;
                        //        }

                        //    };

                        //    if (root.attr("data-dyn-hidemarkcolumn") === undefined || root.attr("data-dyn-hidemarkcolumn") === true) {
                        //        width += tableHeader.find(".markAllContainer").outerWidth();
                        //    }

                        //    if (width > 0) {
                        //        root.width(width);
                        //    }
                        //}
                    }
                });
            }
        };
    };

    $dyn.controls.HierarchicalGridBase.prototype = $dyn.ui.extendPrototype($dyn.ui.Control.prototype, {
        IsSelectionIndicatorVisible: true,
        IsHeaderVisible: true,
        //  DataSource is an array of object (not necessarily an array of $dyn.serverForm.Model
        DataSource: null,

        NavigationEnabled: false,
        // ForcePrimaryNavigation means that the grid should perform its NavigationAction on row selection.
        ForcePrimaryNavigation: false,
        NavigationAction: null,

        ShowHeader: true,
        ShowSelection: true,

        /*SelectedIndex is copied from the FormDataSource in html-binding-serverform.js*/
        SelectedIndex: -1,
        // The cache related index that indicates the starting index of the fetched data (page)
        FetchedDataStartPosition: 0,
        // The current size of the cache
        CacheSize: 0,
        // Indicates whether we have data above starting position of the cache
        DataAbove: false,
        // indicates whether we have data below ending position of the cache
        DataBelow: false,

        // The last selected index is used for marking rows
        LastSelectedIndex: -1,

        Virtualized: false,
        LoadPreviousPage: null,
        LoadNextPage: null,
        ExportAllowed: true,
        ExportLabel: "",
        ExportToExcel: null,
        MarkAllRecords: false,
        // Array valued
        MarkedIndexes: null,
        IsMarkingEnabled: true,
        RowHeightInPixels: null,
        ScrollbarSizeInPixels: null,
        VisibleRowCount: 0,
        VisibleColumnCount: 0,
        Mode: $dyn.ui.ListMode.tabular,
        IsPagingSupported: true,

        // Indicates whether grid is in the infinite scroll mode
        InfiniteScroll: null,

        // Field to group the grid with
        GroupBy: "",
        PreRender: null,
        PostRender: null,

        // function passed to grid by the grid consumer to get the item version for partial rendering scenarios.
        // This function should take in the item object and return the item(/model) version.
        GetDataVersion: null,

        // function passed to grid by the grid consumer to get the item id for partial rendering scenarios.
        // This function should take in the item object and return the item identifier.
        GetDataId: null,
        AsyncRender: true,
        ShowRowLabels: false,
        GridLines: $dyn.ui.GridLines.Auto,
        OriginatingType: "",

        // Private variables
        _selectedRow: undefined,
        _rangePivot: null, // Stores the index of the pivot record around which range is calculated. The index is cache-related.
        _initialDataStartPosition: null, // FetchedDataStartPosition used to compute the row index for ChangeSelectedIndex
        _rangeSelection: false, // Flag indicating that rangeSelection is ON and when that is happening, rangePivot is not reset.
        _markingAction: null, // Stores the marking operation done on the list
        _densityChangeHandler: null, // Stores the density change event handler
        // The cache related index (0-based) of the first rendered row
        clientCacheStart: undefined,

        //The row index that is maintained while keystrokes are getting buffered. The value indicates the currently selected row index on the client (not on the server)
        //This value will increment or decrement depending on whether the up/down arrow is pressed during the keystroke buffering period
        //The index is in the page related coordinate system
        _InternalFocusedRowIndex: undefined,

        // Represents the fraction of viewport size (amount of rows that can be fitted into viewport)
        // that is alowed to render before discarding old rows
        _RowCountThresholdFraction: 8,

        //Keyboard or mouse
        InputMode: null,

        properties: {
            // When true the active row selection visualization is not displayed
            HideSelection: {
                defaultValue: false,

                serialize: true,
                invokeHandlers: false,
            },
        },

        _fastEdit: false,
        _pendingRowsCount: 0,
        _pendingRowProcessing: undefined,
        _pendingExistingRows: undefined,
        _pendingNewRowsToExistingMap: undefined,
        _activeRowValid: undefined,
        _pendingRowsQueue: undefined,
        _lastPendingInvalidIndex: undefined,
        _tabFocusing: false,
        _tabFocused: false,

        _processPendingRowsQueue: function (rows) {
            var self = this,
                row,
                rowIndex,
                pendingChangeTarget,
                collection,
                relativeRowId,
                prevRowPageIndex,
                markMode,
                i,
                len,
                oldSelectedItemId,
                trackingId,
                selectedIndex,
                queueLength;


            queueLength = rows && rows.length;
            $dyn.log.list && $dyn.log.list.trace('List: [fast] Processing queue: length: %o, row processing: %o, active row valid: %s, queue: %o',
                queueLength,
                this._pendingRowProcessing,
                this._activeRowValid(),
                rows.slice(0));

            if (queueLength > 0 && !this._pendingRowProcessing && this._activeRowValid()) {
                row = rows[0];
                this._pendingRowProcessing = row;

                selectedIndex = this.SelectedIndex();

                row.status = PendingRowStates.processing;
                if (row.type === PendingRowTypes.newRow) {
                    $dyn.log.list && $dyn.log.list.info('List: [fast] Processing new row: %o', row);
                    pendingChangeTarget = row.target;
                    collection = $dyn.value(this.DataSource);

                    if (collection) {
                        oldSelectedItemId = this.GetDataId(collection[selectedIndex]);
                        relativeRowId = this._convertDataIdToPageIndex(row.targetDataId);
                    }

                    $dyn.microTask(function () {
                        $dyn.callFunction(this.CreateNewRecord, this, [relativeRowId], function (createdRowId) {
                            var newSelectedIndex = self.SelectedIndex(),
                                newCollection = $dyn.value(self.DataSource),
                                newSelectedIndexId = self.GetDataId(newCollection[newSelectedIndex]);
                            createdRowId = parseInt(createdRowId, 10);
                            if (createdRowId !== -1 && oldSelectedItemId === newSelectedIndexId) {
                                $dyn.logError('List: [fast] New selected item id can not be the same the old, new %s, old: %s, row id: %s',
                                    newSelectedIndexId, oldSelectedItemId, createdRowId);
                                createdRowId = -1;
                            }

                            self._createNewRecordCallback(createdRowId, row);
                        });
                    }, this);
                }
                else {
                    $dyn.log.list && $dyn.log.list.info('List: [fast] Processing existing row: %o', row.rowIndex);
                    $dyn.microTask(function () {
                        $dyn.log.list && $dyn.log.list("List: [fast] Change to index %s", row.rowIndex);
                        var shouldThrottle = (row.inputMode === $dyn.ui.InputType.keyboard);

                        $dyn.callFunction(this.ChangeSelectedIndex, this, [row.rowIndex, row.markMode, row.rangePivotWithinPage, shouldThrottle, function (success) {
                            self._changeSelectedIndexCallback(success, row);
                        }]);
                    }, this);
                }
            }
        },

        _convertDataIdToPageIndex: function (dataId) {
            var collection,
                i,
                len,
                currentDataId,
                relativeRowId;

            collection = $dyn.value(this.DataSource);

            for (i = 0, len = collection.length; i < len; i++) {
                // TODO: wish we didn't have to iterate
                // Find the row index based based on tracking id
                currentDataId = this.GetDataId(collection[i]);
                if (currentDataId === dataId) {
                    relativeRowId = i; // the row position index, todo: coord system?
                    break;
                }
            }

            return relativeRowId;
        },

        _pendingRowsQueueUpdated: function (currentRows) {
            //Process the first row in the queue if the client is not busy and the queue is not empty
            if (currentRows && currentRows.length > 0) {
                this._processPendingRowsQueue(currentRows);
            }
        },

        _createNewRecordCallback: function (createdRowId, row) {
            if (createdRowId !== -1) {
                $dyn.log.list && $dyn.log.list.info("List: [fast] Create new record success: %s", createdRowId);
                this._processPendingRowCallbackSuccess(row);
                if (this._selectedRow.parentNode) {
                    this._makeRowInactive(this._lastPendingInvalidIndex);
                }

                this._lastPendingInvalidIndex = undefined;
            } else {
                $dyn.log.list && $dyn.log.list.info("List: [fast] Create new record fail");
                this._pendingRowProcessing = null;

                var selectedIndex = this.SelectedIndex();
                if (this._lastPendingInvalidIndex !== selectedIndex) {
                    if (this._lastPendingInvalidIndex !== undefined) {
                        this._makeRowInactive(this._lastPendingInvalidIndex);
                    }

                    this._makeRowActive(selectedIndex);
                    this._lastPendingInvalidIndex = selectedIndex;
                }

                this._activeRowValid(false);
            }
        },

        _changeSelectedIndexCallback: function (success, row) {

            if (success) {
                $dyn.log.list && $dyn.log.list.info("List: [fast] Change selected index success");
                if (this._pendingExistingRows) {
                    delete this._pendingExistingRows[row.targetDataId];
                }

                this._processPendingRowCallbackSuccess(row);
                if (this._selectedRow.parentNode) {
                    this._makeRowInactive(this.SelectedIndex());
                }

                this._lastPendingInvalidIndex = undefined;
            }
            else {
                $dyn.log.list && $dyn.log.list.info("List: [fast] Change selected index failed");
                this._pendingRowProcessing = null;
                this._rerenderOptions($dyn.peek(this._rerenderOptions) + 1);

                var selectedIndex = this.SelectedIndex();
                if (this._lastPendingInvalidIndex !== selectedIndex) {
                    if (this._lastPendingInvalidIndex !== undefined) {
                        this._makeRowInactive(this._lastPendingInvalidIndex, true);
                    }

                    this._makeRowActive(selectedIndex);
                    this._lastPendingInvalidIndex = selectedIndex;
                }

                this._activeRowValid(false);
                this._setCurrentPendingRow(null);
            }
        },

        _processPendingRowCallbackSuccess: function (row) {
            this._applyChangesToRow(row);
            row.status = PendingRowStates.processed;

            this._pendingRowProcessing = null;
        },

        _setUserActionBusy: function () {
            if (this._userActionBusyTimer) {
                clearTimeout(this._userActionBusyTimer);
                this._userActionBusyTimer = undefined;
                $dyn.log.list && $dyn.log.list("List: [busy] User still active");
            } else {
                $dyn.log.list && $dyn.log.list("List: [busy] User active");
            }

            this._userActionBusy(true);

            var self = this;
            this._userActionBusyTimer = setTimeout(function () {
                self._userActionBusy(false);
                $dyn.log.list && $dyn.log.list("List: [busy] User not active");
            }, 4000);
        },

        _userActionBusyChanged: function (isBusy) {
            $dyn.log.list && $dyn.log.list('List: [fast] Use busy changed: %o', isBusy);
            var pendingRow = this._activeCell.pendingRow;
            if (!isBusy && pendingRow && $dyn.length(this._pendingRowsQueue) === 0) {
                $dyn.log.list && $dyn.log.list('List: [fast] Applying pending change after not busy: %o', pendingRow);
                if (pendingRow.rowIndex === this.SelectedIndex()) {
                    this._endCellEdit();
                    this._applyPendingChange(pendingRow, this._selectedRow, true);
                    this._activeCell = {
                        rowIndex: this.SelectedIndex(),
                        controlName: this._activeCell.controlName,
                    };

                    pendingRow.status = PendingRowStates.processed;
                }
            }
        },

        _onTabKey: function (evt) {
            var prevActiveCell = this._activeCell;
            this._tabFocusing = true;
            $dyn.log.list && $dyn.log.list('List: [fast] Detecting user focus');

            setTimeout(this._tabCallback.bind(this));
        },

        _tabCallback: function () {
            if (this._tabFocused) {
                $dyn.log.list && $dyn.log.list('List: [fast] Detecting user focus success');
            } else {
                if ($dyn.debug.general) {
                    var listElement = $(document.activeElement).closest('.listRoot')[0];

                    if (listElement !== this.element) {
                        $dyn.log.list && $dyn.log.list('List: [fast] Detecting user focus outside of grid');
                    } else {
                        $dyn.log.list && $dyn.log.list('List: [fast] Detecting user focus failed');
                    }
                }

                this._activeCell = {
                    rowIndex: this._activeCell.rowIndex,
                    pendingRow: this._activeCell.pendingRow,
                    controlName: null,
                };

                this._endCellEdit();
            }

            this._tabFocusing = false;
            this._tabFocused = false;
        },

        /**
        @memberof $dyn.controls.List#
        @summary Gets common context menu options for list
        @function
        @public
        @return {Array} An array of Objects, each representing an option in
        the context menu.
        */
        getCommonGridContextMenuOptions: function () {
            var options = [];
            if (this.ExportToExcel && $dyn.value(this.ExportAllowed)) {
                var exportOption = {
                    Label: $dyn.label("ContextMenu_ExportAllRows"),
                    Command: this.ExecuteExportToExcel.bind(this, false)
                };
                options.push(exportOption);

                if ($dyn.callFunction(this.hasMarkedRows, this)) {
                    exportOption = {
                        Label: $dyn.label("ContextMenu_ExportMarkedRows"),
                        Command: this.ExecuteExportToExcel.bind(this, true)
                    };
                    options.push(exportOption);
                }
            }
            return options;
        },

        /**
        @memberof $dyn.controls.List#
        @summary Gets context menu options for list
        @function
        @public
        @return {Array} An array of Objects, each representing an option in
        the context menu.
        */
        getContextMenuOptions: function () {
            var options = this.getCommonGridContextMenuOptions();
            Array.prototype.push.apply(options, $dyn.ui.Control.prototype.getContextMenuOptions.apply(this));
            return options;
        },

        _focusIn: function (evt) {
            if (this._tabFocusing) {
                var controlToSelect = this._getParentControlName(evt.target);

                this._endCellEdit();

                this._activeCell = {
                    rowIndex: this._activeCell.rowIndex,
                    pendingRow: this._activeCell.pendingRow,
                    controlName: controlToSelect,
                };

                this._beginCellEdit();

                this._tabFocused = true;
            }
        },

        _restoreFocusToPendingRow: function () {
            var name,
                element,
                control;

            if (this.Root) {
                this.Root._focusChanging = true;
                name = this._activeCell.controlName || this.Root.ActiveControl();
                $dyn.log.list && $dyn.log.list("List: [fast] Restoring focus to: %s", name);

                element = this._activeCell.pendingRow && this._activeCell.pendingRow.element;

                control = $('[data-dyn-controlname="' + name + '"]', element);
                if (control[0]) {
                    this.TransferInputFocus(control);
                } else {
                    $dyn.logError('List: no control to focus');
                }
                this.Root._focusChanging = false;
            }
        },

        tryFocus: function (element) {
            $dyn.log.list && $dyn.log.list("List: [fast] Form try focus: %s", this._pendingRowsQueue().length > 0);
            var shouldFocus = true;
            if (this._fastEdit()) {
                var controlToSelect = this._getParentControlName(element);
                if (controlToSelect) {
                    this._activeCell = {
                        rowIndex: this._activeCell.rowIndex,
                        pendingRow: this._activeCell.pendingRow,
                        controlName: controlToSelect
                    };
                }

                if (this._pendingRowsQueue().length > 0) {
                    // Don't steal focus if we have pending rows
                    shouldFocus = false;
                }
            }
            return shouldFocus;
        },

        getFocusCallback: function (focusCallback) {
            return function () {
                focusCallback && focusCallback();
            };
        },

        get _selectedCell() {
            return this.__selectedCell;
        },

        set _selectedCell(value) {
            if (this.__selectedCell !== value) {
                this.__selectedCell = value;
            }
        },

        get _activeCell() {
            if (!this.__activeCell) {
                this.__activeCell = {};
            }
            return this.__activeCell;
        },

        set _activeCell(value) {
            var oldValue = this.__activeCell;
            if (oldValue !== value) {
                $dyn.log.list && $dyn.log.list.trace('List: [fast] Active cell changing: new index: %s, old index: %s, new controlName: %s, old controlName: %s, prev cell: %o, new cell: %o',
                    value.rowIndex,
                    oldValue.rowIndex,
                    value.controlName,
                    oldValue.controlName,
                    oldValue,
                    value);

                this.__activeCell = value;
            }
        },

        _beginCellEdit: function () {
            var pendingRow = this._currentPendingRow,
                $newSelectedCell,
                selectedCellContext,
                selectedCellControl,
                state,
                i,
                len,
                activeCellInitialState = this._activeCell.initialState;

            if (!this._activeCell.controlName) {
                $dyn.logWarning('List: [fast] Begin called without a focused control: %o, row: %o', this._activeCell, pendingRow);
            }

            if (activeCellInitialState) {
                $dyn.logWarning('List: [fast] Begin edit already called on cell: %o, row: %o', this._activeCell, pendingRow);
            }

            if (pendingRow) {
                var name = this._activeCell.controlName;
                $dyn.log.list && $dyn.log.list.trace('List: [fast] Cell edit begin: controlName %o, row: %o', name, pendingRow);

                if (name) {
                    $newSelectedCell = $('[data-dyn-controlname="' + escape(name) + '"]', pendingRow.element);
                    selectedCellContext = this._getVirtualControl($newSelectedCell[0]);
                    selectedCellControl = selectedCellContext && selectedCellContext.current;
                    if (selectedCellControl) {
                        if (!pendingRow.initialState) {
                            pendingRow.initialState = {};
                        }

                        var initialState = pendingRow.initialState[name];
                        $dyn.log.list && $dyn.log.list('List: [fast] Setting active cell initial statue: %o, row: %o', initialState, pendingRow);
                        this._activeCell.initialState = initialState;
                        if (!initialState) {
                            if (activeCellInitialState) {
                                pendingRow.initialState[name] = activeCellInitialState;
                            } else {
                                this._activeCell.initialState = state = selectedCellControl.GetState && selectedCellControl.GetState();
                                if (state) {
                                    $dyn.log.list && $dyn.log.list('List: [fast] Cell edit adding initial state for: %s, row: %o', name, pendingRow);
                                    pendingRow.initialState[name] = state;
                                }
                            }
                        }
                    } else {
                        $dyn.logWarning('List: [fast] Virtual control not found: %s, row: %o', name, pendingRow);
                    }
                }
            }

            $dyn.log.list && $dyn.log.list.trace('List: [fast] Cell edit begin done: cell: %o', this._activeCell);
        },

        _endCellEdit: function (pendingRow) {
            var state,
                i,
                len,
                childControl,
                pendingChangeState,
                hasChanged,
                element,
                selectedElement,
                selectedCellContext,
                selectedCellControl,
                stateItem,
                row = pendingRow,
                stateFound,
                newStateItem,
                initialState,
                activeCellIntialState;

            if (arguments.length === 0) {
                row = this._currentPendingRow;
            }

            activeCellIntialState = this._activeCell.initialState;
            this._activeCell.initialState = null;
            if (row) {
                element = row.element;
                var name = this._activeCell.controlName;

                if (name) {
                    initialState = row.initialState && row.initialState[name];
                    if (activeCellIntialState && !initialState) {
                        // Begin edit called on different row edit for the same row
                        $dyn.log.list && $dyn.log.list.trace('List: [fast] Active cell initial state used: %o, row: %o', activeCellIntialState, row);
                        if (!row.initialState) {
                            row.initialState = {};
                        }

                        initialState = row.initialState[name] = activeCellIntialState;
                    }
                }

                $dyn.log.list && $dyn.log.list.trace('List: [fast] Cell edit end: controlName %o, row: %o', name, row);

                if (element) {
                    selectedElement = $('[data-dyn-controlname="' + escape(name) + '"]', element)[0];
                    selectedCellContext = this._getVirtualControl(selectedElement);
                    selectedCellControl = selectedCellContext && selectedCellContext.current;
                    if (selectedCellControl) {
                        state = selectedCellControl.GetState && selectedCellControl.GetState();
                        var isDirty = false;
                        if (state) {
                            if (!row.state) {
                                row.state = [];
                            }

                            for (i = 0, len = row.state.length; i < len; i++) {
                                stateItem = row.state[i];
                                if (stateItem && stateItem.name === name) {
                                    isDirty = true;

                                    if (this._hasStateChanged(state, stateItem.state)) {
                                        row.state = row.state.slice(0);
                                        newStateItem = { name: name, state: state };
                                        row.state.splice(i, 1, newStateItem);
                                        $dyn.log.list && $dyn.log.list('List: [fast] Cell edit state replace: new %o, old %o, initial: %o', newStateItem, stateItem, initialState);
                                    } else {
                                        $dyn.log.list && $dyn.log.list('List: [fast] Cell edit state not changed: new %o, initial: %o', stateItem, initialState);
                                    }

                                    stateFound = stateItem;
                                    break;
                                }
                            }


                            if (this._hasStateChanged(initialState, state)) {
                                if (!stateFound) {
                                    stateItem = { name: name, state: state };
                                    row.state = row.state.slice(0);
                                    row.state.push(stateItem);
                                }
                                isDirty = true;
                                $dyn.log.list && $dyn.log.list('List: [fast] Cell edit state add: %o, initial: %o', state, initialState);
                            } else {
                                isDirty = false;
                                $dyn.log.list && $dyn.log.list('List: [fast] Cell edit state not changed from initial: %o, initial: %o', state, initialState);
                            }

                        }

                        if (isDirty) {
                            selectedElement.setAttribute('data-dyn-pending', true);
                        } else {
                            selectedElement.removeAttribute('data-dyn-pending');
                        }
                    }
                }
                if (row.state) {
                    Object.freeze(row.state);
                }

                return row.state;
            }
        },

        _hasStateChanged: function (oldState, newState) {
            var keys,
                oldKeys,
                i,
                len,
                key,
                value,
                oldValue;

            if (!oldState || !newState) {
                return true;
            }

            keys = Object.keys(newState);
            oldKeys = Object.keys(oldState);
            if (keys.length !== oldKeys.length) {
                return true;
            }

            for (i = 0, len = keys.length; i < len; i++) {
                key = keys[i];
                value = newState[key];
                oldValue = oldState[key];
                if (value !== oldValue) {
                    return true;
                }
            }

            return false;
        },

        _getPendingInitialState: function (pendingRow) {
            var state,
                i,
                len,
                childControl,
                pendingChangeState,
                rowControl,
                children,
                controlState;

            pendingRow.initialState = {};
            pendingRow.version = 1;

            rowControl = $dyn.context(this._selectedRow);
            children = $dyn.value(rowControl.Children);

            if (children) {
                for (i = 0, len = children.length; i < len; i++) {
                    childControl = children[i];

                    state = childControl.GetState && childControl.GetState();

                    if (state) {
                        if (!pendingRow.initialState[childControl.Name]) {
                            pendingRow.initialState[childControl.Name] = state;
                        }
                    }
                }
            }
        },

        _createNewRow: function (insertElement, isLastRow) {
            var relativeRowId,
                selectedIndex,
                index,
                pendingRow,
                pendingRowTargetElement,
                insertOrder,
                newRowElement,
                referenceNode,
                current,
                pendingNewRows,
                $previousSelectedCell,
                $previousElement;

            if (this._currentPendingRow) {
                this._endCellEdit();
            }

            index = selectedIndex = this._activeCell.rowIndex;

            var renderedRow = this.renderedRows[index];

            pendingRow = this._createPendingRow({
                type: PendingRowTypes.newRow,
                relativeRowId: index,
                targetDataId: this._getDataIdByPageIndex(index),
                target: renderedRow,
            });

            this._pendingRowIdToPendingRow[pendingRow.id] = pendingRow;

            pendingRowTargetElement = pendingRow.target && pendingRow.target.element;
            if ((!pendingRowTargetElement || !pendingRowTargetElement.parentNode) && this._selectedRow.parentNode) {
                pendingRowTargetElement = this._selectedRow;
            }

            insertOrder = 0;

            // Current row is a new row
            // Start from the cell editor and find the closest exsting (by using nextSibling -  for insert above)
            current = insertElement;
            while (current) {
                // Deterimine if this is an existing row client cache index (existing row), aria-posinset is 1 based
                index = parseInt(current.getAttribute('aria-posinset'), 10);
                if (!isNaN(index)) {
                    index = index - 1;
                    var renderedRow = this.renderedRows[index];

                    // valid index - this is an existing row.
                    // get the tracking id (target from rendered rows)
                    // targetDataId is the master tracking id
                    pendingRow.targetDataId = this._getDataIdByPageIndex(index);
                    pendingRow.target = renderedRow;

                    break;
                } else {
                    // This is new row the between the existing row and the row we are doing for the alt-n
                    insertOrder++;
                }

                if ($dyn.debug.gridAppend) {
                    current = current.previousSibling;
                }
                else {
                    current = current.nextSibling;
                }
            }

            newRowElement = this._renderPendingRow(pendingRow);

            pendingRow.element = newRowElement;
            pendingRow.element.setAttribute('data-dyn-pendingid', pendingRow.id);

            referenceNode = $dyn.debug.gridAppend ? insertElement.nextSibling : insertElement;

            insertElement.parentNode.insertBefore(newRowElement, referenceNode);

            // Based on the tracking id, this is the "rendering order" to the element (for new rows) that represents that id, where index 0 is the closest to the element
            pendingNewRows = this._pendingNewRowsToExistingMap[pendingRow.targetDataId];
            if (pendingNewRows) {
                // insertOrder is the rows in between the existing row and the row that we are adding (the alt-n row)
                pendingNewRows.splice(insertOrder, 0, pendingRow);
            } else {
                // There are no other new rows for this tracking id, add row we are doing for th alt-n assoicated to this tracking id
                this._pendingNewRowsToExistingMap[pendingRow.targetDataId] = pendingNewRows = [pendingRow];
            }

            $dyn.log.list && $dyn.log.list('List: [fast] Create row: %o, rowIndex: %s, insert order: %s, related new rows: %o, active cell: %o',
                pendingRow, index, insertOrder, pendingNewRows, this._activeCell);

            if (!isLastRow) {
                var firstInput = $('input', newRowElement)[0];
                var controlToSelect = this._getParentControlName(firstInput);
            }

            this._activeCell = {
                controlName: controlToSelect || this._activeCell.controlName,
                pendingRow: pendingRow,
            };

            this.TransferInputFocus($(newRowElement));

            $previousElement = $('[data-dyn-row-selecting]', this._listBodyElement);
            $previousElement.removeAttr('data-dyn-row-selecting');
            newRowElement.setAttribute('data-dyn-row-selecting', true);

            this._queuePendingRow(pendingRow);
        },

        _changePendingSelectedIndex: function (rowIndex, markMode, inputMode, event) {
            var renderedRow = this.renderedRows[rowIndex],
                pendingRow;

            if (renderedRow) {
                this._pendingExistingRows = this._pendingExistingRows || {};

                pendingRow = this._pendingExistingRows[renderedRow.id];

                if (!this._pendingExistingRows[renderedRow.id]) {

                    pendingRow = this._createPendingRow({
                        rowIndex: rowIndex,
                        type: PendingRowTypes.existingRow,
                        markMode: markMode,
                        inputMode: inputMode,
                        rangePivotWithinPage: this.rangePivotWithinPage(),
                        targetDataId: renderedRow.id,
                        target: renderedRow,
                        element: renderedRow.element
                    });

                    pendingRow.element.setAttribute('data-dyn-pendingid', pendingRow.id);

                    this._pendingExistingRows[pendingRow.targetDataId] = pendingRow;

                    this._queuePendingRow(pendingRow);
                } else {
                    if (!this._getPendingRowFromPendingQueue(pendingRow.id)) {
                        this._queuePendingRow(pendingRow);
                    }
                }

                this._setCurrentPendingRow(pendingRow);
            }
        },

        _createPendingRow: function (rowOptions) {
            if (!this._nextNewRowId) {
                this._nextNewRowId = 1;
            } else {
                this._nextNewRowId++;
            }

            rowOptions.id = this._nextNewRowId;
            rowOptions.status = PendingRowStates.notQueued;

            this._pendingRowIdToPendingRow[this._nextNewRowId] = rowOptions;
            return rowOptions;
        },

        _queuePendingRow: function (pendingRow) {
            $dyn.log.list && $dyn.log.list('List: [fast] Adding pending row: %o', pendingRow);
            pendingRow.status = PendingRowStates.pending;
            this._setCurrentPendingRow(pendingRow);

            if (this._pendingRowsQueue().length === 0 && this._selectedRow.parentNode) {
                this._makeRowInactive(this.SelectedIndex(), true);
            }
            this._pendingRowsQueue.push(pendingRow);
        },

        _setControlState: function (controlState, initialState, rowElement, pendingChange, checkInitialState) {
            var state = controlState.state,
                i,
                len,
                childControl,
                childElement,
                childName,
                name = controlState.name,
                children,
                stateChanges,
                propertyName,
                propertyValue,
                initialValue,
                stateKeys,
                stateKeyIndex,
                stateKeyLength;

            children = rowElement.children;
            if (children) {
                for (i = 0, len = children.length; i < len; i++) {
                    childElement = children[i];
                    childControl = $dyn.context(childElement);
                    if (!childControl) {
                        childControl = this._getVirtualControl(childElement);
                        childControl = childControl && childControl.current;

                    }

                    if (childControl) {
                        if (childControl.Name === name) {

                            stateChanges = {};

                            if (state) {
                                stateKeys = Object.keys(state);
                                for (stateKeyIndex = 0, stateKeyLength = stateKeys.length; stateKeyIndex < stateKeyLength; stateKeyIndex++) {
                                    propertyName = stateKeys[stateKeyIndex];
                                    propertyValue = state[propertyName];
                                    initialValue = initialState && initialState[propertyName];
                                    if (initialValue !== propertyValue) {
                                        stateChanges[propertyName] = propertyValue;
                                    }
                                }
                            }

                            if (Object.keys(stateChanges).length > 0) {
                                $dyn.log.list && $dyn.log.list('List: [fast] Setting control state: %s, changes: %o, state: %o, initial state: %o, row: %o',
                                    name, stateChanges, state, initialState, pendingChange);

                                childControl.ApplyState && childControl.ApplyState(stateChanges);
                            } else {
                                $dyn.log.list && $dyn.log.list('List: [fast] No changes for control: %s, state: %o, initial state: %o, row: %o', name, state, initialState, pendingChange);
                            }
                            return;
                        }
                    }
                }
            }
        },

        _getCurrentPendingRow: function () {
            if (!this.__currentPendingRow) {
                this.__currentPendingRow = $dyn.observable();
            }

            return this.__currentPendingRow;
        },

        get _currentPendingRow() {
            return $dyn.value(this._getCurrentPendingRow());
        },

        set _currentPendingRow(value) {
            var currentPendingRow = this._getCurrentPendingRow();
            currentPendingRow(value);
        },

        _setCurrentPendingRow: function (pendingRow, endCellEdit) {
            var hasChanges,
                index;
            var previousPendingRow = this._currentPendingRow;
            if (pendingRow !== previousPendingRow) {
                if (previousPendingRow) {
                    if (endCellEdit !== false) {
                        this._endCellEdit();
                    }

                    if (previousPendingRow.status === PendingRowStates.notQueued && this.SelectedIndex() === previousPendingRow.rowIndex) {
                        $dyn.log.list && $dyn.log.list('List: [fast] Applying unqueued pending row: %o', previousPendingRow);
                        this._applyPendingChange(previousPendingRow, this._selectedRow, true);
                    }

                    if (previousPendingRow.status === PendingRowStates.pending && (!previousPendingRow.state || previousPendingRow.state.length === 0) && previousPendingRow.type === PendingRowTypes.existingRow && this._pendingRowProcessing !== previousPendingRow) {
                        $dyn.log.list && $dyn.log.list('List: [fast] Removing unchanged pending row: %o', previousPendingRow);
                        delete this._pendingExistingRows[previousPendingRow.targetDataId];

                        previousPendingRow.element.removeAttribute('data-dyn-pendingid');

                        index = this._pendingRowsQueue().indexOf(previousPendingRow);
                        if (index !== -1) {
                            this._pendingRowsQueue().splice(index, 1);
                        }
                    }
                }

                $dyn.log.list && $dyn.log.list('List: [fast] Set pending row: %o, prev: %o', pendingRow, previousPendingRow);
                this._currentPendingRow = pendingRow;
                this._beginCellEdit();
            }
        },

        _applyInitialState: function (pendingChange, rowElement) {
            var state,
                i,
                len,
                initialState;

            if (pendingChange.initialState) {
                var keys = Object.keys(pendingChange.initialState);
                for (i = 0, len = keys.length; i < len; i++) {
                    var controlName = keys[i];
                    state = pendingChange.initialState[controlName];

                    if (state) {
                        this._setControlState(
                            { name: controlName, state: state },
                            undefined,
                            rowElement,
                            pendingChange,
                            false);
                    }
                }
            }
        },

        _applyPendingChange: function (pendingChange, rowElement, checkInitialState) {
            var state,
                i,
                len,
                childControl,
                pendingChangeState,
                initialState,
                initialStateIndex,
                initialStateLength,
                initialStateItem;

            if (pendingChange.state) {
                len = pendingChange.state.length;
                for (i = 0; i < len; i++) {
                    pendingChangeState = pendingChange.state[i];

                    if (pendingChangeState) {
                        this._setControlState(
                            pendingChangeState,
                            checkInitialState && pendingChange.initialState[pendingChangeState.name],
                            rowElement,
                            pendingChange,
                            checkInitialState);
                    }
                }
            }
        },

        _applyChangesToRow: function (pendingRow) {
            // Apply changes MUST be called on the active row
            var state,
                i,
                len,
                childControl,
                pendingChangeState,
                currentPendingRow = this._currentPendingRow,
                element,
                collection,
                model,
                newId,
                previousTrackingId,
                newRowIndex,
                newRowsLength,
                newRowsForTrackingId,
                newRow,
                newRows,
                item;

            element = pendingRow.element;

            // The newRow complete successfully which means that it is now that row and we will know that tracking id for it now
            collection = $dyn.value(this.DataSource);

            var index = this.SelectedIndex();
            model = collection[index];

            // GetDataId returns the tracking id
            newId = this.GetDataId(model);

            previousTrackingId = pendingRow.targetDataId;

            element.removeAttribute('data-dyn-pendingid');
            $('[data-dyn-pending]', element).removeAttr('data-dyn-pending');

            if (pendingRow.type === PendingRowTypes.newRow && previousTrackingId !== newId) {
                this._createdPendingRows[newId] = element;

                var clientCacheIndex = this.ConvertPageIndexToClientCacheIndex(index);
                element.setAttribute('aria-posinset', this.SelectedIndex() + 1);

                this.renderedRows[clientCacheIndex] = {
                    element: element,
                    version: this.GetDataVersion ? this.GetDataVersion(model) : undefined,
                    selected: false,
                    marked: !!this.MarkedIndexes()[index],
                    id: newId
                };

                // Get the new rows associated to the tracking id associated to this newRow at time the newRow was sent to server
                newRowsForTrackingId = this._pendingNewRowsToExistingMap && this._pendingNewRowsToExistingMap[previousTrackingId]; // to this is incorrect

                if (newRowsForTrackingId) {
                    for (i = 0, len = newRowsForTrackingId.length; i < len; i++) {
                        newRow = newRowsForTrackingId[i];
                        if (newRow.id === pendingRow.id) {
                            // Remove the complete change from the tracking id associated to this newRow at time the newRow was sent to server
                            if (i === 0) {
                                // There are no new rows between the recently "created" row and the previous existing row
                                // Remove the previous tracking id from the
                                delete this._pendingNewRowsToExistingMap[previousTrackingId];
                            } else {
                                // There are still rows are in between the newly created row and the previous extsing row
                                this._pendingNewRowsToExistingMap[previousTrackingId] = newRowsForTrackingId.slice(0, i);
                            }

                            // For the new rows above this change (for insert above), associate them to the to new created row (as that is no existing)
                            // (i.e. this is now the closed existing rows to the new rows above it (adjacent only, non-adjacent would associated to a different tracking id)
                            this._pendingNewRowsToExistingMap[newId] = newRowsForTrackingId.slice(i + 1);
                            if (this._pendingNewRowsToExistingMap[newId].length === 0) {
                                // There are no rows above the newly created row
                                delete this._pendingNewRowsToExistingMap[newId];
                            } else {
                                // There are adjacent new rows above the newly created row that need to associated to the newly created row
                                newRows = this._pendingNewRowsToExistingMap[newId];
                                for (newRowIndex = 0, newRowsLength = newRows.length; newRowIndex < newRowsLength; newRowIndex++) {
                                    item = newRows[newRowIndex];
                                    // Update the tracking id to point to the newly created row
                                    item.targetDataId = newId;
                                    item.target = {
                                        id: newId,
                                        index: this.SelectedIndex(),
                                        element: element,
                                    };
                                }
                            }
                            break;
                        }
                    }
                }
            }

            $dyn.log.list && $dyn.log.list('List: [fast] Scheduling task for applying pending row: %o', pendingRow);
            $dyn.microTask(function () {
                $dyn.log.list && $dyn.log.list('List: [fast] Applying pending row: %o, state: %o, initialState', pendingRow, pendingRow.state, pendingRow.initialState);
                if (!this._userActionBusy()) {
                    this._endCellEdit();
                }
                // Applies the change to the "live" elements (not in the UI currently), which will raise their changes (for example, SetValue for Input)
                // by calling ApplyState on the live control.
                this._applyPendingChange(pendingRow, this._selectedRow, true);

                // Remove record from queue (which can trigger a re-render of the grid)
                this._pendingRowsQueue.splice(0, 1);

                // TODO: Probably should be checking if this is the current edit.
                if (this._pendingRowsQueue().length === 0) {
                    $dyn.log.list && $dyn.log.list('List: [fast] Pending rows queue is empty');

                    if (this._userActionBusy()) {
                        var currentPending = this._activeCell.pendingRow;

                        var newEdit = this._createPendingRow({
                            rowIndex: index,
                            type: PendingRowTypes.existingRow, // TODO: Should keep the type?
                            status: PendingRowStates.notQueued,
                            targetDataId: currentPending.targetDataId,
                            target: currentPending.target,
                            element: currentPending.element
                        });

                        this._activeCell = {
                            rowIndex: this.SelectedIndex(),
                            controlName: this._activeCell.controlName,
                            pendingRow: newEdit,
                            initialState: this._activeCell.initialState
                        };

                        this._setCurrentPendingRow(newEdit, false);
                    }
                }
            }, this);

            this.Root.ActiveControl(this._selectedCell);
        },

        _renderPendingNewRows: function () {
            var renderedRow,
                newRowId,
                existingRowId,
                i,
                len,
                renderedRowIndex,
                renderedRowLength,
                changeIndex,
                pendingChange,
                createdNewRowElement,
                pendingChanges,
                fragment,
                element,
                pendingRowTargetElement,
                referenceNode,
                keys;

            $dyn.log.list && $dyn.log.list('List: [fast] Render: %o', this._pendingNewRowsToExistingMap);

            keys = Object.keys(this._pendingNewRowsToExistingMap);
            for (i = 0, len = keys.length; i < len; i++) {
                existingRowId = keys[i];
                pendingChanges = this._pendingNewRowsToExistingMap[existingRowId];
                for (renderedRowIndex = 0, renderedRowLength = this.renderedRows.length; renderedRowIndex < renderedRowLength; renderedRowIndex++) {
                    //  TODO: wish we didn't have to iterate
                    if (this.renderedRows[renderedRowIndex].id === existingRowId) {
                        renderedRow = this.renderedRows[renderedRowIndex];
                        break;
                    }
                }

                if (renderedRow) {
                    fragment = document.createDocumentFragment();

                    if ($dyn.debug.gridAppend) {
                        for (changeIndex = 0; changeIndex < pendingChanges.length; changeIndex++) {
                            pendingChange = pendingChanges[changeIndex];
                            element = this._renderPendingRow(pendingChange);
                            fragment.appendChild(element);
                        }
                    }
                    else {
                        for (changeIndex = pendingChanges.length - 1; changeIndex >= 0; changeIndex--) {
                            pendingChange = pendingChanges[changeIndex];
                            element = this._renderPendingRow(pendingChange);
                            fragment.appendChild(element);
                        }
                    }

                    pendingRowTargetElement;
                    if (renderedRow.selected) {
                        pendingRowTargetElement = this._selectedRow;
                    } else {
                        pendingRowTargetElement = renderedRow.element;
                    }

                    referenceNode = $dyn.debug.gridAppend ? pendingRowTargetElement.nextSibling : pendingRowTargetElement;

                    pendingRowTargetElement.parentNode.insertBefore(fragment, referenceNode);
                } else {
                    $dyn.logError('List: [fast] Could not find render row %s', existingRowId);
                }
            }
        },

        _renderedCurrentPendingRow: undefined,
        _renderPendingRow: function (pendingRow, model, rowData, rowsToReuse) {
            var rowElement = pendingRow.element;

            if (!rowElement) {
                this._inactiveItem(model || {});
                this._applyPendingChange(pendingRow, this._inactiveItemElement);
                rowElement = this._inactiveItemElement.cloneNode(true);
                pendingRow.element = rowElement;

                if (pendingRow.type === PendingRowTypes.newRow) {
                    rowElement.removeAttribute('aria-posinset');
                }

                rowElement.setAttribute('data-dyn-pendingid', pendingRow.id);
            }

            return rowElement;
        },

        _getPendingRowByPendingId: function (pendingRowId) {
            return this._pendingRowIdToPendingRow[pendingRowId];
        },

        _getPendingRowFromPendingQueue: function (pendingRowId) {
            var pendingRows = this._pendingRowsQueue(),
                i,
                len,
                pendingRow;

            for (i = 0, len = pendingRows.length; i < len; i++) {
                pendingRow = pendingRows[i];
                if (pendingRow && pendingRow.id === pendingRowId) {
                    return pendingRow;
                }
            }
        },

        _fastEditFocusRowAndSelectCell: function (relativePosition, totalNumberOfRows, markMode, inputMode, event) {
            var self = this,
                isNavigationToPendingNewRow = false,
                createNewRow = false,
                rowIndexToNavigateTo,
                currentRowIndex,
                targetRowElement,
                pendingRow,
                previousPendingRow,
                previousPendingRowTargetRowIndex,
                rowElement,
                previousRow,
                $previousElement,
                controlToSelect,
                $previousSelectedCell,
                previousCell,
                activeCell;

            // Find current row element
            rowElement = $(event.target).closest('.rowTemplate')[0];

            $dyn.log.list && $dyn.log.list.info("List: [fast] Row focus, relative: %s, mouse: %s, event: %o",
                relativePosition, inputMode === $dyn.ui.InputType.mouse, event);

            previousRow = this._currentPendingRow;
            if (this._currentPendingRow) {
                this._endCellEdit();
            }

            if (rowElement) {
                currentRowIndex = this.rowIndex(rowElement);
                if (inputMode === $dyn.ui.InputType.mouse) {
                    targetRowElement = rowElement;
                    if (isNaN(currentRowIndex)) {
                        isNavigationToPendingNewRow = true;
                        pendingRow = this._getPendingRowByPendingId(parseInt(targetRowElement.getAttribute('data-dyn-pendingid'), 10));
                        if (pendingRow !== this._currentPendingRow) {
                            this._setCurrentPendingRow(pendingRow);
                        }
                    }
                    else {
                        rowIndexToNavigateTo = currentRowIndex;
                    }
                }
                else {
                    // Keyboard navigation
                    if (relativePosition === 1) {
                        targetRowElement = rowElement.nextElementSibling;
                    }
                    else if (relativePosition === -1) {
                        targetRowElement = rowElement.previousElementSibling;
                    }
                    if (targetRowElement) {
                        pendingRow = this._getPendingRowByPendingId(parseInt(targetRowElement.getAttribute('data-dyn-pendingid'), 10));
                        if (pendingRow) {
                            isNavigationToPendingNewRow = true;
                            if (pendingRow !== this._currentPendingRow) {
                                this._setCurrentPendingRow(pendingRow);
                            }
                        }
                        else {
                            //Check if we are navigating to an existing row
                            rowIndexToNavigateTo = parseInt(targetRowElement.getAttribute('aria-posinset'), 10) - 1;//aria-posinset is 1-based
                            if (this._currentPendingRow) {
                                this._endCellEdit();
                            }
                        }
                    }
                    else {
                        // We are trying to navigate to a non-existent row. Is this the last row in the db cache and there are no more rows ?
                        // If so, create a new row if the datasource allowCreate and insertAtEnd flags are true
                        // TODO: Currently, we don't have these ds properties coming down from the server
                        // To validate some key scenarios the prototype will assume that these properties are true.
                        if (!isNaN(currentRowIndex) && this._isLastRow(currentRowIndex)) {
                            createNewRow = true;
                        }
                        else {
                            previousPendingRow = this._getPendingRowByPendingId(parseInt(rowElement.getAttribute('data-dyn-pendingid'), 10));
                            if (previousPendingRow) {
                                previousPendingRowTargetRowIndex = this._convertDataIdToPageIndex(previousPendingRow.targetDataId);
                                if (!isNaN(previousPendingRowTargetRowIndex) && this._isLastRow(previousPendingRowTargetRowIndex)) {
                                    createNewRow = true;
                                }
                            }
                        }
                        if (createNewRow) {
                            this._createNewRow(rowElement, true);
                        }
                    }
                }
            }

            $previousElement = $('[data-dyn-row-selecting]', this._listBodyElement);
            $previousElement.removeAttr('data-dyn-row-selecting');

            if (isNavigationToPendingNewRow) {
                if (this._activeRowValid()) {
                    this._processPendingRowsQueue(this._pendingRowsQueue());
                }

            }
            else {
                self.FocusRow(rowIndexToNavigateTo, markMode, inputMode, event);
            }

            if (targetRowElement) {
                pendingRow = this.__currentPendingRow;
                if (targetRowElement !== this._selectedRow) {
                    targetRowElement.setAttribute('data-dyn-row-selecting', true);
                }
                $previousSelectedCell = $('[data-dyn-controlname="' + escape(self._selectedCell) + '"]', $previousElement);

                controlToSelect = this._getParentControlName(event.target);
                this._selectedCell = controlToSelect;
                previousCell = this._activeCell;
                activeCell = this._activeCell = {
                    controlName: controlToSelect,
                    pendingRow: this._currentPendingRow,
                    rowIndex: rowIndexToNavigateTo,
                };

                if (pendingRow) {
                    this._beginCellEdit();
                }

                if (previousCell.rowIndex !== activeCell.rowIndex || activeCell.pendingRow !== previousCell.pendingRow) {
                    self.TransferInputFocus($(targetRowElement));
                } else {
                    $dyn.log.list && $dyn.log.list.info("List: [fast] Row not changed, new cell: %o, prev cell: %o", activeCell, previousCell);
                }
            }
        },

        _fastEditUpdateRow: function (relativePosition, totalNumberOfRows, markMode, $activeElement, inputMode, event) {
            var self = this;
            self._fastEditFocusRowAndSelectCell(relativePosition, totalNumberOfRows, markMode, inputMode, event);
            if (event && event.preventDefault) {
                event.preventDefault();
            }
        },

        _fastEditSelectAndMarkRow: function (previouslyFocusedRowIndex, updatedFocusedRowIndex, markMode, activeElement) {
            var self = this,
                targetRow,
                markRow;

            targetRow = $(self.FindRowAtIndex(updatedFocusedRowIndex));

            // Change row selection to the new index
            self.UpdateRowSelection(previouslyFocusedRowIndex, updatedFocusedRowIndex);

            if (previouslyFocusedRowIndex !== updatedFocusedRowIndex) {
                if (markMode === $dyn.controls.HierarchicalGridBase.MarkMode.rangeSelect) {
                    // Mark all of the records in the set bounded by rangePivot and updatedFocusedRowIndex.
                    self.MarkRange(self.rangePivotWithinPage(), updatedFocusedRowIndex);
                }

                if (markMode === $dyn.controls.HierarchicalGridBase.MarkMode.select) {
                    // Clear previous marking but don't mark the new index
                    // If there are no pending keydown events when the response to the row change interaction arrives then ApplyChanges will do the marking
                    // Note: This could result in a slight delay from the time the row is selected till the time it is marked
                    // If we try to mark the new index then quick keystrokes will result in a trail of marked rows that eventually get cleared
                    // as the rendering is slower than the rate of keydowns
                    markRow = $dyn.value(self.AllowEdit) ? updatedFocusedRowIndex : -1;
                    self.ClearMarkedState(markRow);
                }
            }
        },

        _selectedIndexChanged: function () {
            var listCollection,
                index,
                markingEnabled,
                supportsDataIdAndVersion,
                lastSelectedPageIndex,
                clientCacheIndex,
                $activeElement;

            $dyn.log.list && $dyn.log.list("List: _PendingRowChangeCount " + $dyn.value(this._PendingRowChangeCount));
            // Do not react to any changes to the selected Index if there are pending data load or key down events.
            // Pending events like the ones above mean that either an interation is pending or will be processing in the near future
            // which will re-apply this logic again. Reacting to these changes while such events are pending would result in
            // skipping (up/down) of the focus to other rows which is undesirable
            if ($dyn.value(this._PendingRowChangeCount) > 0) {
                $dyn.log.list && $dyn.log.list("List: SelectedIndexChanged: Not updating selected index");
                return;
            }

            listCollection = $dyn.value(this.DataSource);

            index = this.SelectedIndex();
            this._activeCell = {
                rowIndex: index,
                controlName: this._activeCell.controlName
            };

            markingEnabled = this._supportsMarking();
            supportsDataIdAndVersion = this.GetDataId && (typeof (this.GetDataId) === "function") && this.GetDataVersion && (typeof (this.GetDataVersion) === "function");
            clientCacheIndex = this.ConvertServerCacheIndexToClientCacheIndex(this.LastSelectedIndex);
            lastSelectedPageIndex = this.ConvertServerCacheIndexToPageIndex(this.LastSelectedIndex);
            if (lastSelectedPageIndex !== index) {
                $dyn.log.list && $dyn.log.list("List: selected index changed - old %s, new %s", lastSelectedPageIndex, index);

                $activeElement = this._makeRowInactive(this.LastSelectedIndex);
                this._makeRowActive(index, $activeElement);

                // Call the public post selected index changed callback, if any
                $dyn.callFunction(this.PostSelectedIndexChanged, this, [index]);
            }

            this.LastSelectedIndex = this._InternalFocusedRowIndex = this.ConvertPageIndexToServerCacheIndex(index);

        },

        _makeRowInactive: function (lastSelectedIndex, checkState) {
            var $activeElement,
                inactiveItemElement,
                inactiveItemDataId,
                inactiveItemDataVersion,
                lastSelectedItem,
                renderedRow,
                existingRow,
                listCollection = $dyn.value(this.DataSource),
                markingEnabled = this._supportsMarking(),
                clientCacheIndex = this.ConvertServerCacheIndexToClientCacheIndex(lastSelectedIndex),
                lastSelectedPageIndex = this.ConvertServerCacheIndexToPageIndex(lastSelectedIndex),
                supportsDataIdAndVersion = this.GetDataId && (typeof (this.GetDataId) === "function") && this.GetDataVersion && (typeof (this.GetDataVersion) === "function");

            if (lastSelectedPageIndex >= 0 && lastSelectedPageIndex < listCollection.length) {
                // Create a new row for previously selected row which is now an inactive row
                lastSelectedItem = listCollection[lastSelectedPageIndex];
                this._inactiveItem(lastSelectedItem);
                inactiveItemElement = this._inactiveItemElement.cloneNode(true);

                if (supportsDataIdAndVersion) {
                    inactiveItemDataId = this.GetDataId(lastSelectedItem);
                    inactiveItemDataVersion = this.GetDataVersion(lastSelectedItem);

                    if (this._fastEdit() && checkState) {
                        renderedRow = this.renderedRows[lastSelectedPageIndex];
                        renderedRow.element = inactiveItemElement;

                        existingRow = this._createPendingRow({
                            rowIndex: lastSelectedPageIndex,
                            type: PendingRowTypes.existingRow,
                            targetDataId: renderedRow.id,
                            target: renderedRow,
                            element: this._selectedRow
                        });

                        $dyn.log.list && $dyn.log.list("List: [fast] Switch active row to inactive, index: %s", lastSelectedPageIndex);
                        this._getPendingInitialState(existingRow);
                        existingRow.element = inactiveItemElement;

                        this._pendingExistingRows[inactiveItemDataId] = existingRow;

                        this._applyInitialState(existingRow, renderedRow.element);
                    }
                }
            } else {
                // Previously selected item can be outside active page.
                // Let's search for it in rendered rows array.
                if (this.renderedRows[clientCacheIndex] && this.renderedRows[clientCacheIndex].selected) {
                    inactiveItemElement = this.renderedRows[clientCacheIndex].element;
                    inactiveItemDataId = this.renderedRows[clientCacheIndex].id;
                    inactiveItemDataVersion = this.renderedRows[clientCacheIndex].version;
                }
            }
            if (inactiveItemElement) {
                inactiveItemElement.setAttribute("aria-posinset", clientCacheIndex + 1);
                if (markingEnabled) {
                    this._setRowMarking(inactiveItemElement, !!this.MarkedIndexes()[lastSelectedPageIndex]);
                }

                if (document.activeElement) {
                    $activeElement = $(this._selectedRow).find(document.activeElement);
                }

                if (this._selectedRow.parentNode) {
                    this._selectedRow.parentNode.replaceChild(inactiveItemElement, this._selectedRow);
                    // Let's maintain objects that are used by rerendering
                    this.renderedRows[clientCacheIndex] = {
                        element: inactiveItemElement,
                        version: inactiveItemDataVersion,
                        selected: false,
                        marked: !!this.MarkedIndexes()[lastSelectedPageIndex],
                        id: inactiveItemDataId
                    };
                }
            }

            return $activeElement;
        },

        _makeRowActive: function (index, $activeElement) {
            var markingEnabled = this._supportsMarking(),
                newSelectedItem,
                currentElementAtSelectedIndex,
                clientCacheIndex,
                listCollection = $dyn.value(this.DataSource);

            $dyn.log.list && $dyn.log.list("List: [fast] Switch active row to active, index: %s", index);

            if (index >= 0 && index < listCollection.length) {
                // Updated the new selected row
                newSelectedItem = listCollection[index];
                this._selectedItem();

                clientCacheIndex = this.ConvertPageIndexToClientCacheIndex(index);
                this._selectedRow.setAttribute("aria-posinset", clientCacheIndex + 1);
                if (this.renderedRows[clientCacheIndex]) {
                    currentElementAtSelectedIndex = this.renderedRows[clientCacheIndex].element;
                }

                if (currentElementAtSelectedIndex && currentElementAtSelectedIndex.parentNode) {

                    currentElementAtSelectedIndex.parentNode.replaceChild(this._selectedRow, currentElementAtSelectedIndex);

                    // Let's maintain objects that are used by rerendering
                    this.renderedRows[clientCacheIndex] = {
                        element: currentElementAtSelectedIndex,
                        version: this.GetDataVersion ? this.GetDataVersion(newSelectedItem) : undefined,
                        selected: true,
                        marked: !!this.MarkedIndexes()[index],
                        id: this.GetDataId ? this.GetDataId(newSelectedItem) : undefined
                    };

                    if ($activeElement) {
                        this.TransferInputFocus($(this._selectedRow), $activeElement);
                    }
                }

                // Set the attributes that tell the browser and the CSS rendering that the row is selected. IE exibits a
                // problem applying the correct CSS if this is done before the DOM is updated.
                this._selectedRow.setAttribute("data-dyn-row-active", true);
                if (markingEnabled) {
                    this._setRowMarking(this._selectedRow, !!this.MarkedIndexes()[index]);
                }

                // The new selected item could be out of view, scroll it into view if necessary
                if (!$dyn.value(this._dataLoadedRenderPending) && this._selectedRow.parentNode) {
                    // Selected item is in the view. No need to maintain focus triggers for it
                    if (this._scrollParameters) {
                        this._scrollParameters.selectionFocusTriggerMax = undefined;
                        this._scrollParameters.selectionFocusTriggerMin = undefined;
                    }
                    this.scrollItemIntoView(this._selectedRow, this._$scrollContainer);
                }
            }
        },

        _getDataIdByPageIndex: function (pageIndex) {
            var collection = $dyn.value(this.DataSource());
            if (collection) {
                var dataItem = collection[pageIndex];
                if (dataItem) {
                    return this.GetDataId(dataItem);
                }
            }
        },

        _getRowElementFromEvent: function (event) {
            var rowElement = $(event.target).closest('.listItem')[0];
            return rowElement;
        },

        _getVirtualControl: function (virtualizedElement) {
            var controlElement = $(virtualizedElement).closest('[data-dyn-controlname]').addBack()[0];
            if (controlElement) {
                var selector = '[data-dyn-controlname="' + escape(controlElement.getAttribute('data-dyn-controlname')) + '"]';
                var liveElement = $(this._inactiveItemElement).find(selector)[0];

                if (liveElement) {
                    var listItem = $(virtualizedElement).closest('.listItem')[0];
                    var index = this.rowIndex(listItem);

                    var items = $dyn.value(this.DataSource);
                    if (items) {
                        var item = items[index];
                        this._inactiveItem(item);
                        var control = Object.create(liveElement.dynContext.current);
                        control.index = index;
                        control.serverIndex = this.ConvertPageIndexToServerCacheIndex(index) + 1; // dbcacherownumber is 1 based
                        control.dynContext = new $dyn.DataContext(control, liveElement.dynContext);
                        control.element = controlElement;
                        control.__elementCache = {};
                        return control.dynContext;
                    }
                }
            }
        },

        ToggleMarkAllRecordsMode: function () {
            /// <summary>
            /// Toggles the mark all records mode.
            /// </summary>

            var self = this;
            if (self._supportsMarking()) {
                $dyn.callFunction(self.SetMarkAllRecordsMode, self, !$dyn.value(self.MarkAllRecords));
                var allRows = self._getAllRowElements();
                for (var i = 0, len = allRows.length ; i < len; i++) {
                    var rowElement = allRows[i];
                    self._setRowMarking(rowElement, !$dyn.value(self.MarkAllRecords));
                }
            }
        },

        FindRowAtIndex: function (pageIndex) {
            var self = this;
            var position = parseInt(pageIndex);
            var clientCacheIndex = self.ConvertPageIndexToClientCacheIndex(position);
            return self._$listElement.find('.listItem[aria-posinset=' + (clientCacheIndex + 1) + ']')[0];
        },

        _getAllRowElements: function () {
            return this._$listElement.find('.listBody .listItem');
        },

        _isLastRow: function (pageIndex) {
            var serverCacheIndex = this.ConvertPageIndexToServerCacheIndex(pageIndex);
            var cacheSize = $dyn.value(this.CacheSize);
            return serverCacheIndex === (cacheSize - 1) && $dyn.value(this.DataBelow) === false;
        },

        UpdateClientCacheStart: function (value) {
            var clientCacheStartShift = 0;
            if (this.clientCacheStart !== undefined && this.clientCacheStart !== null && value !== undefined && value !== null) {
                clientCacheStartShift = this.clientCacheStart - value;
            }
            this.clientCacheStart = value;
            if (this._clientCacheStartUpdated) {
                this._clientCacheStartUpdated(clientCacheStartShift);
            }
        },
        ConvertPageIndexToServerCacheIndex: function (pageIndex) {
            return pageIndex + $dyn.peek(this.FetchedDataStartPosition);
        },

        ConvertPageIndexToClientCacheIndex: function (pageIndex) {
            return this.ConvertPageIndexToServerCacheIndex(pageIndex) - this.clientCacheStart;
        },

        ConvertClientCacheIndexToServerCacheIndex: function (clientCacheIndex) {
            return clientCacheIndex + this.clientCacheStart;
        },

        ConvertClientCacheIndexToPageIndex: function (clientCacheIndex) {
            return this.ConvertClientCacheIndexToServerCacheIndex(clientCacheIndex) - $dyn.peek(this.FetchedDataStartPosition);
        },

        ConvertServerCacheIndexToClientCacheIndex: function (serverCacheIndex) {
            return serverCacheIndex - this.clientCacheStart;
        },

        ConvertServerCacheIndexToPageIndex: function (serverCacheIndex) {
            return serverCacheIndex - $dyn.peek(this.FetchedDataStartPosition);
        },

        RenderedRowsCount: function () {
            return this.renderedRows && this.renderedRows.length;
        },

        ChangeSelectedIndex: function (event) {
            var self = this;
            self._PendingRowChangeCount(0);
            // Default implementation of the command.  Can be overridden
            if (Array.isArray(event)) {
                var rowIndex = event[0];
                var markMode = event[1];
                var rangePivot = event[2];

                var rowCount = self.DataSource ? $dyn.value(self.DataSource).length : 0;

                // Update the MarkedIndexes array
                var markedIndexes = self.MarkedIndexes();
                switch (markMode) {
                    case $dyn.controls.HierarchicalGridBase.MarkMode.select:
                        // Unmark all marked records
                        for (var i = 0; i < rowCount; i++) {
                            if (markedIndexes[i])
                                markedIndexes[i] = false;
                        }
                        // Mark the record at rowIndex
                        markedIndexes[rowIndex] = true;
                        break;
                    case $dyn.controls.HierarchicalGridBase.MarkMode.toggle:
                        markedIndexes[rowIndex] = !markedIndexes[rowIndex];
                        break;
                    case $dyn.controls.HierarchicalGridBase.MarkMode.rangeSelect:
                        var startIndex, endIndex;
                        if (rangePivot > rowIndex) {
                            startIndex = rowIndex;
                            endIndex = rangePivot;
                        } else {
                            startIndex = rangePivot;
                            endIndex = rowIndex;
                        }
                        // Unmark all marked records
                        for (var i = 0; i < rowCount; i++) {
                            if (markedIndexes[i])
                                markedIndexes[i] = false;
                        }
                        //Mark records in the range
                        for (var i = startIndex; i <= endIndex; i++) {
                            markedIndexes[i] = true;
                        }
                        break;
                    case $dyn.controls.HierarchicalGridBase.MarkMode.clearMark:
                        // Unmark all marked records
                        for (var i = 0; i < rowCount; i++) {
                            if (markedIndexes[i])
                                markedIndexes[i] = false;
                        }
                        break;
                }

                self.MarkedIndexes.valueChanged.raise();

                // Update SelectedIndex property on the grid
                self.SelectedIndex(rowIndex);
            }
        },

        setChildControlFocus: function () {
            ///<sumamry>
            /// Sets focus to the first focusable control in the container.
            ///</sumamry>

            var self = this;

            // Find the first focusable child control
            var childElement = $dyn.util.findFirstFocusableControl(self.element);

            // Call setFocus with the parent form for this container as the root.
            if (childElement) {
                // Return the control that was found to focus, if there is one.
                this.setFocus(childElement, self.Root);
                return childElement;
            }
        },

        findFieldByName: function (fieldName) {
            if (this._selectedRow) {
                return $.find("[data-dyn-controlname='" + escape(fieldName) + "']", this._selectedRow);
            }
        },

        _layoutCompleted: function () {
            this.postRenderInternal(true);
            this.forceRenderCardListIE();
        },

        _getHeaderId: function (headerData) {
            return headerData && $dyn.value(headerData.Name);
        },

        _getHeaderVersion: function (headerData) {
            return headerData && headerData.Version;
        },

        _headerPrefixTemplate: 'HierarchicalGridBaseMarkAllHeader',

        _getHeaderPrefix: function () {
            if (!this._headerPrefix) {
                this._headerPrefix = $dyn._getTemplate(this._headerPrefixTemplate).cloneNode(true);
                this._headerPrefix.removeAttribute('id');
                $dyn.dataBind(this._headerPrefix, this.element.dynContext);
            }

            return this._headerPrefix;
        },

        _renderHeader: function () {
            this.RenderedHeaderMetadata(this.ColumnHeaderMetadata());
        },

        _headerPreRender: function () {
            var prefixElement = this._getHeaderPrefix();
            var parent = prefixElement && prefixElement.parentNode;
            if (parent) {
                parent.removeChild(prefixElement);
            }
        },

        _headerPostRender: function () {
            var prefixElement = this._getHeaderPrefix();
            var headerElement = this._headerRowElement;
            if (headerElement) {
                headerElement.insertBefore(prefixElement, headerElement.firstChild);
            }
        },
    	
        _resizeControlColumn: function (controlIndex, width, updatePersonalizationSettings) {
            // Resizes a control based column inside the grid
            var self = this;

            // Map to the appropriate contained control and set its width so that the update happens synchronously.
            // Setting the width property on the child control should cause all instances throughout the foreach to be updated.
            var childControlContexts = self.getDataColumnContextsByIndex(controlIndex);
            if (childControlContexts.length) {
                for (var i = 0; i < childControlContexts.length; i++) {
                    if (childControlContexts[i]) {
                        var controlElements = self.findControlElementsInAllRows(childControlContexts[i].Name);
                        var j, len;
                        for (j = 0, len = controlElements.length; j < len; j++) {
                            var childControl = $dyn.context(controlElements[j]);
                            if (childControl && childControl.LayoutWidth) {
                                childControl.LayoutWidth(width);
                            } else {
                                $dyn.bindingHandlers.sizing.widthChanged(controlElements[j], width);
                            }
                        }
                    }
                }

                if (updatePersonalizationSettings && childControlContexts.length === 1) {
                    // Persist personalization settings on the server.
                    $dyn.callFunction(childControlContexts[0].PersistPersonalization, childControlContexts[0], ["width", width]);
                }
            }
        },

        _processColumnHeaderResize: function (event, updatePersonalizationSettings, columnHeaderWidth) {
            var self = this;
            var target = $(event.target);
            var parentHeader = target.closest(".columnHeader");
            if (parentHeader.length === 1) {
                var closestWrapper = target.closest('.columnHeaderWrapper');

                // Attempt to line up control columns by index.
                var controlndex = self.interiorColumnIndex(parentHeader.index());
                closestWrapper.outerWidth(columnHeaderWidth);

                // Resize the control column thoroughly
                self._resizeControlColumn(controlndex, columnHeaderWidth, updatePersonalizationSettings);
            }
        },

        _isInvalidMarkTarget: function (event) {
            /// <summary>
            /// Right clicking and ctrl-clicking JumpRef and HyperLink elements in a grid should not trigger a mark event.
            /// </summary>
            /// <param name="event">The event representing the click action.</param>
            /// <returns><c>true</c> if the target is not valid for initiating a record mark; otherwise, <c>false</c>.</returns>
            var self = this;
            var isInvalid = false;
            if (event) {
                var eventContext = event.target ? $dyn.context(event.target) : null;
                var isHyperlink = eventContext ? $dyn.value(eventContext.IsHyperlink) : false;
                var isNavigationAction = self.IsNavigationAction(event);
                if (isHyperlink || isNavigationAction) {
                    isInvalid = $dyn.util.clickedText(event.target);
                }
            }
            return isInvalid;
        },

        getTaskGuideParams: function (options) {
            options = $dyn.ui.Control.prototype.getTaskGuideParams.apply(this, [options]);
            var commandName = options.action;
            var stepArgs = options.arguments;
            var pagingButtons, rowIndex, rowElement;
            if (commandName.indexOf("LoadNextPage") !== -1) {
                var pagingButtons = $(this.element).children('.pagingButtons');
                if (pagingButtons) {
                    var nextButton = pagingButtons.children('[title="' + $dyn.label('Grid_Next') + '"]');
                    options.target = nextButton;
                    options.my = 'top center';
                    options.at = 'bottom center';
                }
            } else if (commandName.indexOf("LoadPreviousPage") !== -1) {
                pagingButtons = $(this.element).children('.pagingButtons');
                if (pagingButtons) {
                    var nextButton = pagingButtons.children('[title="' + $dyn.label('Grid_Previous') + '"]');
                    options.target = nextButton;
                    options.my = 'top center';
                    options.at = 'bottom center';
                }
            } else if (commandName.match('MarkActiveRow|UnmarkActiveRow|ChangeSelectedIndex|ChangeSelectedIndexInCache|NavigationAction')) {
                options.target = $(this.element);
            }
            return options;
        },

        SelectCell: function (event) {
            var controlName = event[1];
            var index = event[0];
            if (!this.Root || !this.Root.ActiveControl) {
                return;
            }
            if ($dyn.isObservable(this.Root.ActiveControl)) {
                this.Root.ActiveControl(controlName);
            } else {
                this.Root.ActiveControl = controlName;
            }
        },

        _getParentControlName: function (element) {
            var parent = element;
            while (parent && typeof(parent.getAttribute) === "function") {
                var ctrlName = parent.getAttribute('data-dyn-controlname');
                var role = parent.getAttribute('data-dyn-role');
                if (ctrlName && (role && role !== 'HierarchicalGridBaseRowTemplate')) {
                    return ctrlName;
                } else {
                    parent = parent.parentNode;
                }
            }
        },

        ToggleMark: function (event) {
            var markedIndexes = this.MarkedIndexes();
            var rowIndex = this.SelectedIndex();
            markedIndexes[rowIndex] = markedIndexes[rowIndex] ? false : true;
        },

        SetMarkAllRecordsMode: function (markState) {
            var rowCount = this.DataSource ? $dyn.value(this.DataSource).length : 0;
            var markedIndexes = this.MarkedIndexes();
            for (var i = 0; i < rowCount; i++) {
                markedIndexes[i] = markState;
            }
        },

        Create: function () {
        },

        Delete: function () {
        },

        getColumnLabel: function (label, name, showLabel) {
            // Can be overriden to get a custom label.

            if ((showLabel == undefined) || $dyn.value(showLabel)) {
                return label;
            }

            return "";
        },

        _invalidateRowSelection: function (rowIndex, prevRowIndex, markMode) {
            /// <summary>
            /// Remove the row selection and marking attributes from the row that was going to be selected.
            /// If multiple records were being marked then update the visualization based on markMode.
            /// </summary>
            if (rowIndex !== prevRowIndex) {
                var rowElem = this.FindRowAtIndex(rowIndex);
                if (rowElem) {
                    rowElem.removeAttribute("data-dyn-row-active");
                }

                switch (markMode) {
                    case $dyn.controls.HierarchicalGridBase.MarkMode.select:
                    case $dyn.controls.HierarchicalGridBase.MarkMode.toggle:
                        this._removeRowMarking(rowElem);
                        break;
                    case $dyn.controls.HierarchicalGridBase.MarkMode.rangeSelect:
                        var startIndex = prevRowIndex <= rowIndex ? prevRowIndex : rowIndex;
                        var endIndex = prevRowIndex > rowIndex ? prevRowIndex : rowIndex;
                        for (var i = startIndex; i <= endIndex; i++) {
                            var rowElem = this.FindRowAtIndex(i);
                            this._removeRowMarking(rowElem);
                        }
                }

                // Set the selected and checked attributes on the previous row.
                var prevRowElement = this.FindRowAtIndex(prevRowIndex);
                if (prevRowElement) {
                    prevRowElement.setAttribute("data-dyn-row-active", true);
                    this._setRowMarking(prevRowElement, true);
                }
            }
        },

        _supportsMarking: function () {
            var isMarkingAllowed = false;
            var isMarkingEnabled = $dyn.value(this.IsMarkingEnabled);
            var isSelectionIndicatorVisible = $dyn.value(this.IsSelectionIndicatorVisible);

            if (isMarkingEnabled && isSelectionIndicatorVisible) {
                isMarkingAllowed = true;
            } else if (isMarkingEnabled && !isSelectionIndicatorVisible) {
                // If isMarkingEnabled = False and isSelectionIndicatorVisible = Yes then marking is allowed when modifier keys are pressed.
                // Only when we do a single select marking is disabled.
                isMarkingAllowed = this._markingAction !== $dyn.controls.HierarchicalGridBase.MarkMode.select;
            } else if (!isMarkingEnabled && isSelectionIndicatorVisible) {
                // If isSelectionIndicatorVisible = True and isMarkingEnabled = No then marking is allowed only on a single select and not when you
                // press modifier keys.
                isMarkingAllowed = this._markingAction == $dyn.controls.HierarchicalGridBase.MarkMode.select ? true : false;
            }

            return isMarkingAllowed;
        },

        // Sets row marking attribute and visuals to the row element
        _setRowMarking: function (rowElement, marked) {
            if (rowElement) {
                rowElement.setAttribute('data-dyn-row-active', marked);
                rowElement.setAttribute('data-dyn-row-marked', marked);

                // Set the same attribute on the marking checkbox as well
                if (rowElement.children && rowElement.children[0]) {
                    $(".marked-record-checkbox[role='checkbox']", rowElement.children[0]).attr('aria-checked', marked);
                }
            }
        },

        // Removes the row marking if it exists currently
        _removeRowMarking: function (rowElement) {
            if (rowElement && rowElement.hasAttribute('data-dyn-row-active')) {
                rowElement.setAttribute('data-dyn-row-active',false);

                // Remove the same attribute from the marking checkbox as well
                if (rowElement.children && rowElement.children[0]) {
                    $(".marked-record-checkbox[role='checkbox']", rowElement.children[0]).removeAttr('aria-checked');
                }
            }
        },

        hasMarkedRows: function () {
            /// <summary>
            /// Determines if this grid control has marked rows.
            /// </summary>
            /// <returns>A Boolean value indicating if this grid control has marked rows.</returns>

            var foundMarkedRow = false;
            var rowCount = this.DataSource ? $dyn.value(this.DataSource).length : 0;
            var markedIndexes = this.MarkedIndexes();

            for (var i = 0; i < rowCount && !foundMarkedRow; i++) {
                foundMarkedRow = markedIndexes[i] === true;
            }

            return foundMarkedRow;
        },

        columnMutator: function (element, index) {
            /// <summary>
            /// Mutates the content as appropriate for Tabular List.
            /// </summary>
            element.classList.add('listCell');
            if (index === 0) {
                element.classList.add('listCell-firstCell');
            }
        },

        IsMarked: function (index) {
            return this.MarkedIndexes()[index];
        },

        calculateMarkMode: function (event) {
            /// <summary>
            /// Calculates the appropriate mark mode and rangePivot given a jQuery event.
            /// </summary>
            var mode;
            var target = $(event.target);

            // If ShowRowLabels(IsSelectionIndicatorVisible) and MultiSelect (IsMarkingEnabled) are both set to No then disable marking.
            if (!$dyn.value(this.IsMarkingEnabled) && !$dyn.value(this.IsSelectionIndicatorVisible)) {
                return $dyn.controls.HierarchicalGridBase.MarkMode.none;
            }

            // If the user clicked on the checkbox then it is the toggle behavior (IsSelectionIndicatorVisible and IsMarkingEnabled both will be true)
            if (target.hasClass('checkMarkTarget') || target.hasClass('markContainer')) {
                this._markingAction = $dyn.controls.HierarchicalGridBase.MarkMode.toggle;
                return $dyn.controls.HierarchicalGridBase.MarkMode.toggle;
            }

            // If IsMarkingEnabled is set on the grid allow multiple records to be marked
            if ($dyn.value(this.IsMarkingEnabled)) {
                if (event.ctrlKey && event.shiftKey) {
                    mode = $dyn.controls.HierarchicalGridBase.MarkMode.rangeExpansion;
                } else if (event.ctrlKey) {
                    if (event.which == $dyn.ui.KeyCodes.upArrow || event.which == $dyn.ui.KeyCodes.downArrow)
                        mode = $dyn.controls.HierarchicalGridBase.MarkMode.none;
                    else
                        mode = $dyn.controls.HierarchicalGridBase.MarkMode.toggle;
                    // Clear RangeSelection indicating that the shift key is no longer pressed.
                    this._rangeSelection = false;
                } else if (event.shiftKey) {
                    mode = $dyn.controls.HierarchicalGridBase.MarkMode.rangeSelect;
                } else if ($dyn.value(this.IsSelectionIndicatorVisible)) {
                    // Only if IsSelectionIndicatorVisible (ShowRowLabels) is set to TRUE do implicit marking, i.e. record is marked when selected.
                    mode = $dyn.controls.HierarchicalGridBase.MarkMode.select;
                    // Clear RangeSelection indicating that the shift key is no longer pressed.
                    this._rangeSelection = false;
                } else if (!$dyn.value(this.IsSelectionIndicatorVisible)) {
                    // If IsSelectionIndicatorVisible (ShowRowLabels) is set to FALSE then clear existing marking if it exists
                    // and don't mark the record on selection (i.e. dont implicitly mark the record)
                    mode = $dyn.controls.HierarchicalGridBase.MarkMode.clearMark;
                    // Clear RangeSelection indicating that the shift key is no longer pressed.
                    this._rangeSelection = false;
                }
            }
            else {
                if ($dyn.value(this.IsSelectionIndicatorVisible)) {
                    // Only if IsSelectionIndicatorVisible (ShowRowLabels) is set to TRUE do implicit marking, i.e. record is marked when selected.
                    mode = $dyn.controls.HierarchicalGridBase.MarkMode.select;
                }
            }

            // If the rangeSelection flag is not set, but the markMode is rangeSelect, then Set the rangePivot and the rangeSelection to true.
            // RangeSelection flag is set to false if an operation other than rangeSelect is performed which should cause the rangePivot to be reset.
            if (!this._rangeSelection && mode == $dyn.controls.HierarchicalGridBase.MarkMode.rangeSelect) {
                this._rangeSelection = true;
                this._rangePivot = this.SelectedIndex() + $dyn.value(this.FetchedDataStartPosition);
            }

            this._markingAction = mode;
            return this._markingAction;
        },

        rangePivotWithinPage: function () {
            return this._rangePivot - $dyn.value(this.FetchedDataStartPosition);
        },

        /// <summary>
        /// Notifies of any changes to the child hierarchy structure for the given target element.
        /// </summary>
        /// <returns>Returns a computed observable</returns>
        _rowElementGenerator: function (parentElement) {
            // This is used so that the computed value changes every time, to avoid
            // caching inside $dyn.computed.

            var self = this;
            return $dyn.computed(function () {

                var cellType = self._fastEdit() && $dyn.value(self.AllowEdit) ? 'cellEditor' : 'cellRoleName';
                var recurseThroughChildHierarchy = function (element) {
                    var clonedElement = document.createElement(element.nodeName);

                    var controlName = element.getAttribute('data-dyn-controlname');
                    if (controlName) {
                        clonedElement.setAttribute('data-dyn-controlname', controlName);
                    }

                    var roleName = element.getAttribute('data-dyn-role');
                    if (roleName) {
                        roleName = ($dyn.controls[roleName] && $dyn.controls[roleName].prototype[cellType]) || roleName;
                        clonedElement.setAttribute('data-dyn-role', roleName);
                    }

                    var attributes = element.getAttribute('data-dyn-options');
                    if (attributes) {
                        clonedElement.setAttribute('data-dyn-bind', attributes.slice(1, -1));
                    }

                    // Observe the children and recurse.
                    var children = $dyn.value(element.dynChildren);
                    if (children) {
                        for (var i = 0, len = children.length; i < len; i++) {
                            var childElement = children[i];
                            var clonedChildElement = recurseThroughChildHierarchy(childElement);
                            clonedElement.appendChild(clonedChildElement);
                        }
                    }

                    return clonedElement;
                };

                var copy = recurseThroughChildHierarchy(parentElement);
                return copy.children;
            });
        },

        _cachedInactiveItemElement: undefined,

        get _inactiveItemElement() {
            var self = this;
            var inactiveItemElement = this._cachedInactiveItemElement;
            if (!inactiveItemElement) {
                $dyn.log.list && $dyn.log.list('List: Generating inactive row element');
                inactiveItemElement = this._cachedInactiveItemElement = self.ChildHierarchy()[0];
            }

            if (inactiveItemElement && !$dyn.context(inactiveItemElement)) {
                if (!this._itemDataContext) {
                    this._listBodyRole = $dyn.role(this._listBodyElement);
                    this._itemDataContext = new $dyn.DataContext(self._inactiveItem, self._listBodyElement.dynContext);
                }
                $dyn.log.list && $dyn.log.list('List: Databinding inactive row element');
                this._listBodyRole.dataBindItem(inactiveItemElement, this._itemDataContext);
                $dyn.layout.arrange(inactiveItemElement);
            }

            return inactiveItemElement;
        },

        _resetCachedInactiveItemElement: function (newInactiveItemElement) {
            if (this._cachedInactiveItemElement) {
                $dyn.log.list && $dyn.log.list('List: Unbind inactive row');
                $dyn.unbind(this._cachedInactiveItemElement);
            }
            this._cachedInactiveItemElement = newInactiveItemElement ? newInactiveItemElement : null;
        },

        updateSchema: function (changes) {
            $dyn.log.list && $dyn.log.list('List: Inactive row schema changed (updateSchema)');

            this._resetCachedInactiveItemElement();
            this._rerenderOptions($dyn.peek(this._rerenderOptions) + 1);
        },

        rowIndex: function (element) {
            if (!element) {
                return undefined;
            }

            var clientCacheIndex = parseInt(element.getAttribute('aria-posinset')) - 1;
            return this.ConvertClientCacheIndexToPageIndex(clientCacheIndex);
        },

        rowClick: function (event) {
            // Can be called statically

            var list = $dyn.context($(event.currentTarget).closest('.listRoot')[0]);
            list.RowClicked(event);
        },

        _getValues: function () {
            // Internal use only

            var rows = $('.listBody', this.element).find('.listItem');
            var rowsArray = [];
            rows.each(function (index, element) {
                var row = $(element);
                var rowArray = new Array();
                row.find('.listCell:not(.repeater-template)').each(function (listCellIndex, listCell) {
                    var value = '';
                    if ($(listCell).is('.checkBox-container')) {
                        value = $(listCell).attr('aria-checked') ? true : false;
                    } else {
                        var foundInputs = $(listCell).find('input');

                        if (foundInputs.length > 0) {
                            // Simple taking the first input element won't cover all cases, but it is acceptable
                            // given the purposes of this method.
                            var inputElement = foundInputs[0];
                            value = inputElement.value;
                        }
                    }

                    rowArray.push(value);
                });
                rowsArray.push(rowArray);
            });

            return rowsArray;
        },

        _hasTextSelection: function (event) {
            // Internet Explorer selects text in the input field by itself when Ctrl/Shift key is pressed
            // We don't treat such a selection as text selected by user
            // As a result any selection with Ctrl/Shift pressed is not considered to be user selection
            if (event.ctrlKey || event.metaKey || event.shiftKey) {
                return false;
            }

            // Detect if there is a selection.  The browser will normally remove the selection as part of a click but if there is a selection that can indicate
            // that the user was not doing a click but actually was selecting text.
            var selectedText = window.getSelection().toString();
            if (selectedText) {
                $dyn.log.list && $dyn.log.list('List: row click canceled due to selection: %s', selectedText);
                return true;
            }

            var documentSelection = document.activeElement && $dyn.ui.selectionStart(document.activeElement) !== $dyn.ui.selectionEnd(document.activeElement);

            if (documentSelection) {
                $dyn.log.list && $dyn.log.list('List: row click canceled due to selection: start="%s", end="%s"',
                    $dyn.ui.selectionStart(document.activeElement),
                    $dyn.ui.selectionEnd(document.activeElement));

                return true;
            }

            return false;
        },

        ExecuteExportToExcel: function (onlyMarkedRows) {
            /// <summary>
            /// Exports the contents of this grid to Excel.
            /// </summary>
            /// <param name="onlyMarkedRows">A Boolean value indicating whether or not to limit the export to marked rows.</param>
            $dyn.callFunction(this.ExportToExcel, this, onlyMarkedRows);
        },

        GetScrollRegion: function () {
            var that = this;
            if (!this.scrollRegion) {
                this.scrollRegion = $(this.element).find(".scrollRegion").first();
                if (this.WindowResizeHandler) {
                    $(window).resize(this.WindowResizeHandler);
                }

            }
            return this.scrollRegion;
        },
        GetScrollRegionHeight: function () {
            return this.GetScrollRegion().height();
        },
        SetScrollRegionHeight: function (value) {
            this.GetScrollRegion().height(value);
        },
        GetScrollRegionWidth: function () {
            if (this.CachedScrollRegionWidth) {
                return this.CachedScrollRegionWidth;
            }
            this.UpdateScrollRegionWidth();
            return this.CachedScrollRegionWidth;
        },
        UpdateScrollRegionWidth: function () {
            this.CachedScrollRegionWidth = this.GetScrollRegion().width();
        },
        GetScrollRegionTop: function () {
            if (this.CachedScrollRegionTop !== undefined && this.CachedScrollRegionTop !== null) {
                return this.CachedScrollRegionTop;
            }
            this.CachedScrollRegionTop = this.GetScrollRegion().scrollTop();
            return this.CachedScrollRegionTop;
        },
        UpdateScrollTop: function (value) {
            if (value < 0) {
                value = 0;
            }

            var oldValue = this.CachedScrollRegionTop;
            if (value !== undefined && value !== null) {
                this.CachedScrollRegionTop = value;
            } else {
                this.CachedScrollRegionTop = this.GetScrollRegion().scrollTop();
            }

            return oldValue !== this.CachedScrollRegionTop;
        },
        SetScrollRegionTop: function (value) {
            this.GetScrollRegion().scrollTop(value);
            this.UpdateScrollTop(value);
        },

        _updateDefaultActionEnabled: function () {
            var defaultActionEnabled = $dyn.value(this.NavigationEnabled);

            this._updateRowDefaultAction(this._cachedInactiveItemElement, defaultActionEnabled, '_lastInactiveDefaultControl');
            this._updateRowDefaultAction(this._selectedRow, defaultActionEnabled, '_lastActiveDefaultControl');
        },

        _updateRowDefaultAction: function (rowElement, defaultActionEnabled, lastDefaultControlField) {
            // If the default action is enabled on grid then the first column on grid that can have the default action will be the default action (DefaultActionEnabled).
            var lastDefaultControl = this[lastDefaultControlField];

            if (defaultActionEnabled && rowElement) {
                var defaultActionControl = this._getDefaultControl(rowElement);
                if (defaultActionControl) {
                    defaultActionControl.DefaultActionEnabled = true;
                }
                this[lastDefaultControlField] = defaultActionControl;
            }

            if (lastDefaultControl && (!defaultActionEnabled || lastDefaultControl !== defaultActionControl)) {
                lastDefaultControl.DefaultActionEnabled = false;
            }
        },

        _getDefaultControl: function (rowElement) {
            var columns = $dyn.children(rowElement);
            if (columns) {
                for (var i = 0, len = columns.length; i < len; i++) {
                    var column = columns[i];
                    var control = $dyn.context(column);
                    if (control) {
                        if (control.SupportsDefaultAction) {
                            return control;
                        }
                    }
                }
            }
        },

        dispose: function () {
            $dyn.ui.Control.prototype.dispose.call(this);
            $(document).off("densitychanged", this._densityChangeHandler);
            if (this.WindowResizeHandler) {
                $(window).off('resize', this.WindowResizeHandler);
            }

            if (this._cachedInactiveItemElement) {
                $dyn.unbind(this._cachedInactiveItemElement);
                this._cachedInactiveItemElement = null;
            }

            this._lastInactiveDefaultControl = null;
            this._lastActiveDefaultControl = null;
        },
    });

    $dyn.controls.HierarchicalGridBase.MarkMode = {
        /// <summary>
        /// Defines the mark mode given a click action in list view.
        /// </summary>
        select: 0, // Selects that record and unmarks all other records.
        toggle: 1, // Toggles the target record - ctrl + click or checkbox.
        rangeSelect: 2, // Contiguous range selection. Selects the range and unselects other records - shift + click.
        rangeExpansion: 3, // Range expansion - ctrl + shift + click.
        none: 4, // Don't mark the row
        clearMark: 5 // Clear marking
    };

    $dyn.controls.HierarchicalGridBaseRowTemplate = function (data, element, parent) {
        $dyn.ui.Control.apply(this, arguments);
        this.ParentGrid = parent;
        this.ServerFocusableControl = parent;
    };

    $dyn.controls.HierarchicalGridBaseRowTemplate.prototype = $dyn.extendPrototype($dyn.ui.Control.prototype, {
        ParentGrid: undefined,
        AllowPersonalization: false,

        focusCallback: function (options) {
            var self = this;
            var controlElement = options[0] ? options[0] : self.element;
            if (controlElement) {

                // Focus on the grid control.
                var elementToFocus = self.ParentGrid ? self.ParentGrid.element : $(controlElement).parents('[data-dyn-role="List"]')[0];
                if (elementToFocus) {
                    $dyn.util.focusElement(elementToFocus);
                }
                $dyn.log.focus && $dyn.log.focus('Focus: Grid->focusCallback set to: %s', self.Name);
            }
        }
    });


    $dyn.bindingHandlers.hierarchicalgridbase = function (targetElement, role) {
        var renderForEachTask = $dyn.bindingHandlers.foreach.task;
        var itemTask = $dyn.bindingHandlers.foreach.itemTask;
        var target = $(targetElement);
        if (!role) {
            role = $dyn.role(targetElement);
        }
        var listContext = $dyn.context(targetElement);
        var appliedBinding = false;
        // The template to use for the binding may have been cached before
        var selectedItemElement = targetElement.dynSelectedRow;
        if (!selectedItemElement) {
            // Current children are used as template
            var templateContainer = target.children().not(".repeater-template");

            templateContainer.attr($dyn.const.stopBindingAttribute, true);

            $dyn.util.listEmpty(targetElement);

            selectedItemElement = listContext._selectedRow = targetElement.dynSelectedRow = templateContainer[0].children[0];
        }

        /// INFINITE SCROLL LOGIC:
        /// STATEMENTS:
        /// - Rows in the grid are rendered based on 'collection' items provided from outside to the grid as DataSource
        /// - Collection is representing a projection of the cache
        /// - Grid has knowledge about cache based on fetchedDataStartPosition, CacheSize, dataBelow, dataAbove parameters
        /// - fetchedDataStartPosition - represents 0-based position in the cache that is the start of the collection
        /// - Cache can grow in both directions
        /// - dataAbove, dataBelow are representing whether cache can grow Up or Down from the current state
        /// - Grid renders row elements based on collection data and whitespaces: above nd below row elements
        /// - Upper whitespace represents how many records are currently in the cache above first record in the collection
        /// - Lower whitespace represents how many records are currently in the cache below last record in the collection
        /// - Sizes of whitespaces are calculated based on rowHeight that is static by assumption
        ///
        /// SCROLLING FLOW:
        ///    SyncVerticalScrollOffset function is invoked on every scroll event -> It invokes fetchDataOnScroll function
        ///   if scroll position is outside of data triggers segment -> fetchDataOnScroll calculates new fetchDataStartPosition and fetchSize
        ///   and invokes LoadData function that unders ServerForm scenario goes to server as command. ->
        ///   Right after LoadData call readjustScrollParametersByFetchCondition gets invokes with new fetchStart and fetchSize parameters. ->
        ///   This function updates dataTriggers so that no new immediate fetchDataOnScroll calls happens. -> Once new data comes to grid,
        ///   After rendering process adjustScrollParameters is invoked that calculates all scroll parameters including dataTriggers.
        ///   It also resizes White Spaces and applies coordinate system shift that can happen when cache grown up. ->
        ///   ensureFullViewPortContent is invoked that can also invoke LoadData if it decides that it is not enough data to fill the view and have good reserve
        ///
        /// SCROLL PARAMETERS:
        /// - cacheSize, fetchedDataStartPosition, dataAbove, dataBelow - parameters that are given to grid from outside
        /// - paddingRowsAbove/Below - representing the number of rows that can be renderedin the upper and lower whitespaces
        /// paddingRowsAbove can be more than fetchedDataStartPosition if dataAbove is true. We dedicate some space for the rows that are not iun the cache,
        /// so that user can scroll there and request new rows by expanding the cache. Same for paddingRowsBelow
        /// - dataTriggerMax, dataTriggerMin - trigger points for scroll position of the scroll region.
        /// When scroll position goes out of these boundaries, we request new data
        /// - rowHeight - the height of the rendered row in pixels. rowCount - count of the rendered rows
        ///
        /// FUNCTIONS:
        /// - adjustScrollParameters - adjusts scroll parameters based on row height, rendered row count and collection data
        /// Resizes whitespaces.
        /// - readjustScrollParametersByFetchCondition - updates scroll parameters, based on requested fetch position and fetch size.
        /// Used after requesting the new data, updates data triggers, so that between request and rendering no redundant requests are going
        /// - applyPendingScrollShifts. Changes scroll position based on shifts of the cache that are pending.
        /// 'Shift' means that 0-position of the cache changed. Happens when we expand cache above.
        /// - ensureFullViewPortContent - Triggers the logic that makes sure that current visible area has data
        /// - fetchDataOnScroll - Requests data load if needed, based on data triggers.
        /// Takes into account the scroll direction and requests more data towards scroll direction
        /// Requests page size increase if current row count is not enough to fill visible space and non-visible reserve
        /// - syncVerticalScrollOffset - the handler of the scroll event that triggers infinite scroll logic if scroll position is outside of data triggers
        /// Check focus triggers to decide whether it is needed to restore focus
        /// - restoreSelectionFocus - invokes focus restoration logic on the Grid control if selected item is in the view
        /// Otherwise caclculates focus triggers that indicate under which scroll position selected item goes into view.

        // FetchedDataStartPosition - Server cache co-ord, page start in db cache
        // SelectedIndex - Page coord - selected index in page
        // _InternalFocusedRowIndex - Server cache co-ord,
        // lastSelectedIndex (local var - binding) - Server coord

        // We will need the scroll region a lot, let's have it available
        var scrollRegion = $(listContext.element).find(".scrollRegion").first();

        // Maintain the fixed length row height for items
        var _rowHeight;
        function getRowHeight(recalculate) {
            if (!_rowHeight || recalculate) {
                // TODO: In Chrome first row has less height than others. Would be great to investigate.
                var lastRow = $(targetElement).find('.rowTemplate.listItem:last');
                // When the row element is not visible we still can have non 0 outer height that will be incorrect one
                // That's why we need offsetHeight check too
                if (lastRow.length && lastRow[0].offsetHeight && !$dyn.shell.isShellInitializing()) {
                    _rowHeight = lastRow.outerHeight();
                }
            }
            return _rowHeight;
        }

        // Ensure padding elements above and below the item collection
        // These padding elements are used for infinite scrolling
        var listContent = targetElement.parentNode;
        var upperWhiteSpaceElement = $(".listRoot-loadingAbove", listContent)[0];
        if (!upperWhiteSpaceElement) {
            upperWhiteSpaceElement = $("<div class='listRoot-loading listRoot-loadingAbove' style='display: none;'><span>" + $dyn.label('List_LoadingMoreData') + "</span></div>")[0];
            listContent.insertBefore(upperWhiteSpaceElement, targetElement);
        }
        var lowerWhiteSpaceElement = $(".listRoot-loadingBelow", listContent)[0];
        if (!lowerWhiteSpaceElement) {
            lowerWhiteSpaceElement = $("<div class='listRoot-loading listRoot-loadingBelow' style='display: none;'><span>" + $dyn.label('List_LoadingMoreData') + "</span></div>")[0];
            listContent.insertBefore(lowerWhiteSpaceElement, targetElement.nextSibling);
        }

        // Text elements in white space node
        var whiteSpaceTexts = $(listContent).find(".listRoot-loading span");

        var _lastKnownWhiteSpaceWidth;
        function updateWhiteSpaceWidth() {
            var width = listContext.GetScrollRegionWidth();
            if (width !== _lastKnownWhiteSpaceWidth && width !== 0) {
                _lastKnownWhiteSpaceWidth = width;
                whiteSpaceTexts.css("max-width", width);
            }
        }

        // In infinite scrolling mode, scrolling is a complex function of many parameters
        // Let's track those parameters based on scroll events
        var scrollParameters;

        var lastRequestedPageSize;
        var upperWhiteSpaceVisible;
        var lowerWhiteSpaceVisible;
        var upperWhiteSpaceHeight = 0;
        var lowerWhiteSpaceHeight = 0;
        var scrollStateAdjusted;
        // Adjusts scroll triggers and other parameters based on the loaded data
        function adjustScrollParameters(rowHeight, renderedRowCount, data) {
            rowHeight = rowHeight || 0;
            var cacheSize = $dyn.value(data.CacheSize) || 0;
            var fetchedDataStartPosition = $dyn.value(data.FetchedDataStartPosition) || 0;
            var dataAbove = $dyn.value(data.DataAbove);
            var dataBelow = $dyn.value(data.DataBelow);

            var bufferAbove = (listContext.clientCacheStart !== undefined && (dataAbove || listContext.clientCacheStart > 0));
            var bufferBelow = (listContext.clientCacheStart !== undefined && (dataBelow || (listContext.clientCacheStart + renderedRowCount) < cacheSize));

            if (upperWhiteSpaceVisible !== bufferAbove) {
                upperWhiteSpaceElement.style.display = bufferAbove ? "block" : "none";
                upperWhiteSpaceVisible = bufferAbove;
            }

            if (lowerWhiteSpaceVisible !== bufferBelow) {
                lowerWhiteSpaceElement.style.display = bufferBelow ? "block" : "none";
                lowerWhiteSpaceVisible = bufferBelow;
            }

            if (!upperWhiteSpaceHeight && bufferAbove) {
                upperWhiteSpaceHeight = $(upperWhiteSpaceElement).outerHeight();
            }

            if (!lowerWhiteSpaceHeight && bufferBelow) {
                lowerWhiteSpaceHeight = $(lowerWhiteSpaceElement).outerHeight();
            }

            // Upper padding
            var bufferAboveHeight = 0;
            var bufferBelowHeight = 0;
            if (bufferAbove) {
                bufferAboveHeight = upperWhiteSpaceHeight;
            }

            if (bufferBelow) {
                bufferBelowHeight = lowerWhiteSpaceHeight;
            }
            var scrollViewportHeight = listContext.GetScrollRegionHeight();
            var dataTriggerMin = bufferAboveHeight;
            var dataTriggerMax = $(listContent).height() - bufferBelowHeight - scrollViewportHeight;

            if (bufferAbove) {
                dataTriggerMin += rowHeight;
            }
            if (bufferBelow) {
                dataTriggerMax -= rowHeight;
            }

            // Apply pending scroll shifts to account for any cache expansion causing the origin of the scroll to change
            applyPendingScrollShifts();

            // Keep the scroll params around until updated
            scrollParameters = listContext._scrollParameters = {
                cacheSize: cacheSize,
                fetchedDataStartPosition: fetchedDataStartPosition,
                dataAbove: dataAbove,
                dataBelow: dataBelow,
                dataTriggerMax: dataTriggerMax,
                dataTriggerMin: dataTriggerMin,
                rowHeight: rowHeight,
                renderedRowCount: renderedRowCount,
                bufferAboveHeight: bufferAboveHeight,
                bufferBelowHeight: bufferBelowHeight
            };

            // In case scroll viewport height was 0, we consider scroll state being not adjusted
            scrollStateAdjusted = (scrollViewportHeight !== 0);
        }

        // Manages pending scroll shifts
        var pendingScrollShifts = 0;
        function applyPendingScrollShifts() {
            if (pendingScrollShifts !== 0) {
                if (scrollParameters) {
                    var shiftInPixels = pendingScrollShifts * scrollParameters.rowHeight;
                    listContext.SetScrollRegionTop(listContext.GetScrollRegionTop() + shiftInPixels);
                }
                pendingScrollShifts = 0;
            }
        }

        var viewportFull = true;

        // Ensures that the viewport is filled up with rows if it is loaded at some arbitrary location
        function ensureFullViewPortContent() {
            // Just trigger the fetchData logic with current scroll top
            if (scrollParameters) {
                if (!scrollAdjustmentPending) {
                    if ($dyn.value(dataLoadPending) <= 0 && !$dyn.value(dataLoadedRenderPending) && !$dyn.value($dyn.clientBusy)) {
                        tryFetchDataOnScroll();
                    } else {
                        scrollAdjustmentPending = true;
                    }
                }
                //viewportFull = fetchDataOnScroll(listContext.GetScrollRegionTop());
            }
        }

        function readjustScrollState() {
            if ($dyn.value(listContext.InfiniteScroll)) {
                var rowHeight = getRowHeight();

                if (listContext) {
                    var renderedRowCount = listContext.RenderedRowsCount();
                    adjustScrollParameters(rowHeight, renderedRowCount, listContext);
                }
            }
        }

        var _reflowHandlerTimout;
        function reflowHandler() {
            listContext.SyncHorizontalScrollOffset($dyn.log.list);
            if (_reflowHandlerTimout) {
                clearTimeout(_reflowHandlerTimout);
            }
            _reflowHandlerTimout = listContext.setTimeout(function () {
                $dyn.log.list && $dyn.log.list("List: Reflow Handler invoked");
                if ($dyn.value(listContext.InfiniteScroll)) {
                    readjustScrollState();
                    ensureFullViewPortContent();
                }
                _reflowHandlerTimout = undefined;
                if (listContext._reflowHandlerInvoked) {
                    listContext._reflowHandlerInvoked();
                }
            });
        }

        // When list body is resized, handler changes
        function resizeHandler() {
            updateWhiteSpaceWidth();
        }

        // Determines whether LoadData call is going to return any new data based on provided dataStart and DataSize parameters
        function loadDataNeeded(dataStart, dataSize) {
            if (scrollParameters && listContext.clientCacheStart !== undefined && (!listContext.Root || !listContext.Root.IsCloseRequested || !listContext.Root.IsCloseRequested())) {
                // No need to load data that is already rendered
                if (dataSize <= 0 || (dataStart >= listContext.clientCacheStart && dataSize > 0 && (dataStart + dataSize) <= listContext.clientCacheStart + scrollParameters.renderedRowCount)) {
                    $dyn.log.list && $dyn.log.list("List: Loading Data - not needed. Requested start: %s, requested size: %s", dataStart, dataSize);
                    return false;
                }

                // Make sure we are going to load data within current cache or expand current cache
                if (dataStart + dataSize <= 0 && !scrollParameters.dataAbove) {
                    $dyn.log.list && $dyn.log.list("List: Loading Data - not needed. Requested start: %s, requested size: %s", dataStart, dataSize);
                    return false;
                }

                if (dataStart >= scrollParameters.cacheSize && !scrollParameters.dataBelow) {
                    $dyn.log.list && $dyn.log.list("List: Loading Data - not needed. Requested start: %s, requested size: %s", dataStart, dataSize);
                    return false;
                }

                var allowLoad = false;

                // Allow to load data if there is non loaded data above
                if (dataStart < listContext.clientCacheStart && (scrollParameters.dataAbove || listContext.clientCacheStart > 0)) {
                    allowLoad = true;
                }

                // Allow to load data if there is non loaded data below
                if ((dataStart + dataSize) > scrollParameters.renderedRowCount && (scrollParameters.dataBelow || listContext.clientCacheStart + scrollParameters.renderedRowCount < scrollParameters.cacheSize)) {
                    allowLoad = true;
                }

                if (!allowLoad) {
                    $dyn.log.list && $dyn.log.list("List: Loading Data - not needed. Requested start: %s, requested size: %s", dataStart, dataSize);
                }

                return allowLoad;
            }

            return false;
        }

        var lastLoadDataState = {};
        function loadDataStateChanged() {
            if (!scrollParameters || lastLoadDataState.isDirty !== false) {
                return true;
            }

            var stateChanged = !(scrollParameters.dataAbove === lastLoadDataState.dataAbove && scrollParameters.dataBelow === lastLoadDataState.dataBelow &&
                scrollParameters.cacheSize === lastLoadDataState.cacheSize && scrollParameters.fetchedDataStartPosition === lastLoadDataState.fetchedDataStartPosition &&
                scrollParameters.renderedRowCount === lastLoadDataState.renderedRowCount && listContext.clientCacheStart === lastLoadDataState.clientCacheStart);

            if (!stateChanged) {
                $dyn.log.list && $dyn.log.list("List: Loading Data - state didn't change");
            }

            return stateChanged;
        }
        function populateLastLoadDataState() {
            lastLoadDataState = {
                isDirty: false,
                dataAbove: scrollParameters.dataAbove,
                dataBelow: scrollParameters.dataBelow,
                cacheSize: scrollParameters.cacheSize,
                fetchedDataStartPosition: scrollParameters.fetchedDataStartPosition,
                renderedRowCount: scrollParameters.renderedRowCount,
                clientCacheStart: listContext.clientCacheStart
            };
        }

        // Invokes LoadData function when client is not busy and when it makes sense to invoke it
        function invokeLoadData(dataStart, dataSize) {
            if (loadDataStateChanged() && loadDataNeeded(dataStart, dataSize)) {
                dataLoadPending($dyn.value(dataLoadPending) + 1);
                lastRequestedPageSize = dataSize;
                populateLastLoadDataState();
                $dyn.callFunction(listContext.LoadData, self, [dataStart, dataSize], function loadDataCallback(shift) {
                    shift = parseInt(shift);
                    if (shift > 0) {
                        listContext.applyServerCacheCoordinateShift(shift);
                    }
                    if (scrollPending) {
                        dataLoadedCompletionPending++;
                    }
                    else {
                        dataLoadedRenderPending(true);
                        dataLoadPending($dyn.value(dataLoadPending) - 1);
                    }
                });
            }
        }

        function getDesiredPageSize(scrollHeight) {
            if (scrollParameters && scrollParameters.rowHeight) {
                return 2 * Math.ceil(scrollHeight / scrollParameters.rowHeight);
            }
            return;
        }

        /// <summary>
        /// Gets client cache related index of the top most row in viewport
        /// Row is considered to be visible even if it is partially visible
        /// </summary>
        function getFirstVisibleRow() {
            if (scrollParameters && scrollParameters.rowHeight) {
                var scrollTop = listContext.GetScrollRegionTop();
                var firstVisibleRow = Math.floor((scrollTop - scrollParameters.bufferAboveHeight) / scrollParameters.rowHeight);
                return firstVisibleRow;
            }

            return undefined;
        }

        function getVisibleRowCount(scrollHeight) {
            var rowHeight = getRowHeight();
            if (scrollHeight && rowHeight) {
                return Math.round(scrollHeight / rowHeight);
            }
        }

        function getDesiredPageStart(scrollHeight) {
            var desiredPageStart;
            if (scrollParameters && scrollParameters.rowHeight && scrollParameters.cacheSize) {
                var firstVisibleRow = getFirstVisibleRow();
                var visibleRowsCount = getVisibleRowCount(scrollHeight);
                var pageSize = lastRequestedPageSize ? lastRequestedPageSize : getDesiredPageSize(scrollHeight);
                if (pageSize < visibleRowsCount) {
                    desiredPageStart = firstVisibleRow;
                } else {
                    desiredPageStart = firstVisibleRow - Math.round((pageSize - visibleRowsCount) / 2);
                }
                desiredPageStart = listContext.ConvertClientCacheIndexToServerCacheIndex(desiredPageStart);
                if (desiredPageStart + pageSize >= scrollParameters.cacheSize) {
                    desiredPageStart = scrollParameters.cacheSize - pageSize;
                }
                if (desiredPageStart < 0) {
                    desiredPageStart = 0;
                }
            }
            return desiredPageStart;
        }

        // Fetches data from server for this scroll operation - the given scrollTop determines what offset to fetch data from
        var dataLoadPending = $dyn.observable(0);
        var scrollPendingTimeoutId;
        var scrollPending;
        var scrollAdjustmentPending;
        var dataLoadedCompletionPending = 0;
        var dataLoadedRenderPending = listContext._dataLoadedRenderPending = $dyn.observable(0);
        function fetchDataOnScroll(scrollTop) {
            var scrollViewportHeight = listContext.GetScrollRegionHeight();

            if (!scrollParameters || !scrollParameters.rowHeight || !scrollViewportHeight || $dyn.shell.isShellInitializing() || listContext.clientCacheStart === undefined) {
                return false;
            }

            var requestPageSize = getDesiredPageSize(scrollViewportHeight);
            var clientStart = listContext.clientCacheStart;
            var dataFetched = false;
            if (scrollTop > scrollParameters.dataTriggerMax) {
                // The position to load new data in client cache coordinate system.
                var lastRenderedRow = clientStart + listContext.RenderedRowsCount();
                if (scrollParameters.dataBelow || lastRenderedRow < scrollParameters.cacheSize) {
                    var newFetchPosition = lastRenderedRow - 1;
                    invokeLoadData(newFetchPosition, requestPageSize);
                    dataFetched = true;
                }
            }
            if (scrollTop < scrollParameters.dataTriggerMin && !dataFetched) {
                var newFetchBottomPosition = clientStart;
                if (scrollParameters.dataAbove || newFetchBottomPosition >= 0) {
                    var newFetchPosition = newFetchBottomPosition - requestPageSize;
                    invokeLoadData(newFetchPosition, requestPageSize);
                }
            }
            return true;
        }

        function tryFetchDataOnScroll() {
            // Where are we in the scroll offset?
            var scrollTop = listContext.GetScrollRegionTop();
            var adjustmentPerformed = false;

            if (scrollParameters) {
                // Are we outside of our scroll triggers?
                if (scrollTop < scrollParameters.dataTriggerMin || scrollTop > scrollParameters.dataTriggerMax) {
                    // We need to fetch some data from server
                    adjustmentPerformed = fetchDataOnScroll(scrollTop);
                }

                // Let's restore selection focus when selected item enters into visible area (maintained by focus triggers)
                if (scrollTop >= scrollParameters.selectionFocusTriggerMin && scrollTop <= scrollParameters.selectionFocusTriggerMax) {
                    listContext.restoreSelectionFocus();
                    scrollParameters.selectionFocusTriggerMax = undefined;
                    scrollParameters.selectionFocusTriggerMin = undefined;
                }
            }

            return adjustmentPerformed;
        }


        function tryScrollAdjustment() {
            if ($dyn.value(dataLoadPending) <= 0 && !$dyn.value(dataLoadedRenderPending) && scrollAdjustmentPending && !$dyn.value($dyn.clientBusy)) {
                // Adjust scroll state in case it was not successfully adjusted last time
                if (scrollStateAdjusted === false) {
                    readjustScrollState();
                }
                tryFetchDataOnScroll();
                scrollAdjustmentPending = false;
            }
        }

        listContext.usingDisposable($dyn.observe(dataLoadPending, function () {
            tryScrollAdjustment();
        }));

        listContext.usingDisposable($dyn.observe(dataLoadedRenderPending, function () {
            tryScrollAdjustment();
        }));

        listContext.usingDisposable($dyn.observe($dyn.clientBusy, function () {
            tryScrollAdjustment();
        }));

        // Scroll events handler to react to user scroll position changing
        var verticalScrollTimeoutId;
        var clientViewStartCommandPending = false;

        function pageCoversViewport() {
            if (scrollParameters && scrollParameters.rowHeight && listContext.DataSource) {
                var firstVisibleRow = getFirstVisibleRow();
                var visibleRowsCount = getVisibleRowCount(listContext.GetScrollRegionHeight());
                var lastVisibleRow = firstVisibleRow + visibleRowsCount - 1;
                firstVisibleRow = listContext.ConvertClientCacheIndexToServerCacheIndex(firstVisibleRow);
                lastVisibleRow = listContext.ConvertClientCacheIndexToServerCacheIndex(lastVisibleRow);
                var pageStart = $dyn.value(listContext.FetchedDataStartPosition);
                var pageSize = $dyn.value(listContext.DataSource).length;
                var pageEnd = pageStart + pageSize - 1;

                return pageStart <= firstVisibleRow && pageEnd >= lastVisibleRow;
            }

            return true;
        }

        function setClientViewStart() {
            if (!clientViewStartCommandPending) {
                if (!pageCoversViewport() && scrollParameters && scrollParameters.rowHeight && scrollParameters.cacheSize) {
                    clientViewStartCommandPending = true;
                    $dyn.callFunction(listContext.SetClientViewStart, listContext, [], undefined, undefined, function (commandInteraction) {
                        // Before execute callback
                        clientViewStartCommandPending = false;
                        var desiredPageStart = getDesiredPageStart(listContext.GetScrollRegionHeight());
                        commandInteraction.PositionalParameters = [desiredPageStart];
                        $dyn.log.list && $dyn.log.list("List: SetClientViewStart command is executing with an argument: %s", desiredPageStart);
                    });
                }
            }
        }

        listContext._loadDataNeeded = loadDataNeeded;
        listContext._readjustScrollState = readjustScrollState;
        listContext._pageCoversViewport = pageCoversViewport;
        listContext._getFirstVisibleRow = getFirstVisibleRow;
        listContext._getDesiredPageStart = getDesiredPageStart;
        listContext._getDesiredPageSize = getDesiredPageSize;
        listContext._getVisibleRowCount = getVisibleRowCount;
        listContext._dataLoadPending = dataLoadPending;
        listContext._dataLoadedRenderPending = dataLoadedRenderPending;

        function syncVerticalScrollOffset() {
            if (scrollParameters) {
                if (listContext.UpdateScrollTop()) {
                    lastLoadDataState.isDirty = true;
                }
                setClientViewStart();

                if (!scrollAdjustmentPending) {
                    if ($dyn.value(dataLoadPending) <= 0 && !$dyn.value(dataLoadedRenderPending) && !$dyn.value($dyn.clientBusy)) {
                        tryFetchDataOnScroll();
                    } else {
                        scrollAdjustmentPending = true;
                    }
                }
            }

            if (scrollPendingTimeoutId) {
                clearTimeout(scrollPendingTimeoutId);
            }
            scrollPending = true;

            scrollPendingTimeoutId = setTimeout(function () {
                scrollPendingTimeoutId = undefined;
                scrollPending = false;
                if (dataLoadedCompletionPending > 0) {
                    dataLoadedRenderPending(true);
                    dataLoadPending($dyn.value(dataLoadPending) - dataLoadedCompletionPending);
                    dataLoadedCompletionPending = 0;
                }
            }, 100);
        }

        // Updates the white space offset w.r.t list box scroll region
        function syncWhiteSpacesScrollOffset() {
            whiteSpaceTexts.css("margin-left", listContext.GetScrollRegion().scrollLeft());
        }

        if ($dyn.value(listContext.InfiniteScroll)) {
            listContext._scrollHandler = function () {
                syncWhiteSpacesScrollOffset();
                syncVerticalScrollOffset();
            };
            listContext._resizeHandler = resizeHandler;
        }

        listContext.usingDisposable($dyn.layout.registerEndReflowHandler(listContext.element, reflowHandler));

        listContext._clientCacheStartUpdated = function (clientCacheStartShift) {
            pendingScrollShifts += clientCacheStartShift;
        };
        listContext.usingDisposable($dyn.observe(listContext.InfiniteScroll, function (value) {
            if (!value) {
                listContext._scrollHandler = null;
                listContext._resizeHandler = null;
                scrollParameters = listContext._scrollParameters = null;
                $(upperWhiteSpaceElement).height(0);
                $(lowerWhiteSpaceElement).height(0);
            }
        }));

        function restoreSelectionFocus() {
            if (listContext.Root && listContext.Root.isFormActive() && $dyn.value(listContext.Root.TakeFocus) && $dyn.value(listContext.Root.ReadyState) !== $dyn.ui.ReadyState.aborted) {
                var activecontrol = $dyn.value(listContext.Root.ActiveControl());
                if (!activecontrol) return;
                var $matchingControls = $(selector().attr('data-dyn-controlname', activecontrol).find(listContext.element));
                if ($matchingControls.length === 0 || ($matchingControls.length === 1 && $matchingControls.closest(".place-holder").length !== 0)) {
                    // Don't affect focus if there are no matching children, or if the only match is contained by the "place holder" row.
                    return;
                }

                if (scrollParameters) {
                    var selectedItemTop = scrollParameters.bufferAboveHeight + scrollParameters.rowHeight * (listContext.ConvertPageIndexToClientCacheIndex($dyn.value(listContext.SelectedIndex)));
                    var selectedItemBottom = selectedItemTop + scrollParameters.rowHeight;
                    var selectionFocusTriggerMax = selectedItemTop;
                    var selectionFocusTriggerMin = selectedItemBottom - listContext.GetScrollRegionHeight();
                    // Selected row is inside scroll region completely
                    var scrollRegionScrollTop = listContext.GetScrollRegionTop();
                    if (scrollRegionScrollTop >= selectionFocusTriggerMin && scrollRegionScrollTop <= selectionFocusTriggerMax) {
                        listContext.restoreSelectionFocus();
                        scrollParameters.selectionFocusTriggerMax = undefined;
                        scrollParameters.selectionFocusTriggerMin = undefined;
                    } else {
                        scrollParameters.selectionFocusTriggerMax = selectionFocusTriggerMax;
                        scrollParameters.selectionFocusTriggerMin = selectionFocusTriggerMin;
                        if (document.activeElement !== scrollRegion[0]) {
                            // Let's focus grid's scrollRegion in this case.
                            $dyn.log.focus && $dyn.log.focus("Grid->Focus the scroll region after rendering infinite scroll grid");
                            scrollRegion.focus();
                        }
                    }
                } else {
                    listContext.restoreSelectionFocus();
                }
            }
        }

        listContext.__restoreSelectionFocus = restoreSelectionFocus;

        function updateRenderedRowsIndexes() {
            if (listContext.renderedRows.length) {
                for (var i = 0; i < listContext.renderedRows.length; ++i) {
                    var rowData = listContext.renderedRows[i];
                    if (rowData && rowData.element) {
                        rowData.element.setAttribute('aria-posinset', i + 1);
                        rowData.element.setAttribute('aria-setsize', listContext.renderedRows.length);
                    }
                    if (rowData && rowData.selected) {
                        selectedItemElement.setAttribute('aria-posinset', i + 1);
                        selectedItemElement.setAttribute('aria-setsize', listContext.renderedRows.length);
                    }
                }
            }
        }

        return {
            apply: function applyForeach(data, dataContext) {
                var self = this;

                targetElement.dynVirtualContext = {
                    getContext: function () {
                        return listContext._getVirtualControl.apply(listContext, arguments);
                    },
                };

                var collection = data;
                if (data === null || data === undefined) {
                    return;
                }
                if (!(dataContext instanceof $dyn.DataContext)) {
                    throw new TypeError("The argument 'dataContext' must be a an instance of $dyn.DataContext");
                }

                if (!(data instanceof Array)) {
                    collection = data.collection;
                    var deferred = data.deferred;
                    var preRender = data.preRender;
                    var postRender = data.postRender;

                    if (listContext._fastEdit()) {
                        var updating = listContext._updating = $dyn.observable(false);
                        listContext._updatingComputed = function () {
                            var isUpdating = listContext._pendingRowsQueue().length > 0 || listContext._userActionBusy() || $dyn.value(listContext._PendingRowChangeCount) > 0 || $dyn.value(data.updating);
                            return isUpdating;
                        };
                        $dyn.observe(
                            listContext._updatingComputed,
                            function (value) {
                                updating(value);
                            }
                        );
                    } else {
                        var updating = $dyn.computed(function () {
                            return ($dyn.value(listContext._PendingRowChangeCount) > 0) || $dyn.value(data.updating);
                        });
                        listContext._updatingComputed = updating;
                    }

                    var renderPlaceholderRow = data.renderPlaceholderRow;
                    var skipRenderDuringStaticLoading = data.skipRenderDuringStaticLoading;
                    var $scrollContainer = listContext._$scrollContainer = data.scrollContainer;

                }

                self.collection = collection;

                self.selectedIndex = data.selectedIndex;

                if (selectedItemElement) {
                    listContext._selectedItem = self.selectedItem = $dyn.computed(function () {
                        var items = $dyn.value(data.collection);
                        return items && items[self.selectedIndex()];
                    });

                    var selectedItemDataContext = new $dyn.DataContext(self.selectedItem, dataContext);

                    selectedItemElement.setAttribute("aria-posinset", parseInt(self.selectedIndex()) + 1);
                    selectedItemElement.setAttribute("data-dyn-row-active", true);
                    if (listContext.MarkedIndexes()) {
                        listContext._setRowMarking(selectedItemElement, !!listContext.MarkedIndexes()[self.selectedIndex()]);
                    }

                    role.dataBindItem(selectedItemElement, selectedItemDataContext);
                }

                listContext._headersDataBound = false;
                $dyn.observe(listContext.ShowHeader, function (value) {
                    listContext._header = $(listContext.element).find('.listHeader')[0];
                    if (value && !listContext._headersDataBound) {
                        $dyn.dataBind(listContext._header, listContext);
                        listContext._headersDataBound = true;
                    }

                    listContext._header.hidden = !value;
                });

                self.isValidCollectionItemId = function (itemId) {
                    // Collection item id that is null or undefined or equal to 0 or 0-like value considered to be invalid
                    return itemId && itemId != 0;
                };

                self.resources = function () {
                    var formElement = listContext.Root && listContext.Root.element;
                    var resources;
                    if (formElement) {
                        resources = $dyn.resources.getResource(formElement);
                    } else {
                        resources = [];
                    }

                    return resources;
                };

                var getRenderedRowElement = function (index) {
                    var rowData = listContext.renderedRows[index];
                    if (rowData) {
                        if (rowData.selected) {
                            return selectedItemElement;
                        } else {
                            return rowData.element;
                        }
                    }
                    return undefined;
                };

                var fullRenderPending;

                self.runDiscardPolicy = function () {
                    if (listContext._disableDiscardPolicy === true) {
                        return false;
                    }

                    if (listContext.clientCacheStart !== undefined && listContext.clientCacheStart !== null) {
                        var rowHeight = getRowHeight();
                        var scrollRegionHeight = listContext.GetScrollRegionHeight();
                        if (rowHeight && scrollParameters && scrollRegionHeight > 0) {
                            var firstVisibleRow = getFirstVisibleRow();
                            var visibleRowCount = getVisibleRowCount(scrollRegionHeight);
                            var rowCountThreshold = listContext._RowCountThresholdFraction * visibleRowCount;
                            var renderedRowsCount = listContext.renderedRows.length;
                            if (renderedRowsCount > rowCountThreshold) {
                                var remainingAmount = Math.round(rowCountThreshold / 2);
                                // Always remain at least two viewport sizes
                                if (remainingAmount < visibleRowCount * 2) {
                                    remainingAmount = visibleRowCount * 2;
                                }
                                var remainingAmountAbove = Math.floor((remainingAmount - visibleRowCount) / 2);
                                var remainingAmountBelow = Math.ceil((remainingAmount - visibleRowCount) / 2);
                                var remainingStart = firstVisibleRow - remainingAmountAbove;
                                var remainingEnd = firstVisibleRow + visibleRowCount + remainingAmountBelow - 1;
                                if (remainingEnd >= renderedRowsCount) {
                                    remainingStart -= (remainingEnd - renderedRowsCount + 1);
                                    remainingEnd = (renderedRowsCount - 1);
                                }

                                if (remainingStart < 0) {
                                    remainingEnd -= remainingStart;
                                    remainingStart = 0;
                                }

                                var removeSegmentOfRenderedRows = function (start, end) {
                                    for (var i = start; i < end; ++i) {
                                        var currentElement = getRenderedRowElement(i);
                                        if (currentElement && currentElement.parentNode) {
                                            currentElement.parentNode.removeChild(currentElement);
                                        }
                                    }
                                    listContext.renderedRows.splice(start, end);
                                };

                                // Remove everything outside remaining start and end
                                removeSegmentOfRenderedRows(remainingEnd + 1, renderedRowsCount);
                                removeSegmentOfRenderedRows(0, remainingStart);
                                $dyn.log.list && $dyn.log.list("List: Discard policy. Discarding " + remainingStart + " rows above");
                                $dyn.log.list && $dyn.log.list("List: Discard policy. Discarding " + (renderedRowsCount - remainingEnd - 1) + " rows below");
                                listContext.UpdateClientCacheStart(listContext.clientCacheStart + remainingStart);
                                return true;
                            }
                        }
                    }

                    return false;
                };

                self.applyForeach = function () {
                    var options = targetElement.dynForeachOptions;
                    collection = options.collection;

                    if (collection !== null && collection !== undefined) {
                        if (targetElement.dynListRendering) {
                            $dyn.log('list binding already pending');
                        }

                        targetElement.dynListRendering = true;

                        var renderForeach = function () {
                            /// RENDERING LOGIC:
                            /// - Check the grid state that may influence the InfiniteScroll ability and partial rendering (GroupBy, ExtendedStyle)
                            /// - Calculate rows that are candidates to be reused, based on new page position
                            /// - Go through main rendering loop. Reuse rows that can be reused, move rows that can be reused but in different order, create new rows
                            /// - Within rendering loop handle active row specially as active row's element is always present and updated through binding
                            /// - Update client cache start index
                            /// - After rendering loop Remove rows that are needed to be removed
                            /// - Render newly created rows, handle placeholder row if needed
                            /// - Merge rendered rows cache with just rendered rows
                            /// - Handle group by
                            /// - Readjust infinite scroll related state
                            /// - Run discard policy
                            /// - Make sure that viewport is full
                            /// - Update aria attributes on rendered rows,
                            /// - Post render
                            /// - Restore focus


                            var startTime = performance.now();

                            renderForEachTask && renderForEachTask.start();
                            if (listContext._inactiveItemElement && collection) {

                                // Represents server cache based index of the first row of the page
                                var serverCachePageStart = $dyn.value(listContext.FetchedDataStartPosition);

                                // Don't take a dependency on selectedIndex as it is observed separately
                                var selectedIndex = $dyn.peek(data.selectedIndex);
                                var markedEnabled = listContext._supportsMarking();
                                var arrayLength = collection.length;
                                var markedIndexes = listContext.MarkedIndexes();
                                // Indicates whether rendering algorithm should preserve all previously rendered rows
                                // that are outside of current page
                                var preserveOldRows = $dyn.peek(dataLoadedRenderPending);

                                // Represents client cache based index of the first row of the page
                                var clientCachePageStart;
                                var supportsDataIdAndVersion = listContext.GetDataId && (typeof (listContext.GetDataId) === "function") && listContext.GetDataVersion && (typeof (listContext.GetDataVersion) === "function");
                                if (listContext.clientCacheStart !== undefined) {
                                    clientCachePageStart = serverCachePageStart - listContext.clientCacheStart;
                                }
                                var groupBy = $dyn.value(data.groupBy);

                                // GroupBy feature doesn't support infinite scroll and partial rendering
                                if (groupBy) {
                                    $dyn.setValue(listContext, 'InfiniteScroll', false);
                                    fullRenderPending = true;
                                }

                                // The grid with applied extended style doesn't support infinite scroll
                                // as rows in the grid may be layed out horizontally ad break some scrolling logic assumptions
                                if (listContext.ExtendedStyle) {
                                    $dyn.setValue(listContext, 'InfiniteScroll', false);
                                }

                                $dyn.log.list && $dyn.log.list("List: Starting rendering. Server state: CacheSize: %i, FetchedDataStartPosition: %i, DataAbove: %s, DataBelow: %s, PageSize: %i. Client state: CacheStart: %i, RowCount: %i, FullRenderPending: %s, PreserveOldRows: %s, SupportsDataId: %s",
                                    $dyn.value(listContext.CacheSize), $dyn.value(listContext.FetchedDataStartPosition), $dyn.value(listContext.DataAbove), $dyn.value(listContext.DataBelow), $dyn.value(listContext.DataSource).length, listContext.clientCacheStart, listContext.RenderedRowsCount(), fullRenderPending, preserveOldRows, supportsDataIdAndVersion);

                                if (fullRenderPending) {
                                    $dyn.util.listEmpty(targetElement, false);
                                }

                                if (deferred) {
                                    var wasUpdatingSet = false;
                                    if (!$dyn.peek($dyn.ui.updating)) {
                                        $dyn.ui.updating(true);
                                        wasUpdatingSet = true;
                                    }

                                    // Only defer on initial render. Subsequent renders for create, etc., should be performed immediately
                                    // so that there are minimal delays, focus is maintained and all binding handlers are operating against
                                    // the final instance of the row.
                                    deferred = false;
                                }

                                var layoutBlockSet = $dyn.ui.layout();
                                if (!layoutBlockSet) {
                                    /// Put the handler into an updating block so that newly created children in the grid with layout bindings will be batched so that only the
                                    /// topmost element gets laid out at the end.
                                    $dyn.ui.layout(true);
                                }

                                if (fullRenderPending) {
                                    listContext._updateDefaultActionEnabled();
                                }

                                var groupValue,
                                    curGroupValue,
                                    groupList,
                                    groupByContext,
                                    inactiveGroupByElement,
                                    groupTemplate,
                                    groupElement;

                                if (groupBy) {
                                    inactiveGroupByElement = $(listContext._inactiveItemElement).find('[data-dyn-controlname="' + groupBy + '"]')[0];
                                    groupTemplate = $dyn.templates.HierarchicalGridBaseGroupTemplate;
                                }

                                var instance;
                                var currentFragment = null;
                                var currentInsertAfterElement = null;
                                var documentFragments = [];
                                var insertAfterElements = [];
                                var rowsToReuse = {};
                                if (clientCachePageStart !== undefined && clientCachePageStart !== null) {
                                    for (var renderedRowsIndex = clientCachePageStart; renderedRowsIndex < arrayLength + clientCachePageStart && renderedRowsIndex < listContext.renderedRows.length; renderedRowsIndex++) {
                                        var rowData = listContext.renderedRows[renderedRowsIndex];
                                        if (rowData && rowData.id) {
                                            rowsToReuse[rowData.id] = $.extend({}, rowData, { order: renderedRowsIndex });
                                        }
                                    }

                                    if (preserveOldRows) {
                                        if (listContext.renderedRows[clientCachePageStart - 1]) {
                                            currentInsertAfterElement = getRenderedRowElement(clientCachePageStart - 1);
                                        }

                                        // In case we are going to render the page below currently rendered rows,
                                        // let's insert new page after last rendered element
                                        if (listContext.renderedRows.length && clientCachePageStart >= listContext.renderedRows.length) {
                                            currentInsertAfterElement = getRenderedRowElement(listContext.renderedRows.length - 1);
                                        }
                                    }
                                }

                                var createNewFragmentIfNeeded = function () {
                                    if (!currentFragment) {
                                        currentFragment = document.createDocumentFragment();
                                        documentFragments.push(currentFragment);
                                        insertAfterElements.push(currentInsertAfterElement);
                                    }
                                };

                                var createNewInactiveRow = function (item) {
                                    listContext._inactiveItem(item);
                                    return listContext._inactiveItemElement.cloneNode(true);
                                };

                                var refreshedRows = [];
                                var lastReusedOrder = -1;
                                var _createdCount = 0, _reusedCount = 0, _movedCount = 0, _removedCount = 0;
                                for (var arrayIndex = 0; arrayIndex < arrayLength; arrayIndex++) {
                                    itemTask && itemTask.start(arrayIndex);
                                    var markingApplied = false;
                                    var item = collection[arrayIndex];
                                    if (arrayIndex !== selectedIndex) {
                                        var itemId = listContext.GetDataId && listContext.GetDataId(item);
                                        if (supportsDataIdAndVersion && self.isValidCollectionItemId(itemId)) {
                                            var pendingExistingRow = listContext._pendingExistingRows && listContext._pendingExistingRows[itemId];
                                            var pendingRowSelected = listContext._currentPendingRow === pendingExistingRow;
                                            // The current item is not rendered or has been updated
                                            var version = listContext.GetDataVersion(item);
                                            var rowData = rowsToReuse[itemId];
                                            if (fullRenderPending || !rowData || rowData.version !== version || rowData.selected) {
                                                createNewFragmentIfNeeded();
                                                instance = createNewInactiveRow(collection[arrayIndex]);
                                                _createdCount++;
                                            } else {
                                                var elementToReuse = rowData.element;
                                                var elementsOrder = rowData.order;
                                                // We are reusing an element in correct order (There were no other element reused after this)
                                                if (elementsOrder > lastReusedOrder) {
                                                    // As we are reusing an element we have to 'close' the document fragment
                                                    currentFragment = null;
                                                    currentInsertAfterElement = elementToReuse;
                                                    lastReusedOrder = elementsOrder;

                                                    if (rowData && rowData.marked !== markedIndexes[arrayIndex]) {
                                                        listContext._setRowMarking(elementToReuse, markedIndexes[arrayIndex]);
                                                    }
                                                    markingApplied = true;
                                                    _reusedCount++;
                                                } else {
                                                    // In this case an element has incorrect order relatively to last reused element
                                                    // We will add it to document fragment
                                                    createNewFragmentIfNeeded();
                                                    _movedCount++;
                                                }
                                                instance = elementToReuse;
                                                // Remove row data from the dictionary as we succesfully reused it.
                                                rowsToReuse[itemId] = null;
                                            }
                                            refreshedRows.push({
                                                element: instance,
                                                id: itemId,
                                                version: version,
                                                selected: false,
                                                marked: markedEnabled && markedIndexes[arrayIndex],
                                                pendingRow: pendingExistingRow,
                                                pendingRowSelected: pendingRowSelected,
                                                index: arrayIndex // fast grid prototype use only
                                            });
                                        } else {
                                            createNewFragmentIfNeeded();
                                            instance = createNewInactiveRow(collection[arrayIndex]);
                                            refreshedRows.push({
                                                element: instance,
                                                selected: false,
                                                marked: markedEnabled && markedIndexes[arrayIndex]
                                            });
                                            // We can't preserve old rows if grid doesn't support data id
                                            preserveOldRows = false;
                                            _createdCount++;
                                        }
                                    } else {
                                        selectedItemElement.setAttribute("data-dyn-row-active", true);
                                        instance = selectedItemElement;
                                        var itemId = listContext.GetDataId ? listContext.GetDataId(item) : undefined;
                                        var itemVersion = listContext.GetDataVersion ? listContext.GetDataVersion(item) : undefined;
                                        if (supportsDataIdAndVersion && self.isValidCollectionItemId(itemId)) {
                                            var rowData = rowsToReuse[itemId];
                                            if (!fullRenderPending && rowData && rowData.selected && rowData.order > lastReusedOrder) {
                                                // The selected element is in the correct position, we have to 'close' the document fragment
                                                currentFragment = null;
                                                currentInsertAfterElement = selectedItemElement;
                                                //Mark selected item row data as reused
                                                rowsToReuse[itemId] = null;
                                                _reusedCount++;
                                            } else {
                                                createNewFragmentIfNeeded();
                                                _movedCount++;
                                            }

                                            if (rowData && rowData.marked !== markedIndexes[arrayIndex] && rowData.selected || !rowData) {
                                                listContext._setRowMarking(instance, markedIndexes[arrayIndex]);
                                                markingApplied = true;
                                            }
                                        } else {
                                            createNewFragmentIfNeeded();
                                            _createdCount++;
                                        }
                                        refreshedRows.push({
                                            element: createNewInactiveRow(item),
                                            id: itemId,
                                            version: itemVersion,
                                            selected: true,
                                            marked: markedEnabled && markedIndexes[arrayIndex],
                                            index: arrayIndex // fast grid prototype use only
                                        });
                                    }

                                    if (!markingApplied && markedEnabled) {
                                        listContext._setRowMarking(instance, !!markedIndexes[arrayIndex]);
                                    }

                                    // Handle grouping of items
                                    if (groupBy) {
                                        // We have to create new inactive item element and we can't use selected one
                                        // as selected item can receive values later and we need formattedValue right now
                                        if (arrayIndex === selectedIndex) {
                                            listContext._inactiveItem(collection[arrayIndex]);
                                        }
                                        groupByContext = $dyn.context(inactiveGroupByElement);
                                        if (groupByContext) {
                                            curGroupValue = $dyn.value(groupByContext.formattedValue);
                                            if (groupValue !== curGroupValue) {
                                                if (groupList) {
                                                    groupList.appendChild(currentFragment);
                                                    groupList = null;
                                                }
                                                if (!groupList) {
                                                    groupElement = groupTemplate.cloneNode(true);
                                                    groupElement.getElementsByClassName('listGroupTitle')[0].innerText = curGroupValue;
                                                    groupList = groupElement.getElementsByClassName('listGroupContent')[0];
                                                    targetElement.appendChild(groupElement);
                                                }
                                                groupValue = curGroupValue;
                                            }
                                        }
                                    }

                                    if (currentFragment && instance) {
                                        currentFragment.appendChild(instance);
                                    }

                                    itemTask && itemTask.stop(arrayIndex);
                                }

                                if (arrayLength) {
                                    if (preserveOldRows) {
                                        // Update client cache start that represents server cache based index of the first rendered row
                                        if (listContext.clientCacheStart === undefined || listContext.clientCacheStart === null || serverCachePageStart < listContext.clientCacheStart) {
                                            listContext.UpdateClientCacheStart(serverCachePageStart);
                                        }
                                    } else {
                                        listContext.UpdateClientCacheStart(serverCachePageStart);
                                    }
                                } else {
                                    if (!preserveOldRows) {
                                        listContext.UpdateClientCacheStart(undefined);
                                    }
                                }

                                listContext.LastSelectedIndex = lastSelectedIndex = listContext.ConvertPageIndexToServerCacheIndex(selectedIndex);
                                if (!listContext._fastEdit() || !listContext._currentPendingRow) {
                                    listContext._InternalFocusedRowIndex = lastSelectedIndex;
                                }

                                // Allow to remove selected item only if it was rendered in current page
                                var allowSelectedItemRemoval = selectedIndex < 0 || selectedIndex >= arrayLength;
                                var removeElement = function (item) {
                                    if (item) {
                                        var elementToDelete = item.element;
                                        if (elementToDelete && elementToDelete.parentNode) {
                                            elementToDelete.parentNode.removeChild(elementToDelete);
                                            _removedCount++;
                                        }
                                        if (item.selected && allowSelectedItemRemoval && selectedItemElement.parentNode) {
                                            selectedItemElement.parentNode.removeChild(selectedItemElement);
                                            _removedCount++;
                                        }
                                    }
                                };

                                if (preserveOldRows) {
                                    for (var i = 0, keys = Object.keys(rowsToReuse), len = keys.length; i < len; ++i) {
                                        var itemId = keys[i];
                                        var item = rowsToReuse[itemId];
                                        removeElement(item);
                                    }
                                } else {
                                    for (var i = 0; i < listContext.renderedRows.length; ++i) {
                                        var item = listContext.renderedRows[i];
                                        var itemId = item && item.id;
                                        if (self.isValidCollectionItemId(itemId)) {
                                            if (rowsToReuse[itemId] === undefined || (rowsToReuse[itemId] !== undefined && rowsToReuse[itemId] !== null)) {
                                                removeElement(item);
                                            }
                                        } else {
                                            removeElement(item);
                                        }
                                    }
                                }

                                $dyn.log.list && $dyn.log.list("List: Rendering: Created count: %i, Reused count: %i, Moved count: %i, Removed count: %i", _createdCount, _reusedCount, _movedCount, _removedCount);

                                // If there's no data to be rendered, conditionally render out a placeholder row.
                                if (arrayLength === 0 && renderPlaceholderRow) {
                                    createNewFragmentIfNeeded();
                                    selectedItemElement.classList.add("place-holder");
                                    currentFragment.appendChild(selectedItemElement);
                                    refreshedRows.push({
                                        element: null,
                                        selected: true,
                                        marked: false,
                                    });
                                    $dyn.log.list && $dyn.log.list("List: Rendering placeholder row");
                                } else {
                                    selectedItemElement.classList.remove("place-holder");
                                }

                                if (groupList) {
                                    groupList.appendChild(currentFragment);
                                    groupList = null;
                                } else {
                                    for (var i = 0, len = documentFragments.length; i < len; ++i) {
                                        var elementToInsertAfter = insertAfterElements[i];
                                        if (elementToInsertAfter && elementToInsertAfter.parentNode && elementToInsertAfter.parentNode === targetElement) {
                                            $dyn.log.list && $dyn.log.list("List: Rendering insert before row.%o", elementToInsertAfter.nextSibling);
                                            targetElement.insertBefore(documentFragments[i], elementToInsertAfter.nextSibling);

                                        } else {
                                            if (i === 0 && (elementToInsertAfter === null || !elementToInsertAfter.parentNode)) {
                                                $dyn.log.list && $dyn.log.list("List: Rendering insert at the begin.%o", targetElement.firstChild);
                                                targetElement.insertBefore(documentFragments[i], targetElement.firstChild);
                                            } else {
                                                $dyn.logError('List: Insertion point unknown for dom fragment');
                                            }
                                        }

                                    }
                                }

                                // Reset fullRender flag
                                fullRenderPending = false;
                                if (preserveOldRows) {
                                    // Splice rendered rows with new ones
                                    var spliceStart = clientCachePageStart >= 0 ? clientCachePageStart : 0;
                                    var deleteCount = clientCachePageStart >= 0 ? arrayLength : arrayLength + clientCachePageStart;
                                    Array.prototype.splice.apply(listContext.renderedRows, [spliceStart, deleteCount].concat(refreshedRows));
                                } else {
                                    listContext.renderedRows = refreshedRows;
                                }

                                if (groupBy) {
                                    // Resize groups to support column wrapping.
                                    var resizeGroups = function () {
                                        $('.listGroupContent').each(function (index) {
                                            var groupList = this;
                                            var childElementHeight = $(groupList.children[0]).outerHeight(true); // Height of a single unit in the group.
                                            var availableHeight = $(groupList).closest('.scrollRegion').height() - $(groupList).siblings('.listGroupTitle').outerHeight(); // Available height for the group.

                                            var numberOfElementsPerColumn = Math.floor(availableHeight / childElementHeight); // Number of units in the group that can be fit in 1 column.
                                            var numberOfColumns = Math.ceil(groupList.childElementCount / numberOfElementsPerColumn); // Total number of columns to fit all units.
                                            var widthOfSingleColumn = $(groupList.children[0]).outerWidth(true); // Width of a column.

                                            // Set width and height for the group.
                                            $(groupList).width(widthOfSingleColumn * numberOfColumns);
                                            $(groupList).height(childElementHeight * numberOfElementsPerColumn);
                                        });
                                    };

                                    var timeout;

                                    // Throttle group resize when window resizes.
                                    $(window).resize(function () {
                                        clearTimeout(timeout);
                                        timeout = setTimeout(resizeGroups, 200);
                                    });

                                    if ($(instance).is(":hidden")) {
                                        setTimeout(resizeGroups, 200);
                                    } else {
                                        resizeGroups();
                                    }
                                }

                                if (listContext._fastEdit()) {
                                    listContext._renderPendingNewRows();
                                }

                                // Infinite scroll post render related logic
                                readjustScrollState();

                                if ($dyn.value(listContext.InfiniteScroll)) {
                                    // In case rendering produced too many rows, we have to discard some rows
                                    if (self.runDiscardPolicy()) {
                                        // In case we discarded rows, we need to readjust scroll state again
                                        readjustScrollState();
                                    }
                                    ensureFullViewPortContent();
                                }

                                // Sets aria-posinset and aria-setsize attribute to rows
                                updateRenderedRowsIndexes();

                                // Update white space width to match the rendered dimension
                                updateWhiteSpaceWidth();

                                var postRenderStart = performance.now();
                                $dyn.applyFunction(postRender);

                                var restoreFocusStart = performance.now();
                                if ($dyn.value(listContext.InfiniteScroll)) {
                                    restoreSelectionFocus();
                                }

                                if (wasUpdatingSet) {
                                    $dyn.ui.updating(false);
                                }

                                // Need to queue the parent element for an arrange
                                $dyn.layout.queueArrange(targetElement.parentNode);

                                if (!layoutBlockSet) {
                                    $dyn.ui.layout(false);
                                }

                                // It's possible for the grid to render with a selected item out of view without
                                // user interaction (eg, form open, setPosition(), etc), scroll it into view if necessary
                                // This has to be done after the surrounding elements are laid out already so the grid has
                                // its final dimensions and we can correctly determine if the row is in view
                                // We are using setTimeout instead of $dyn.async as scrolling active item into view doesn't change the state of the grid
                                if (!$dyn.value(dataLoadedRenderPending) && selectedItemElement.parentNode === targetElement) {
                                    listContext.setTimeout(function () {
                                        listContext.scrollItemIntoView(selectedItemElement, $scrollContainer);
                                    });
                                }
                            }
                            // Any data loaded is now rendered
                            dataLoadedRenderPending(false);

                            renderForEachTask && renderForEachTask.stop();

                            var name = '<unknown>';
                            var context = $dyn.context(targetElement);
                            if (context && context.Name) {
                                name = context.Name;
                            }
                            var endTime = performance.now();
                            $dyn.log.list && $dyn.log.list("List: Finished rendering. Client state: CacheStart: %i, RowCount: %i", listContext.clientCacheStart, listContext.RenderedRowsCount());
                            $dyn.log.list && $dyn.log.list("List: for each render time: %s, %s (ms)", name, (endTime - startTime));
                        };

                        $dyn.applyFunction(preRender);

                        if (!skipRenderDuringStaticLoading || !$dyn.shell.isPreProcessing || !($dyn.value(dataLoadPending) > 0)) {
                            if (deferred) {
                                $dyn.async(renderForeach, undefined, "Grid render foreach");
                            } else {
                                renderForeach();
                            }
                        }

                        appliedBinding = true;
                        targetElement.dynListRendering = false;
                    }
                };

                var lastSelectedIndex = listContext.ConvertPageIndexToServerCacheIndex(data.selectedIndex());
                self.selectedIndexChanged = function () {
                    $dyn.log.list && $dyn.log.list("List: _PendingRowChangeCount " + $dyn.value(listContext._PendingRowChangeCount));
                    // Do not react to any changes to the selected Index if there are pending data load or key down events.
                    // Pending events like the ones above mean that either an interation is pending or will be processing in the near future
                    // which will re-apply this logic again. Reacting to these changes while such events are pending would result in
                    // skipping (up/down) of the focus to other rows which is undesirable
                    if ($dyn.value(listContext._PendingRowChangeCount) > 0) {
                        $dyn.log.list && $dyn.log.list("List: SelectedIndexChanged: Not updating selected index");
                        return;
                    }

                    var listCollection = $dyn.value(self.list.collection);
                    var index = data.selectedIndex();
                    //var position = parseInt(index) + 1;
                    var markingEnabled = listContext._supportsMarking();
                    var supportsDataIdAndVersion = listContext.GetDataId && (typeof (listContext.GetDataId) === "function") && listContext.GetDataVersion && (typeof (listContext.GetDataVersion) === "function");
                    var clientCacheIndex = listContext.ConvertServerCacheIndexToClientCacheIndex(lastSelectedIndex);
                    var lastSelectedPageIndex = listContext.ConvertServerCacheIndexToPageIndex(lastSelectedIndex);
                    if (lastSelectedPageIndex !== index) {
                        $dyn.log.list && $dyn.log.list("List: selected index changed - old %s, new %s", lastSelectedPageIndex, index);

                        var $activeElement;
                        var inactiveItemElement;
                        var inactiveItemDataId;
                        var inactiveItemDataVersion;
                        if (lastSelectedPageIndex >= 0 && lastSelectedPageIndex < listCollection.length) {
                            // Create a new row for previously selected row which is now an inactive row
                            var lastSelectedItem = listCollection[lastSelectedPageIndex];
                            listContext._inactiveItem(lastSelectedItem);
                            inactiveItemElement = listContext._inactiveItemElement.cloneNode(true);
                            if (supportsDataIdAndVersion) {
                                inactiveItemDataId = listContext.GetDataId(lastSelectedItem);
                                inactiveItemDataVersion = listContext.GetDataVersion(lastSelectedItem);
                            }
                        } else {
                            // Previously selected item can be outside active page.
                            // Let's search for it in rendered rows array.
                            if (listContext.renderedRows[clientCacheIndex] && listContext.renderedRows[clientCacheIndex].selected) {
                                inactiveItemElement = listContext.renderedRows[clientCacheIndex].element;
                                inactiveItemDataId = listContext.renderedRows[clientCacheIndex].id;
                                inactiveItemDataVersion = listContext.renderedRows[clientCacheIndex].version;
                            }
                        }
                        if (inactiveItemElement) {
                            inactiveItemElement.setAttribute("aria-posinset", clientCacheIndex + 1);
                            if (markingEnabled) {
                                listContext._setRowMarking(inactiveItemElement, !!listContext.MarkedIndexes()[lastSelectedPageIndex]);
                            }

                            if (document.activeElement) {
                                $activeElement = $(selectedItemElement).find(document.activeElement);
                            }

                            if (selectedItemElement.parentNode) {
                                selectedItemElement.parentNode.replaceChild(inactiveItemElement, selectedItemElement);
                                // Let's maintain objects that are used by rerendering
                                listContext.renderedRows[clientCacheIndex] = {
                                    element: inactiveItemElement,
                                    version: inactiveItemDataVersion,
                                    selected: false,
                                    marked: !!listContext.MarkedIndexes()[lastSelectedPageIndex],
                                    id: inactiveItemDataId
                                };
                            }
                        }

                        if (index >= 0 && index < listCollection.length) {
                            // Updated the new selected row
                            var newSelectedItem = listCollection[index];
                            self.selectedItem();

                            var currentElementAtSelectedIndex;
                            var clientCacheIndex = listContext.ConvertPageIndexToClientCacheIndex(index);
                            selectedItemElement.setAttribute("aria-posinset", clientCacheIndex + 1);
                            if (listContext.renderedRows[clientCacheIndex]) {
                                currentElementAtSelectedIndex = listContext.renderedRows[clientCacheIndex].element;
                            }

                            if (currentElementAtSelectedIndex && currentElementAtSelectedIndex.parentNode) {

                                currentElementAtSelectedIndex.parentNode.replaceChild(selectedItemElement, currentElementAtSelectedIndex);

                                // Let's maintain objects that are used by rerendering
                                listContext.renderedRows[clientCacheIndex] = {
                                    element: currentElementAtSelectedIndex,
                                    version: listContext.GetDataVersion ? listContext.GetDataVersion(newSelectedItem) : undefined,
                                    selected: true,
                                    marked: !!listContext.MarkedIndexes()[index],
                                    id: listContext.GetDataId ? listContext.GetDataId(newSelectedItem) : undefined
                                };

                                if ($activeElement) {
                                    listContext.TransferInputFocus($(selectedItemElement), $activeElement);
                                }
                            }

                            // Set the attributes that tell the browser and the CSS rendering that the row is selected. IE exibits a
                            // problem applying the correct CSS if this is done before the DOM is updated.
                            selectedItemElement.setAttribute("data-dyn-row-active", true);
                            if (markingEnabled) {
                                listContext._setRowMarking(selectedItemElement, !!listContext.MarkedIndexes()[index]);
                            }

                            // The new selected item could be out of view, scroll it into view if necessary
                            if (!$dyn.value(dataLoadedRenderPending) && selectedItemElement.parentNode) {
                                // Selected item is in the view. No need to maintain focus triggers for it
                                if (scrollParameters) {
                                    scrollParameters.selectionFocusTriggerMax = undefined;
                                    scrollParameters.selectionFocusTriggerMin = undefined;
                                }
                                listContext.scrollItemIntoView(selectedItemElement, $scrollContainer);
                            }
                        }
                        // Call the public post selected index changed callback, if any
                        $dyn.callFunction(listContext.PostSelectedIndexChanged, self, [index]);
                    }

                    listContext.LastSelectedIndex = listContext._InternalFocusedRowIndex = lastSelectedIndex = listContext.ConvertPageIndexToServerCacheIndex(index);

                };

                if (listContext._fastEdit()) {
                    self.selectedIndexChanged = listContext._selectedIndexChanged.bind(listContext);
                }

                // Update lastSelectedItem when server cache shifted
                listContext._serverCacheCoordinateShiftHandler = function (shift) {
                    lastSelectedIndex += shift;
                };

                var isShellInitializing = $dyn.peek($dyn.shell.isShellInitializing) ? $dyn.shell.isShellInitializing : false;

                self.list = {
                    collection: collection,
                    groupBy: data.groupBy,
                    rerenderOptions: listContext._rerenderOptions,
                    dataChanged: listContext._dataChangedVersion,
                    dataLoadPending: dataLoadPending,
                    selectedIndex: data.selectedIndex,
                    markedIndexes: listContext.MarkedIndexes,
                    allResourcesLoaded: listContext._allResourcesLoaded,
                    // A dependency is not needed if the shell is not initializing
                    shellInitializing: isShellInitializing,
                    defaultActionEnabled: listContext.NavigationEnabled,
                    pendingSelectedRow: listContext._getCurrentPendingRow(),
                    columnHeaderMetadata: listContext.ColumnHeaderMetadata,
                };

                self.applyChanges = function (changes) {
                    var renderRequired = false;
                    var selectedIndexChanged = false;
                    var markingChanged = false;
                    var renderNeeded = false;
                    var headerRenderNeeded = false;

                    if ($dyn.shell.isShellInitializing() || !listContext._allResourcesLoaded()) {
                        return;
                    }


                    if (changes.collection || changes.dataLoadPending || changes.dataChanged || changes.pendingSelectedRow) {
                        renderNeeded = true;
                    }

                    if (changes.allResourcesLoaded || changes.columnHeaderMetadata || changes.shellInitializing) {
                        headerRenderNeeded = true;
                    }

                    if (changes.rerenderOptions || changes.groupBy || changes.shellInitializing || changes.defaultActionEnabled || changes.allResourcesLoaded) {
                        fullRenderPending = true;
                        renderNeeded = true;
                    }

                    if (changes.selectedIndex) {
                        selectedIndexChanged = true;
                    }
                    if (changes.markedIndexes) {
                        markingChanged = true;
                    }

                    //if header rendering it pending , render it first
                    if (headerRenderNeeded) {
                        listContext._renderHeader();
                    }
                    if (renderNeeded) {
                        // If render needed - render and exit.
                        // Rendering takes care about marking and selection.
                        if (self.render() !== false) {
                            return;
                        }
                    }
                    if (selectedIndexChanged) {
                        self.selectedIndexChanged();
                    }

                    if (markingChanged) {
                        listContext.markedIndexesChanged();
                    }
                };

                listContext.usingDisposable($dyn.bulkObserve(self.list, self.applyChanges, updating, self));

                $dyn.observe(
                    self.resources(),
                    function resourcesChanged(resources) {
                        if (resources && resources.length > 0) {
                            var allResourcesLoaded = true;
                            $.each(resources, function (index, value) {
                                // Should be checking if deferred isResolved
                                if (value !== undefined && !value.loaded) {
                                    allResourcesLoaded = false;
                                }
                            });

                            listContext._allResourcesLoaded(allResourcesLoaded);

                            if (!allResourcesLoaded) {
                                // TODO: Handle deferred rejection (when resource failed to load)
                                $.when.apply($.when, resources.map(function (item) { if (!item.loaded) { return item.promise(); } })).done(function () {
                                    listContext._resetCachedInactiveItemElement();
                                    listContext._allResourcesLoaded(true);
                                });
                            }
                        }
                        else {
                            listContext._allResourcesLoaded(true);
                        }
                    }
                );

                self.render = function () {
                    $dyn.log.list && $dyn.log.list("Render: _PendingRowChangeCount " + $dyn.value(listContext._PendingRowChangeCount));
                    // If some data loading is still pending, do not render
                    if ($dyn.value(dataLoadPending) > 0 || $dyn.value(listContext._PendingRowChangeCount) > 0) {
                        $dyn.log.list && $dyn.log.list("Render: Not rendering");
                        return false;
                    }

                    // Convert list options to non-observable options
                    var options = {};
                    for (var option in self.list) {
                        options[option] = $dyn.value(self.list[option]);
                    }

                    targetElement.dynForeachOptions = options;
                    self.applyForeach();
                };
            },
            dispose: function () {
                // unbind children if they had been bound
                if (appliedBinding == true) {
                    target.children().each(function (elementIndex, element) {
                        $dyn.unbind(element);
                    });
                    appliedBinding = false;

                    if (listContext._selectedRow && !listContext._selectedRow.parentElement) {
                        $dyn.unbind(listContext._selectedRow);
                    }

                    if (listContext._headersDataBound) {
                        $dyn.unbind(listContext._header);
                    }

                    listContext._updatingComputed.valueChanged && listContext._updatingComputed.valueChanged.clearAllDependencies();
                }
                if (scrollPendingTimeoutId) {
                    clearTimeout(scrollPendingTimeoutId);
                }
            }
        };
    };

    $dyn.controls.HierarchicalGridBaseColumnHeader = function (data, element, parent) {
        $dyn.ui.Control.apply(this, arguments);
        var self = this;
        if (parent) {
            self.Root = parent.Root;
        }

        self.ColumnHeaderPopup = $(element).children('.columnHeader-popup')[0];
        // data.DataSource is the headerMetadata pushed from grid
        if (data.DataSource) {
            self.FormDataSourceName = data.DataSource.FormDataSourceName;
            self.FieldName = data.DataSource.FieldName;
            self.IsFilterable = data.DataSource.IsFilterable;
            self.IsSortable = data.DataSource.IsSortable;
            self.IsFiltered = data.DataSource.IsFiltered;
            self.IsSorted = data.DataSource.IsSorted;
            self.Label = data.DataSource.Label;
            self.Alignment = data.DataSource.Alignment;
            self.ControlType = data.DataSource.ControlType;
            self.FilteringFields = data.DataSource.FilteringFields;
        }

        self.FilterExpressions = $dyn.observable(null);

        self.getFieldFilterCallBack = function (fieldFilterArray) {
            /// <summary>
            /// The callback from the GetFieldFilter command. Updates the filterExpressions if the command returned valid filters.
            /// </summary>
            /// <param name="fieldFilterArray">A list of filterExpressions representing the filters for this column</param>
            /// <returns type="boolean">True if the GetFieldFilter command returned valid filters</returns>

            var validFiltersReturned = true;
            if (fieldFilterArray && fieldFilterArray.length === self.FilteringFields.length) {
                // Array returned from the server should be the same length as the array sent from the client.
                for (var i = 0; i < fieldFilterArray.length; i++) {
                    if (fieldFilterArray[i] && fieldFilterArray[i].Capability) {
                        var label = $dyn.value(self.FilteringFields[i].Label);
                        if (label) {
                            // Override the field's label with the control's label in case the label has been overridden on the control level.
                            fieldFilterArray[i].Capability.FieldLabel = label;
                        }
                    } else {
                        validFiltersReturned = false;
                        break;
                    }
                }
            } else {
                validFiltersReturned = false;
            }

            if (validFiltersReturned) {
                // The filters returned are valid. Show the filters.
                self.updateFieldFilterExpressions(fieldFilterArray);
            } else {
                // The filters returned are not valid. Close the flyout and log an error.
                self.FlyOutPickerOpen(false);
                $dyn.error({ Message: $dyn.label("List_ColumnNotFilterable"), Label: "List_ColumnNotFilterable" }, self);
            }

            return validFiltersReturned;
        };

        self.updateFieldFilterExpressions = function (newFilterExpressions) {
            /// <summary>
            /// Update the FilterExpressions for this column. Note that this will make the FilterExpressions deeply observable, then show and databind the ColumnHeaderPopup.
            /// </summary>
            /// <param name="fieldFilterArray">A list of FieldFilterExpressions representing the filters for this column</param>

            var filterExpressions = newFilterExpressions;
            if (newFilterExpressions) {
                var unwrappedFilterExpressions = $dyn.value(newFilterExpressions);
                unwrappedFilterExpressions.forEach(function (filterExpression) {
                    // Convert the isEditable property from a string to a boolean
                    filterExpression.IsEditable = $dyn.util.stringToBoolean(filterExpression.IsEditable);
                });

                // Make filterExpressions deeply observable. Note that we cannot simply make all of the current members of the
                // filterExpressions observable since actions such as changing to a multi-value operator would not cause the new values
                // to be observable.
                var deeplyObservableFilterExpressions = $dyn.observableObject(unwrappedFilterExpressions);
                filterExpressions = $dyn.value(deeplyObservableFilterExpressions);
            }

            self.FilterExpressions(filterExpressions);
            if (data) {
                var headerContext = $dyn.context(element);
                if (self.ColumnHeaderPopup.hidden) {
                    $dyn.dataBind(self.ColumnHeaderPopup, headerContext);
                    self.ColumnHeaderPopup.hidden = false;
                }

                // Set the aria-label of the popup to describe the sorts and filters.
                var columnHeaderPopupContext = $dyn.context(self.ColumnHeaderPopup);
                if (columnHeaderPopupContext) {
                    columnHeaderPopupContext.UpdateColumnHeaderAriaLabel();
                }
            } else {
                self.FlyOutPickerOpen(false);
            }
        };

        self.StartColumnHeaderResize = function (event, ui) {
            $dyn.log.list && $dyn.log.list("List: Start column header resize");
            if (self && self.element) {
            	var grid = $(self.element).parents("[data-dyn-role='HierarchicalGridBase']");
                if (grid.length > 0) {
                    var target = $(event.target);
                    if (target.is(".columnHeaderWrapper")) {
                        var gridContext = $dyn.context(grid[0]);
                        if (gridContext) {
                            var newColSize;
                            // because of bug in jquery resize, we need to calculate size
                            // the bug causes originalSize and size to be reversed for RTL, thus we need to do the size math.
                            if ($dyn.util.isRTLMode()) {
                                newColSize = ui.originalSize.width + (ui.originalSize.width - ui.size.width);
                            }
                            else {
                                newColSize = ui.originalSize.width + (ui.size.width - ui.originalSize.width);
                            }

                            gridContext._processColumnHeaderResize(event, false, newColSize);
                        }
                    }
                }
            }

        };

        self.StopColumnHeaderResize = function (event) {
            $dyn.log.list && $dyn.log.list("List: Stop column header resize");
            if (self && self.element) {
            	var grid = $(self.element).parents("[data-dyn-role='HierarchicalGridBase']");
                if (grid.length > 0) {
                    var gridContext = $dyn.context(grid[0]);
                    if (gridContext) {
                        gridContext._processColumnHeaderResize(event, true);
                    }
                }
            }
        };

        self.flyoutClickSubscriber = function (options) {
            /// <summary>
            /// This method is passed into the simpleFlyout handler of the field selector dropdown to handle click events.
            /// </summary>
            /// <param name="options">An aggregate element that consists of the following parameters:
            ///     clickTarget: The HtmlElement that received the click event
            ///     showFlyout: The boolean observable that is set to false if the flyout should be closed
            ///     flyoutElement: The jQuery flyout element
            ///     x: The x coordinate of the click event
            ///     y: The y coordinate of the click event
            /// </param>
            /// <returns type="boolean">True if the flyout was closed by the click</returns>

            if ($(options.clickTarget).closest('.flyout-menuItem').length == 1) {
                return false;
            }

            return $dyn.util.flyout.defaultFlyoutClickSubscriber(options);
        };

        self.CloseFilter = function () {
            self.ShowAddFilterDropDown(false);
        };

        self.FlyOutPickerOpen = $dyn.observable(false);

        self.ShowAddFilterDropDown = $dyn.computed(
            function getShowAddFilterDropDown() {
                var flyOutPickerOpen = $dyn.value(self.FlyOutPickerOpen);
                return flyOutPickerOpen && $dyn.value(self.Enabled) && ($dyn.value(self.IsFilterable) || $dyn.value(self.IsSortable));
            },
            function setShowAddFilterDropDown(newValue) {
                var showDropdown = newValue;
                if (self.FilterManager && showDropdown && ($dyn.value(self.IsFilterable) || $dyn.value(self.IsSortable))) {
                    self.FilterManager.getFieldFilters(self.buildFilteringFieldsSimpleObjectList(), self.Name, $dyn.value(self.Label), self.getFieldFilterCallBack);
                } else {
                    showDropdown = false; // If the field is not filterable do not show the flyout.
                }

                self.FlyOutPickerOpen(showDropdown);

                if ($dyn.value(self.FlyOutPickerOpen())) {
                    if (self.Root) {
                        $(self.Root.element).on("formClose", self.CloseFilter);
                    }
                }
                else {
                    if (self.Root) {
                        $(self.Root.element).off("formClose", self.CloseFilter);
                    }
                }
            });

        $dyn.observe(self.FlyOutPickerOpen, function (isOpen) {
            var $parent = findColumnHeader($(element));

            if ($parent === null) {
                return;
            }

            if (isOpen) {
                $parent.addClass("hasOpenPopup");
            } else {
                // clear the aria label of the popup when it closes.
                var columnHeaderPopupContext = $dyn.context(self.ColumnHeaderPopup);
                if (columnHeaderPopupContext) {
                    columnHeaderPopupContext.UpdateColumnHeaderAriaLabel(true);
                }
                $parent.removeClass("hasOpenPopup");
            }
        });

        function findColumnHeader($target) {
            var $parent = $target.closest(".columnHeader");

            return $parent;
        }
    };

    $dyn.controls.HierarchicalGridBaseColumnHeader.prototype = $dyn.extendPrototype($dyn.ui.Control.prototype, {
        FilterExpression: null,
        FormDataSourceName: "",
        FieldName: "",
        Enabled: true,
        Name: "",
        RequestLookupForm: null,
        ShowAddFilterDropDown: false,
        IsFilterable: false,
        IsSortable: false,
        IsFiltered: false,
        IsSorted: -1,
        Label: "",
        Alignment: "Auto",
        AlternateFilteringFields: undefined,

        buildFilteringFieldsSimpleObjectList: function () {
            var simpleObjectList = [];
            if (this.FilteringFields) {
                this.FilteringFields.forEach(function (filteringField) {
                    simpleObjectList.push({ FormDataSourceName: $dyn.value(filteringField.FormDataSourceName), FieldName: $dyn.value(filteringField.FieldName) });
                });
            }
            return simpleObjectList;
        },
        focusIn: function (evt) {
            var self = this;
            var root = self.Root;

            // Set the server selected control to this column control. This is necessary since the column header itself is not a serverside control,
            // and we may need to execute leave/save boundary logic on the control that is losing focus.
            if (root && root.SetServerSelectedControl && self.Name) {
                $dyn.setValue(root, 'ActiveControl', self.Name);
                $dyn.async(function () {
                    $dyn.callFunction(root.SetServerSelectedControl, root, [self.Name]);
                }, undefined, "Send SetServerSelectedControl to the server");
            }

            // stop the focus event since it shouldn't bubble up to the form level 'focusIn' handler.
            evt.stopImmediatePropagation();
            evt.preventDefault();
            $dyn.log.stopPropagation && $dyn.log.stopPropagation('Prevent focusIn from bubbling up from grid column headers.');
        },

        get FilterManager() {
            if (!this._filterManager && this.Root) {
                this._filterManager = this.Root.FilterManager;
            }

            return this._filterManager;
        },

        dispose: function () {
            var self = this;
            if (self.ColumnHeaderPopup && !self.ColumnHeaderPopup.hidden) {
                // ColumnHeaderPopup.hidden attribute is set to false when the columnHeaderPopup gets databound
                // within the updateFieldFilterExpressions method.
                $dyn.unbind(self.ColumnHeaderPopup);
            }

            $dyn.ui.Control.prototype.dispose.apply(this);
        }
    });

    $dyn.controls.HierarchicalGridBaseColumnHeaderPopup = function (data, element, parent) {
        $dyn.controls.ProtoForm.apply(this, arguments);
        var self = this;

        self.FieldCapability = $dyn.observable(null);
        self.IsSorted = $dyn.observable(-1);
        self.Root = parent.Root;
        self.FilterExpressions = [];

        // When Flyout picker closes and ShouldReturnFocus flag is set, then focus should get back to the control inside of the grid.
        $dyn.observe(self.DataSource.FlyOutPickerOpen, function (newValue) {
            if (!$dyn.value(newValue) && self.ShouldReturnFocus && self.Root && self.Root.setControlFocus && self.Name) {
                self.Root.setControlFocus(self.Name);
            }
        });

        // data.DataSource is the headerMetadata pushed from grid
        if (data.DataSource) {
            self.IsSorted(data.DataSource.IsSorted);

            self.FormDataSourceName = data.DataSource.FormDataSourceName;
            self.FieldName = data.DataSource.FieldName;
            self.FilterExpressions = data.DataSource.FilterExpressions;
        }

        self.ButtonsEnabled = $dyn.computed(function () {
            var isEnabled = false;
            if (self.FilterExpressions) {
                var filterExpressions = $dyn.value(self.FilterExpressions);
                filterExpressions.forEach(function (filterExpression) {
                    isEnabled = $dyn.value(filterExpression.IsEditable) || isEnabled;
                });
            }
            return isEnabled;
        });

        var controlType = $dyn.value(data.DataSource.ControlType);
        self.AscendingLabel = $dyn.observable("");
        self.DescendingLabel = $dyn.observable("");
        switch (controlType) {
            case $dyn.ui.ControlTypes.Integer:
            case $dyn.ui.ControlTypes.Int64:
            case $dyn.ui.ControlTypes.Real:
                self.AscendingLabel($dyn.label("List_IncSortNumber"));
                self.DescendingLabel($dyn.label("List_DecSortNumber"));
                break;
            case $dyn.ui.ControlTypes.Date:
            case $dyn.ui.ControlTypes.DateTime:
            case $dyn.ui.ControlTypes.Time:
                self.AscendingLabel($dyn.label("List_IncSortTime"));
                self.DescendingLabel($dyn.label("List_DecSortTime"));
                break;
            default:
                self.AscendingLabel($dyn.label("List_IncSortString"));
                self.DescendingLabel($dyn.label("List_DecSortString"));
                break;
        }

        self.DescendingToggle = $dyn.computed(function () {
            var curState = $dyn.value(self.IsSorted);
            return $dyn.value(curState) === $dyn.ui.SortDirection.descending ? true : false;
        });

        self.AscendingToggle = $dyn.computed(function () {
            var curState = $dyn.value(self.IsSorted);
            return $dyn.value(curState) === $dyn.ui.SortDirection.ascending ? true : false;
        });

        self.applySort = function (isAscending) {
            var sort = [];
            sort.push(self.UnwrappedFilterExpressions());
            var sortLabel;

            if (isAscending) {
                sort.push("1");
                sortLabel = $dyn.value(self.AscendingLabel);
            } else {
                sort.push("0");
                sortLabel = $dyn.value(self.DescendingLabel);
            }

            // set clear sorts to 1
            sort.push("1");

            if ($dyn.value($dyn.ax.taskRecording.isRecording())) {
                sort.push(self.Name);
                sort.push(sortLabel);
            }

            self.FilterManager.setFieldSorts(sort, $dyn.telemetry.ActionEvents.gridColumnSort);
            self.ShouldReturnFocus = true;
            self.DataSource.FlyOutPickerOpen(false);
        };

        self.SortAscending = function () {
            self.applySort(true);
        };

        self.SortDescending = function () {
            self.applySort(false);
        };

        self.applyFilters = function () {
            self.FilterManager.applyFieldFilters(self.UnwrappedFilterExpressions(), self.Name, $dyn.telemetry.ActionEvents.gridColumnFilter);
            self.ShouldReturnFocus = true;
            self.DataSource.FlyOutPickerOpen(false);
        };

        self.resetFilters = function () {
            self.FilterManager.resetFieldFilters(self.UnwrappedFilterExpressions(), self.Name, $dyn.telemetry.ActionEvents.gridColumnFilter);
            self.ShouldReturnFocus = true;
            self.DataSource.FlyOutPickerOpen(false);
        };
    };

    $dyn.controls.HierarchicalGridBaseColumnHeaderPopup.prototype = $dyn.extendPrototype($dyn.controls.ProtoForm.prototype, {
        FormDataSourceName: "",
        FieldName: "",
        Enabled: true,
        AllowEdit: true,
        Operator: "",
        Values: [],
        FieldCapability: null,
        Name: "",
        RequestLookupForm: null,
        IsSorted: -1,
        applyFilters: null,
        resetFilters: null,
        Label: "",
        AscendingLabel: 'List_IncSortString',
        DescendingLabel: 'List_DecSortString',
        ShouldReturnFocus: false,

        get FilterManager() {
            if (!this._filterManager && this.Root) {
                this._filterManager = this.Root.FilterManager;
            }

            return this._filterManager;
        },

        Shortcuts: {
            applyFilter: {
                Keys: { keyCode: $dyn.ui.KeyCodes.enter },
                Handler: function (evt) {
                    return this.HandleEnter(evt);
                },
            },
            escapeHandling: {
                Keys: { keyCode: $dyn.ui.KeyCodes.esc },
                Handler: function (evt) {
                    return this.HandleEscape(evt);
                },
            },
        },

        UpdateColumnHeaderAriaLabel: function UpdateColumnHeaderAriaLabel(clearLabel) {
            /// <summary>
            /// Set an aria label on the column header that will describe the sorts and filters applied. Optionally simply clear the aria label
            /// </summary>
            /// <param name="clearLabel">Clear the aria-label of the column header</param>

            var filterExpressions = $dyn.value(this.DataSource.FilterExpressions);
            var sortDirection = $dyn.value($dyn.value(this.IsSorted));
            var ariaLabel = "";

            if (filterExpressions.length && !clearLabel) {
                var filterOpenedLabel = $dyn.label('FilterManager_ColumnFilterOpened');
                var sortLabel = "";
                switch (sortDirection) {
                    case $dyn.ui.SortDirection.ascending:
                        sortLabel = $dyn.label('FilterManager_SortedAscendingAria');
                        break;
                    case $dyn.ui.SortDirection.descending:
                        sortLabel = $dyn.label('FilterManager_SortedDescendingAria');
                        break;
                    default:
                        sortLabel = $dyn.label('FilterManager_UnsortedAria');
                }

                var expressionLabel = $dyn.format($dyn.label('FilterManager_FilterCountAria'), filterExpressions.length);
                filterExpressions.forEach(function (filterExpression) {
                    var capability = $dyn.value(filterExpression.Capability);
                    expressionLabel += $dyn.util.getFilterFieldDescriptionAria(filterExpression, capability);
                });

                ariaLabel = filterOpenedLabel + " " + sortLabel + " " + expressionLabel;
            }

            var $inputControlElement = $(this.element);
            $inputControlElement.attr('aria-busy', "true");
            $inputControlElement.attr('aria-label', ariaLabel);
            $inputControlElement.attr('aria-busy', "false");
        },

        UnwrappedFilterExpressions: function () {
            var filterExpressions = $dyn.value(this.DataSource.FilterExpressions);
            var unwrappedFilterExpressions = [];
            filterExpressions.forEach(function (filterExpression) {
                unwrappedFilterExpressions.push($dyn.util.unwrapFilterExpression(filterExpression));

            });

            return unwrappedFilterExpressions;
        },
        HandleEnter: function (event) {
            var self = this;
            if (self.element) {
                var targetElement = event.target;

                // If the target element is an input element within the columnHeaderPopup, apply filters.
                if (targetElement && targetElement.tagName && targetElement.tagName.toLowerCase() == "input" && $.contains(self.element, targetElement)) {
                    self.applyFilters();
                    return true;
                }
            }

            return false;
        },

        HandleEscape: function (event) {
            this.ShouldReturnFocus = true;
            return false;
        },

        processKeyDown: function (event) {
            /// <summary>
            /// Handles key down events within a ColumnHeaderPopup.
            /// [Ctrl+G] Open the grid column header filter flyout for the current column  and move focus to the filter field input box.
            /// [Enter] When the user hits Enter from the filter field, this corresponds to Applying that column filter.
            /// [Tab/Shift+Tab] To move around the grid filter flyout
            /// [Enter/Spacebar] When focus is on the filter operator, this opens the flyout and put focus on the current operator [Implemented in filtering]
            /// [Up/Down arrows] When the filter operator flyout is open, this moves through the available operators               [Implemented in filtering]
            /// </summary>
            /// <param name="event">
            /// The jQuery event for the key down.
            /// </param>
            $dyn.util.handleShortcuts(this, event);
        },
    });


    // Listen to the 'onResourceLoaded' event and do the following when loaded resource is HierarchicalGrid:
    // Create a wrapper of the createGridTemplate function that invokes original function but updates the return value to have data-dyn-role='HierarchicalGridBase' attribute
    // This is needed because HierarchicalGrid relies on HierarchicalGridBase functionality to work properly.
    var resourceLoadedSubscription = $dyn.HtmlImports.prototype.onResourceLoaded.subscribe(function (resourcePath) {
        if (resourcePath && resourcePath.indexOf("HierarchicalGrid") !== -1) {
            var originalCreateGridTemplate = $dyn.projectManagement.hierarchicalGrid.templateFactory.createGridTemplate;
            if (originalCreateGridTemplate) {
                $dyn.projectManagement.hierarchicalGrid.templateFactory.createGridTemplate = function () {
                    var gridTemplate = originalCreateGridTemplate.apply($dyn.projectManagement.hierarchicalGrid.templateFactory, arguments);
                    $(gridTemplate).attr('data-dyn-role', "HierarchicalGridBase");
                    return gridTemplate;
                };
                $dyn.HtmlImports.prototype.onResourceLoaded.unsubscribe(resourceLoadedSubscription);
            }
        }
    });

})();
